import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate, useLocation, Navigate } from "react-router-dom";
import axios from "axios";
import {
  LayoutDashboard, Building2, LogOut, Menu, X,
  User, ChevronRight, ChevronDown, Trash2, ToggleLeft, ToggleRight,
  BarChart3, Search, RefreshCw, Check,
  Clock, CreditCard, Mail, Phone, Crown, Shield, Zap, Edit3, Save, XCircle,
  TrendingUp, TrendingDown, DollarSign, Percent, CalendarDays,
  Send, Bell, MessageSquare, CheckCircle, AlertTriangle,
  Activity, Users as UsersIcon, UserMinus, UserPlus, Sparkles, MessageCircle,
  Download, ExternalLink, HeadphonesIcon, ArrowLeft, RotateCcw,
  ArrowUpRight, ArrowDownRight, Minus, CalendarCheck, Anchor, BookOpen,
  Megaphone, FileText, PlusCircle, Pencil, Upload, Bookmark, Paperclip,
  Tag, Copy, ChevronUp, Target, Filter, Eye
} from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend, PieChart, Pie, Cell, LineChart, Line } from "recharts";
import { ProviderBadge } from "./components/ProviderBadge";
import { exportCsv } from "./lib/csv";

const API = `${process.env.REACT_APP_API_URL}/api`;
const LOGO_URL = "https://nautiqo.cloud/logo-nautiqo.png";

const fmtEur = (n, dec = true) => new Intl.NumberFormat("it-IT", {
  style: "currency", currency: "EUR",
  minimumFractionDigits: dec ? 2 : 0, maximumFractionDigits: dec ? 2 : 0
}).format(Number(n) || 0);
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
};
const fmtDateShort = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short" }); }
  catch { return iso; }
};

// ==================== SUPER ADMIN LAYOUT ====================
export const SuperAdminLayout = ({ children, user, logout, getAuthHeaders }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [openHelpCount, setOpenHelpCount] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  useEffect(() => {
    if (!getAuthHeaders) return;
    axios.get(`${API}/admin/help-requests?status=open`, { headers: getAuthHeaders() })
      .then(res => setOpenHelpCount(Array.isArray(res.data) ? res.data.length : 0))
      .catch(() => {});
  }, [getAuthHeaders]);

  const navItems = [
    { path: "/super-admin", icon: LayoutDashboard, label: "Dashboard" },
    { path: "/super-admin/insights", icon: Activity, label: "Insights" },
    { path: "/super-admin/revenue", icon: TrendingUp, label: "Revenue" },
    { path: "/super-admin/aziende", icon: Building2, label: "Aziende" },
    { path: "/super-admin/notifiche", icon: Send, label: "Notifiche" },
    { path: "/super-admin/marketing", icon: Megaphone, label: "Marketing" },
    { path: "/super-admin/whatsapp", icon: MessageCircle, label: "WhatsApp" },
    { path: "/super-admin/lead", icon: Target, label: "Lead" },
    { path: "/super-admin/coupon", icon: Tag, label: "Coupon" },
    { path: "/super-admin/supporto", icon: HeadphonesIcon, label: "Supporto", badge: openHelpCount },
    { path: "/super-admin/rimborsi", icon: RotateCcw, label: "Rimborsi" },
    { path: "/super-admin/utenti", icon: Search, label: "Utenti" },
  ];

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Mobile Header */}
      <div className="lg:hidden bg-slate-900 border-b border-slate-800 px-4 py-3 flex items-center justify-between sticky top-0 z-40">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-[22%] overflow-hidden flex-shrink-0"><img src={LOGO_URL} alt="Nautiqo" className="w-full h-full scale-[1.06]" /></div>
          <div>
            <span className="font-semibold text-white text-sm">Nautiqo</span>
            <span className="text-[10px] bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded-full ml-2 font-bold">SUPER ADMIN</span>
          </div>
        </div>
        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 hover:bg-slate-800 rounded-lg text-slate-400">
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 w-64 bg-slate-900 border-r border-slate-800 transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 transition-transform z-50 flex flex-col`}>
        {/* Logo */}
        <div className="p-5 border-b border-slate-800 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-[22%] overflow-hidden flex-shrink-0"><img src={LOGO_URL} alt="Nautiqo" className="w-full h-full scale-[1.06]" /></div>
            <div>
              <h1 className="font-semibold text-white">Nautiqo</h1>
              <span className="text-[10px] bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded-full font-bold tracking-wide">SUPER ADMIN</span>
            </div>
          </div>
        </div>

        {/* Nav — scrollable */}
        <nav className="flex-1 overflow-y-auto p-3 space-y-0.5">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all ${
                location.pathname === item.path
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-slate-300 hover:bg-slate-800 hover:text-white'
              }`}
              data-testid={`sa-nav-${item.label.toLowerCase()}`}
            >
              <item.icon className={`w-[18px] h-[18px] flex-shrink-0 ${location.pathname === item.path ? 'text-white' : 'text-slate-400'}`} />
              <span className="text-sm font-medium flex-1">{item.label}</span>
              {item.badge > 0 && (
                <span className="min-w-[20px] h-5 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                  {item.badge > 9 ? "9+" : item.badge}
                </span>
              )}
            </Link>
          ))}
        </nav>

        {/* User + logout — always visible at bottom */}
        <div className="flex-shrink-0 p-3 border-t border-slate-800">
          <div className="flex items-center gap-3 px-2 py-2 mb-1">
            <div className="w-8 h-8 bg-amber-500/20 rounded-full flex items-center justify-center flex-shrink-0">
              <User className="w-4 h-4 text-amber-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.first_name || 'Super Admin'}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 w-full px-3 py-2 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-xl transition text-sm"
            data-testid="sa-logout"
          >
            <LogOut className="w-4 h-4" />
            <span className="font-medium">Esci</span>
          </button>
        </div>
      </aside>

      {/* Backdrop mobile */}
      {sidebarOpen && <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      <main className="lg:ml-64 min-h-screen p-4 lg:p-8">
        {children}
      </main>
    </div>
  );
};

// ==================== HELPERS ====================
const StatCard = ({ label, value, icon: Icon, color = "blue" }) => {
  const colors = {
    blue: "bg-blue-500/15 text-blue-400",
    amber: "bg-amber-500/15 text-amber-400",
    emerald: "bg-emerald-500/15 text-emerald-400",
    violet: "bg-violet-500/15 text-violet-400",
    red: "bg-red-500/15 text-red-400",
  };
  return (
    <div className="bg-slate-900 rounded-2xl p-5 border border-slate-800">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm text-slate-500">{label}</span>
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${colors[color]}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <p className="text-3xl font-bold text-white tracking-tight">{value}</p>
    </div>
  );
};

const CopyableField = ({ label, value, truncate = null, testId }) => {
  const [copied, setCopied] = useState(false);
  const display = truncate && value && value.length > truncate
    ? `${value.slice(0, truncate)}…${value.slice(-4)}`
    : value;
  const handleCopy = async (e) => {
    e.preventDefault();
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {}
  };
  return (
    <div className="flex items-center gap-2 text-xs" data-testid={testId}>
      <span className="text-slate-500 flex-shrink-0">{label}:</span>
      <code className="text-slate-300 bg-slate-800/50 px-2 py-0.5 rounded font-mono text-[11px] truncate max-w-[180px]">{display}</code>
      <button
        onClick={handleCopy}
        className={`px-2 py-0.5 rounded text-[10px] font-semibold transition ${copied ? "bg-emerald-500/20 text-emerald-300" : "bg-slate-800 hover:bg-slate-700 text-slate-400"}`}
        title="Copia negli appunti"
      >
        {copied ? "Copiato!" : "Copia"}
      </button>
    </div>
  );
};


const StatusBadge = ({ status }) => {
  const map = {
    trialing: { bg: "bg-amber-500/15 text-amber-400", label: "Trial" },
    trial: { bg: "bg-amber-500/15 text-amber-400", label: "Trial" },
    active: { bg: "bg-emerald-500/15 text-emerald-400", label: "Attivo" },
    past_due: { bg: "bg-red-500/15 text-red-400", label: "Pagamento in sospeso" },
    inactive: { bg: "bg-slate-600/30 text-slate-400", label: "Inattivo" },
    expired: { bg: "bg-red-500/15 text-red-400", label: "Scaduto" },
    trial_expired: { bg: "bg-orange-500/15 text-orange-400", label: "Trial scaduto" },
    cancelled: { bg: "bg-slate-700 text-slate-400", label: "Cancellato" },
  };
  const s = map[status] || { bg: "bg-slate-600/30 text-slate-400", label: status || '-' };
  return <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${s.bg}`}>{s.label}</span>;
};

// ─── Trend arrow helper ────────────────────────────────────────────────────────
const TrendBadge = ({ current, previous, suffix = "" }) => {
  if (previous === undefined || previous === null) return null;
  const delta = current - previous;
  if (delta === 0) return <span className="text-xs text-slate-500 flex items-center gap-0.5"><Minus className="w-3 h-3" /> invariato</span>;
  if (delta > 0) return <span className="text-xs text-emerald-400 flex items-center gap-0.5"><ArrowUpRight className="w-3 h-3" /> +{delta}{suffix} vs 7gg</span>;
  return <span className="text-xs text-red-400 flex items-center gap-0.5"><ArrowDownRight className="w-3 h-3" /> {delta}{suffix} vs 7gg</span>;
};

// ==================== DASHBOARD ====================
export const SADashboardPage = ({ getAuthHeaders }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [growthData, setGrowthData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(null);

  useEffect(() => {
    const headers = getAuthHeaders();
    Promise.all([
      axios.get(`${API}/admin/stats`, { headers }).catch(e => ({ data: null, _err: e })),
      axios.get(`${API}/admin/companies`, { headers }).catch(e => ({ data: [], _err: e })),
    ]).then(([statsRes, companiesRes]) => {
      const errStats = statsRes._err;
      const errCompanies = companiesRes._err;
      if (errCompanies) {
        const status = errCompanies.response?.status;
        const detail = errCompanies.response?.data?.detail || errCompanies.message;
        setApiError(`/admin/companies → ${status || 'network error'}: ${detail}`);
      }
      setStats(statsRes.data);
      const comps = Array.isArray(companiesRes.data) ? companiesRes.data : [];
      setCompanies(comps);
        // Calcola crescita ultimi 30 giorni
        const now = new Date();
        const growth = [];
        for (let i = 29; i >= 0; i--) {
          const d = new Date(now);
          d.setDate(d.getDate() - i);
          const dateStr = d.toISOString().slice(0, 10);
          const count = comps.filter(c => (c.created_at || "").slice(0, 10) === dateStr).length;
          growth.push({ 
            date: new Date(dateStr).toLocaleDateString("it-IT", { day: "2-digit", month: "short" }), 
            count 
          });
        }
        setGrowthData(growth);
      setLoading(false);
    });
  }, [getAuthHeaders]);

  if (loading) return <div className="flex items-center justify-center h-64"><RefreshCw className="w-6 h-6 text-slate-500 animate-spin" /></div>;
  if (apiError) return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-white mb-4">Dashboard</h1>
      <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-6 text-red-300 font-mono text-sm">
        <p className="font-bold text-red-200 mb-2">⚠️ Errore API — mostra questo messaggio a Claude:</p>
        <p>{apiError}</p>
        <p className="mt-2 text-xs text-slate-400">Headers inviati: {JSON.stringify(Object.keys(getAuthHeaders()))}</p>
      </div>
    </div>
  );

  const activeCompanies = companies.filter(c => c.is_active);
  const trialCompanies = companies.filter(c => c.subscription_status === 'trial');
  const paidCompanies = companies.filter(c => c.subscription_status === 'active');
  const expiredCompanies = companies.filter(c => c.subscription_status === 'expired' || c.subscription_status === 'cancelled');
  const disabledCompanies = companies.filter(c => !c.is_active);

  // Provider split for active subscriptions (multi-channel: Stripe / Apple / Google / Manual)
  const stripeCompanies = paidCompanies.filter(c => (c.billing_provider || '').toLowerCase() === 'stripe');
  const appleCompanies = paidCompanies.filter(c => (c.billing_provider || '').toLowerCase() === 'apple');
  const googleCompanies = paidCompanies.filter(c => (c.billing_provider || '').toLowerCase() === 'google');

  // Recent signups (last 7 days)
  const recentSignups = companies.filter(c => {
    if (!c.created_at) return false;
    const created = new Date(c.created_at);
    const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    return created >= weekAgo;
  });

  // Expiring soon (next 7 days)
  const expiringSoon = companies.filter(c => {
    if (!c.subscription_end_date) return false;
    const end = new Date(c.subscription_end_date);
    const now = new Date();
    const weekFromNow = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
    return end >= now && end <= weekFromNow && c.is_active;
  });

  return (
    <div data-testid="sa-dashboard">
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-2xl font-bold text-white">Dashboard</h1>
        {stats?.open_help_requests > 0 && (
          <button onClick={() => navigate("/super-admin/supporto")} className="flex items-center gap-2 px-4 py-2 bg-red-500/15 border border-red-500/30 rounded-xl text-red-400 text-sm font-semibold hover:bg-red-500/25 transition">
            <HeadphonesIcon className="w-4 h-4" />
            {stats.open_help_requests} richieste aperte
          </button>
        )}
      </div>
      <p className="text-slate-500 mb-8">Panoramica della piattaforma</p>

      {/* Main stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-slate-900 rounded-2xl border border-slate-800 p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs text-slate-500 font-medium">Aziende totali</span>
            <Building2 className="w-4 h-4 text-blue-400" />
          </div>
          <p className="text-3xl font-bold text-white mb-1">{companies.length}</p>
          <TrendBadge current={stats?.trends?.new_signups_this_week ?? 0} previous={stats?.trends?.new_signups_prev_week ?? 0} suffix=" nuove" />
        </div>
        <div className="bg-slate-900 rounded-2xl border border-slate-800 p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs text-slate-500 font-medium">Abbonamenti attivi</span>
            <Crown className="w-4 h-4 text-violet-400" />
          </div>
          <p className="text-3xl font-bold text-white mb-1">{paidCompanies.length}</p>
          <span className="text-xs text-slate-500">{trialCompanies.length} in trial</span>
        </div>
        <div className="bg-slate-900 rounded-2xl border border-slate-800 p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs text-slate-500 font-medium">Prenotazioni totali</span>
            <CalendarCheck className="w-4 h-4 text-emerald-400" />
          </div>
          <p className="text-3xl font-bold text-white mb-1">{stats?.total_bookings ?? "—"}</p>
          <TrendBadge current={stats?.trends?.bookings_this_week ?? 0} previous={stats?.trends?.bookings_prev_week ?? 0} suffix=" questa sett." />
        </div>
        <div className="bg-slate-900 rounded-2xl border border-slate-800 p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs text-slate-500 font-medium">Barche registrate</span>
            <Anchor className="w-4 h-4 text-amber-400" />
          </div>
          <p className="text-3xl font-bold text-white mb-1">{stats?.total_boats ?? "—"}</p>
          <span className="text-xs text-slate-500">{Math.round((stats?.total_boats || 0) / Math.max(companies.length, 1) * 10) / 10} media/azienda</span>
        </div>
      </div>

      {/* Subscription breakdown (multi-provider) */}
      <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6 mb-8">
        <h2 className="text-lg font-semibold text-white mb-1">Distribuzione abbonamenti</h2>
        <p className="text-xs text-slate-500 mb-5">Suddivisione per stato e canale di pagamento</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          <button onClick={() => navigate('/super-admin/aziende', { state: { filter: 'trial' } })} className="bg-amber-500/10 hover:bg-amber-500/20 rounded-xl p-4 text-center transition cursor-pointer" data-testid="sa-card-trial">
            <Clock className="w-6 h-6 text-amber-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">{trialCompanies.length}</p>
            <p className="text-xs text-amber-400 font-medium mt-1">Trial</p>
          </button>
          <button onClick={() => navigate('/super-admin/aziende', { state: { filter: 'active' }, search: '?provider=stripe' })} className="bg-violet-500/10 hover:bg-violet-500/20 rounded-xl p-4 text-center transition cursor-pointer" data-testid="sa-card-stripe">
            <Crown className="w-6 h-6 text-violet-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">{stripeCompanies.length}</p>
            <p className="text-xs text-violet-400 font-medium mt-1">Stripe (Web)</p>
          </button>
          <button onClick={() => navigate('/super-admin/aziende', { state: { filter: 'active' } })} className="bg-slate-500/10 hover:bg-slate-500/20 rounded-xl p-4 text-center transition cursor-pointer" data-testid="sa-card-apple">
            <Zap className="w-6 h-6 text-slate-300 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">{appleCompanies.length}</p>
            <p className="text-xs text-slate-300 font-medium mt-1">Apple App Store</p>
          </button>
          <button onClick={() => navigate('/super-admin/aziende', { state: { filter: 'active' } })} className="bg-emerald-500/10 hover:bg-emerald-500/20 rounded-xl p-4 text-center transition cursor-pointer" data-testid="sa-card-google">
            <Zap className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">{googleCompanies.length}</p>
            <p className="text-xs text-emerald-400 font-medium mt-1">Google Play</p>
          </button>
          <button onClick={() => navigate('/super-admin/aziende', { state: { filter: 'expired' } })} className="bg-red-500/10 hover:bg-red-500/20 rounded-xl p-4 text-center transition cursor-pointer" data-testid="sa-card-expired">
            <XCircle className="w-6 h-6 text-red-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">{expiredCompanies.length}</p>
            <p className="text-xs text-red-400 font-medium mt-1">Scaduti</p>
          </button>
          <button onClick={() => navigate('/super-admin/aziende', { state: { filter: 'disabled' } })} className="bg-slate-800/80 hover:bg-slate-700/80 rounded-xl p-4 text-center transition cursor-pointer" data-testid="sa-card-disabled">
            <Shield className="w-6 h-6 text-slate-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">{disabledCompanies.length}</p>
            <p className="text-xs text-slate-400 font-medium mt-1">Disattivati</p>
          </button>
        </div>
      </div>

        {/* Grafico crescita utenti */}
        <div className="bg-slate-900 rounded-xl p-5 border border-slate-800 mb-6">
          <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wide mb-4">
            Nuove registrazioni — ultimi 30 giorni
          </h2>
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={growthData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="colorGrowth" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#4A90D9" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#4A90D9" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="date" tick={{ fill: "#64748b", fontSize: 10 }} tickLine={false} interval={4} />
              <YAxis allowDecimals={false} tick={{ fill: "#64748b", fontSize: 10 }} tickLine={false} />
              <Tooltip
                contentStyle={{ backgroundColor: "#1e293b", border: "1px solid #334155", borderRadius: 8 }}
                labelStyle={{ color: "#94a3b8" }}
                itemStyle={{ color: "#4A90D9" }}
                formatter={(v) => [v, "Nuovi utenti"]}
              />
              <Area type="monotone" dataKey="count" stroke="#4A90D9" strokeWidth={2} fill="url(#colorGrowth)" dot={false} activeDot={{ r: 4 }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

      {/* Two column: recent signups + expiring soon */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Recent signups */}
        <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Nuove registrazioni (7 gg)</h2>
          {recentSignups.length === 0 ? (
            <p className="text-slate-500 text-sm py-4 text-center">Nessuna nuova registrazione</p>
          ) : (
            <div className="space-y-3">
              {recentSignups.map((c, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-slate-800 last:border-0">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-blue-500/15 rounded-lg flex items-center justify-center">
                      <Building2 className="w-4 h-4 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-white">{c.company_name}</p>
                      <p className="text-xs text-slate-500">{c.email}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <StatusBadge status={c.subscription_status} />
                    <p className="text-xs text-slate-600 mt-1">{new Date(c.created_at).toLocaleDateString('it-IT')}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Expiring soon */}
        <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6">
          <h2 className="text-lg font-semibold text-white mb-4">In scadenza (7 gg)</h2>
          {expiringSoon.length === 0 ? (
            <p className="text-slate-500 text-sm py-4 text-center">Nessun abbonamento in scadenza</p>
          ) : (
            <div className="space-y-3">
              {expiringSoon.map((c, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-slate-800 last:border-0">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-amber-500/15 rounded-lg flex items-center justify-center">
                      <Clock className="w-4 h-4 text-amber-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-white">{c.company_name}</p>
                      <p className="text-xs text-slate-500">{c.plan_name || 'Trial'}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-amber-400 font-medium">Scade: {new Date(c.subscription_end_date).toLocaleDateString('it-IT')}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== AZIENDE ====================
export const SAAziendePage = ({ getAuthHeaders }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [details, setDetails] = useState({});
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(location.state?.filter || "all");
  const [providerFilter, setProviderFilter] = useState("all");
  const [editingSub, setEditingSub] = useState(null);
  const [unlinkTarget, setUnlinkTarget] = useState(null);
  const [unlinkReason, setUnlinkReason] = useState("");
  const [unlinkConfirm, setUnlinkConfirm] = useState(false);
  const [unlinking, setUnlinking] = useState(false);
  const [portalLoading, setPortalLoading] = useState(null);
  const [stripeEmails, setStripeEmails] = useState(new Set());
  const [subForm, setSubForm] = useState({ plan_name: '', subscription_status: '', subscription_end_date: '' });
  const [saving, setSaving] = useState(false);
  const [extendingTrial, setExtendingTrial] = useState(null);
  const [notifyTarget, setNotifyTarget] = useState(null);
  const [notifyForm, setNotifyForm] = useState({ title: '', message: '', channel: 'both' });
  const [notifySending, setNotifySending] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkModal, setBulkModal] = useState(null); // 'extend' | 'notify' | null
  const [bulkDays, setBulkDays] = useState(7);
  const [bulkNotifyForm, setBulkNotifyForm] = useState({ title: '', message: '', channel: 'both' });
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const fetchCompanies = useCallback(async () => {
    try {
      const [extRes, stripeIdsRes] = await Promise.allSettled([
        axios.get(`${API}/admin/companies`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/stripe-companies`, { headers: getAuthHeaders() }),
      ]);
      if (extRes.status === "fulfilled") setCompanies(extRes.value.data);
      if (stripeIdsRes.status === "fulfilled") {
        const emails = (stripeIdsRes.value.data?.emails || []).map(e => e.toLowerCase());
        setStripeEmails(new Set(emails));
      }
    } catch {} finally { setLoading(false); }
  }, [getAuthHeaders]);

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);

  const loadDetail = async (id) => {
    if (details[id]) return;
    try {
      // Fetch external company details + local subscription enrichment in parallel
      const [extRes, localRes] = await Promise.allSettled([
        axios.get(`${API}/admin/companies/${id}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/local-subscription/${id}`, { headers: getAuthHeaders() }),
      ]);
      const extData = extRes.status === "fulfilled" ? extRes.value.data : {};
      const localData = localRes.status === "fulfilled" ? localRes.value.data : null;
      // Merge: local Stripe data fills in gaps (stripe_customer_id, subscription_id, period_end)
      // and — crucially — infers billing_provider='stripe' for legacy users whose external
      // record still has it null.
      const merged = { ...extData };
      if (localData && localData.subscription) {
        const ls = localData.subscription;
        if (!merged.stripe_customer_id && ls.stripe_customer_id) merged.stripe_customer_id = ls.stripe_customer_id;
        if (!merged.stripe_subscription_id && ls.stripe_subscription_id) merged.stripe_subscription_id = ls.stripe_subscription_id;
        if (!merged.billing_cycle && ls.billing_interval) {
          merged.billing_cycle = ls.billing_interval === "annual" ? "annual" : ls.billing_interval;
        }
        if (!merged.billing_provider && ls.stripe_subscription_id) {
          merged.billing_provider = "stripe";
          merged._provider_inferred = true; // flag for UI transparency
        }
        if (!merged.subscription_end_date && ls.current_period_end) {
          merged.subscription_end_date = ls.current_period_end.split("T")[0];
        }
      } else if (localData && localData.has_stripe_history && !merged.billing_provider) {
        // One-shot legacy user (payment_transactions only, no Subscription record)
        merged.billing_provider = "stripe";
        merged._provider_inferred = true;
      }
      setDetails(prev => ({ ...prev, [id]: merged }));
    } catch {}
  };

  const toggleExpand = (id) => {
    if (expanded === id) { setExpanded(null); return; }
    setExpanded(id);
    loadDetail(id);
  };

  const toggleActive = async (id, currentActive) => {
    try {
      await axios.patch(`${API}/admin/companies/${id}`, { is_active: !currentActive }, { headers: getAuthHeaders() });
      setCompanies(prev => prev.map(c => c.id === id ? { ...c, is_active: !currentActive } : c));
      setDetails(prev => {
        if (prev[id]) return { ...prev, [id]: { ...prev[id], is_active: !currentActive } };
        return prev;
      });
    } catch {}
  };

  const deleteCompany = async (id) => {
    if (!window.confirm("Sei sicuro di voler eliminare questa azienda? Questa azione è irreversibile.")) return;
    try {
      await axios.delete(`${API}/admin/companies/${id}`, { headers: getAuthHeaders() });
      setCompanies(prev => prev.filter(c => c.id !== id));
      setExpanded(null);
    } catch {}
  };

  const startEditSub = (company) => {
    setEditingSub(company.id);
    setSubForm({
      plan_name: company.plan_name || 'Trial',
      subscription_status: company.subscription_status || 'trial',
      subscription_end_date: company.subscription_end_date ? company.subscription_end_date.split('T')[0] : '',
    });
  };

  const saveSub = async (id) => {
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/companies/${id}`, subForm, { headers: getAuthHeaders() });
      setCompanies(prev => prev.map(c => c.id === id ? { ...c, ...subForm } : c));
      setDetails(prev => {
        if (prev[id]) return { ...prev, [id]: { ...prev[id], ...subForm } };
        return prev;
      });
      setEditingSub(null);
    } catch {} finally { setSaving(false); }
  };

  const handleOpenAdminPortal = async (companyId) => {
    setPortalLoading(companyId);
    try {
      const res = await axios.post(
        `${API}/admin/subscription/portal`,
        { company_id: companyId, return_url: window.location.href },
        { headers: getAuthHeaders() }
      );
      if (res.data?.url) {
        window.open(res.data.url, "_blank", "noopener,noreferrer");
      }
    } catch (e) {
      alert("Impossibile aprire il Customer Portal: " + (e.response?.data?.detail || "errore sconosciuto"));
    } finally {
      setPortalLoading(null);
    }
  };

  const handleUnlink = async () => {
    if (!unlinkTarget || !unlinkConfirm) return;
    setUnlinking(true);
    try {
      const d = details[unlinkTarget.id] || {};
      const body = {};
      if (unlinkTarget.provider === "apple" && d.apple_original_transaction_id) {
        body.apple_original_transaction_id = d.apple_original_transaction_id;
      } else if (unlinkTarget.provider === "google" && d.google_purchase_token) {
        body.google_purchase_token = d.google_purchase_token;
      } else {
        setUnlinking(false);
        return;
      }
      if (unlinkReason.trim()) body.reason = unlinkReason.trim().slice(0, 300);
      await axios.post(`${API}/iap/admin/unlink-subscription`, body, { headers: getAuthHeaders() });
      setCompanies(prev => prev.map(c => c.id === unlinkTarget.id
        ? { ...c, subscription_status: "inactive", billing_provider: null }
        : c));
      setDetails(prev => {
        if (!prev[unlinkTarget.id]) return prev;
        const next = { ...prev[unlinkTarget.id], subscription_status: "inactive", billing_provider: null };
        if (unlinkTarget.provider === "apple") next.apple_original_transaction_id = null;
        if (unlinkTarget.provider === "google") next.google_purchase_token = null;
        return { ...prev, [unlinkTarget.id]: next };
      });
      setUnlinkTarget(null);
      setUnlinkReason("");
      setUnlinkConfirm(false);
    } catch (e) {
      alert("Errore scollegando l'abbonamento: " + (e.response?.data?.detail?.message || e.response?.data?.detail || "errore sconosciuto"));
    } finally {
      setUnlinking(false);
    }
  };


  const handleExtendTrial = async (company, days) => {
    setExtendingTrial(company.id);
    try {
      const res = await axios.post(`${API}/admin/companies/${company.id}/extend-trial`, { days }, { headers: getAuthHeaders() });
      setCompanies(prev => prev.map(c => c.id === company.id
        ? { ...c, subscription_status: c.subscription_status === 'inactive' ? 'trial' : c.subscription_status, subscription_end_date: res.data.new_end_date }
        : c));
      alert(`✅ Scadenza aggiornata al ${res.data.new_end_date}`);
    } catch (e) {
      alert("Errore: " + (e.response?.data?.detail || e.message));
    } finally {
      setExtendingTrial(null);
    }
  };

  const handleNotifyCompany = async () => {
    if (!notifyTarget || !notifyForm.title.trim() || !notifyForm.message.trim()) return;
    setNotifySending(true);
    try {
      await axios.post(`${API}/admin/companies/${notifyTarget.id}/notify`, notifyForm, { headers: getAuthHeaders() });
      alert(`✅ Notifica inviata a ${notifyTarget.company_name}`);
      setNotifyTarget(null);
      setNotifyForm({ title: '', message: '', channel: 'both' });
    } catch (e) {
      alert("Errore invio: " + (e.response?.data?.detail || e.message));
    } finally {
      setNotifySending(false);
    }
  };

  const filtered = companies.filter(c => {
    const matchesSearch = !search ||
      (c.company_name || '').toLowerCase().includes(search.toLowerCase()) ||
      (c.email || '').toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' ||
      (statusFilter === 'trial' && c.subscription_status === 'trial') ||
      (statusFilter === 'active' && c.subscription_status === 'active') ||
      (statusFilter === 'expired' && (c.subscription_status === 'expired' || c.subscription_status === 'cancelled')) ||
      (statusFilter === 'disabled' && !c.is_active);
    // Infer 'stripe' for legacy users whose external record has billing_provider=null
    // but who have a stripe_subscription_id or a paid transaction in our local DB.
    // Match by EMAIL because external c.id (user_id) != local company_id (JWT claim).
    const effectiveProvider = c.billing_provider || (c.email && stripeEmails.has(c.email.toLowerCase()) ? 'stripe' : null);
    const matchesProvider = providerFilter === 'all' ||
      (providerFilter === 'manual' && !effectiveProvider) ||
      (effectiveProvider === providerFilter);
    return matchesSearch && matchesStatus && matchesProvider;
  });

  const allFilteredIds = filtered.map(c => c.id);
  const allSelected = allFilteredIds.length > 0 && allFilteredIds.every(id => selectedIds.has(id));
  const toggleAll = () => {
    if (allSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(allFilteredIds));
  };
  const toggleOne = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const bulkExtendTrial = async () => {
    setBulkLoading(true);
    const ids = [...selectedIds];
    let ok = 0, fail = 0;
    for (const id of ids) {
      try {
        await axios.post(`${API}/admin/companies/${id}/extend-trial`, { days: bulkDays }, { headers: getAuthHeaders() });
        ok++;
      } catch { fail++; }
    }
    setBulkResult(`✅ ${ok} aziende aggiornate${fail > 0 ? `, ⚠️ ${fail} errori` : ''}`);
    setBulkLoading(false);
    setSelectedIds(new Set());
    fetchCompanies();
  };

  const bulkNotify = async () => {
    setBulkLoading(true);
    const ids = [...selectedIds];
    let ok = 0, fail = 0;
    for (const id of ids) {
      try {
        await axios.post(`${API}/admin/companies/${id}/notify`, bulkNotifyForm, { headers: getAuthHeaders() });
        ok++;
      } catch { fail++; }
    }
    setBulkResult(`✅ ${ok} notifiche inviate${fail > 0 ? `, ⚠️ ${fail} errori` : ''}`);
    setBulkLoading(false);
    setSelectedIds(new Set());
  };

  const bulkExport = () => {
    const selected = companies.filter(c => selectedIds.has(c.id));
    const csv = ['id,azienda,email,piano,stato,provider,iscrizione', ...selected.map(c =>
      `"${c.id}","${c.company_name || ''}","${c.email || ''}","${c.plan_name || 'trial'}","${c.subscription_status || 'trial'}","${c.billing_provider || ''}","${(c.created_at || '').slice(0,10)}"`
    )].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob); 
    const a = document.createElement('a'); a.href = url; a.download = `nautiqo-selezione-${new Date().toISOString().slice(0,10)}.csv`; a.click(); URL.revokeObjectURL(url);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><RefreshCw className="w-6 h-6 text-slate-500 animate-spin" /></div>;

  const handleExportCsv = () => {
    const today = new Date().toISOString().slice(0, 10);
    const headers = [
      "ID", "Azienda", "Email", "Telefono", "Ruolo", "Stato abbonamento",
      "Canale", "Ciclo", "Piano", "Scadenza", "Disdetta in corso",
      "Apple Tx ID", "Google Token", "Stripe Customer ID",
      "Barche", "Prenotazioni", "Data registrazione", "Attivo"
    ];
    const rows = filtered.map(c => {
      const d = details[c.id] || {};
      const provider = c.billing_provider || d.billing_provider || (c.email && stripeEmails.has(c.email.toLowerCase()) ? "stripe" : "");
      const cycle = (c.billing_cycle || d.billing_cycle || "").toLowerCase();
      return [
        c.id || "",
        c.company_name || "",
        c.email || "",
        c.phone || "",
        c.role || "",
        c.subscription_status || "",
        provider === "stripe" ? "Stripe" : provider === "apple" ? "App Store" : provider === "google" ? "Google Play" : "Manuale",
        cycle === "annual" || cycle === "yearly" ? "Annuale" : cycle === "monthly" ? "Mensile" : "",
        c.plan_name || "",
        c.subscription_end_date || "",
        c.cancel_at_period_end ? "Sì" : "No",
        d.apple_original_transaction_id || "",
        d.google_purchase_token || "",
        d.stripe_customer_id || "",
        (d.stats?.boats_count ?? ""),
        (d.stats?.bookings_count ?? ""),
        c.created_at || "",
        c.is_active === false ? "No" : "Sì",
      ];
    });
    exportCsv({
      filename: `nautiqo-aziende-${today}.csv`,
      headers,
      rows,
    });
  };

  return (
    <div data-testid="sa-aziende">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white mb-1">Aziende</h1>
          <p className="text-slate-500">{companies.length} account registrati · {filtered.length} visibili</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <select
            value={providerFilter}
            onChange={e => setProviderFilter(e.target.value)}
            className="px-3 py-2.5 bg-slate-900 border border-slate-800 rounded-xl text-white text-sm focus:border-blue-500 outline-none"
            data-testid="sa-filter-provider"
          >
            <option value="all">Tutti i canali</option>
            <option value="stripe">Stripe (web)</option>
            <option value="apple">App Store</option>
            <option value="google">Google Play</option>
            <option value="manual">Manuali</option>
          </select>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="px-3 py-2.5 bg-slate-900 border border-slate-800 rounded-xl text-white text-sm focus:border-blue-500 outline-none"
            data-testid="sa-filter-status"
          >
            <option value="all">Tutti gli stati</option>
            <option value="trial">Trial</option>
            <option value="active">Abbonati</option>
            <option value="expired">Scaduti</option>
            <option value="disabled">Disattivati</option>
          </select>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Cerca azienda..."
              className="pl-10 pr-4 py-2.5 bg-slate-900 border border-slate-800 rounded-xl text-white placeholder-slate-600 text-sm focus:border-blue-500 outline-none w-64"
              data-testid="sa-search-companies"
            />
          </div>
          <button
            onClick={handleExportCsv}
            disabled={filtered.length === 0}
            className="flex items-center gap-2 px-4 py-2.5 bg-emerald-500/10 border border-emerald-500/20 hover:bg-emerald-500/15 disabled:opacity-40 disabled:cursor-not-allowed text-emerald-300 rounded-xl text-sm font-medium transition"
            data-testid="sa-export-csv"
            title={`Esporta ${filtered.length} ${filtered.length === 1 ? "riga" : "righe"}`}
          >
            <Download className="w-4 h-4" />
            Export CSV ({filtered.length})
          </button>
        </div>
      </div>

      {/* Select all bar */}
      {filtered.length > 0 && (
        <div className="flex items-center gap-3 px-1 mb-2">
          <input type="checkbox" checked={allSelected} onChange={toggleAll} className="rounded border-slate-600 bg-slate-800 text-blue-500 focus:ring-blue-500 focus:ring-offset-0" />
          <span className="text-slate-400 text-xs">{allSelected ? 'Deseleziona tutti' : 'Seleziona tutti'} ({filtered.length})</span>
        </div>
      )}

      <div className="space-y-3">
        {filtered.map(c => (
          <div key={c.id} className="bg-slate-900 rounded-2xl border border-slate-800 overflow-hidden" data-testid={`sa-company-${c.id}`}>
            {/* Company row */}
            <div className="flex items-center">
              <div className="pl-4 pr-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
                <input type="checkbox" checked={selectedIds.has(c.id)} onChange={() => toggleOne(c.id)} className="rounded border-slate-600 bg-slate-800 text-blue-500 focus:ring-blue-500 focus:ring-offset-0" />
              </div>
            <button onClick={() => toggleExpand(c.id)} className="w-full flex items-center justify-between p-5 hover:bg-slate-800/50 transition text-left">
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 bg-blue-500/15 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-white">{c.company_name || 'Senza nome'}</p>
                    {!c.is_active && <span className="text-[10px] bg-red-500/20 text-red-400 px-1.5 py-0.5 rounded-full font-bold">DISATTIVO</span>}
                    {c.cancel_at_period_end && <span className="text-[10px] bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded-full font-bold" title="Rinnovo annullato, accesso attivo fino a fine periodo">DISDETTA IN CORSO</span>}
                  </div>
                  <p className="text-sm text-slate-500">{c.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="hidden md:flex items-center gap-2 text-xs text-slate-400">
                  <span title="Barche attive" className="flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block"></span>
                    {c.active_boats ?? 0}
                  </span>
                  {(c.inactive_boats ?? 0) > 0 && (
                    <span title="Barche disattivate" className="flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-slate-500 inline-block"></span>
                      {c.inactive_boats}
                    </span>
                  )}
                  <span title="Prenotazioni totali" className="text-slate-500">· {c.booking_count ?? 0} pren.</span>
                </div>
                <ProviderBadge provider={c.billing_provider || (c.email && stripeEmails.has(c.email.toLowerCase()) ? 'stripe' : null)} size="sm" />
                <StatusBadge status={c.subscription_status} />
                <span className="text-xs text-slate-500 font-medium hidden md:inline">
                  {c.billing_cycle === 'yearly' || c.billing_cycle === 'annual' ? 'Annuale' : c.billing_cycle === 'monthly' ? 'Mensile' : (c.plan_name || 'Trial')}
                </span>
                {expanded === c.id ? <ChevronDown className="w-5 h-5 text-slate-500" /> : <ChevronRight className="w-5 h-5 text-slate-500" />}
              </div>
            </button>
            </div>

            {/* Expanded detail */}
            {expanded === c.id && (
              <div className="border-t border-slate-800 p-5 bg-slate-900/50">
                {details[c.id] ? (
                  <div>
                    {/* Contact & subscription info */}
                    <div className="grid sm:grid-cols-2 gap-6 mb-6">
                      <div className="space-y-3">
                        <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-2">Informazioni contatto</h3>
                        <div className="flex items-center gap-2 text-sm">
                          <Mail className="w-4 h-4 text-slate-500" />
                          <span className="text-slate-300">{details[c.id].email}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Phone className="w-4 h-4 text-slate-500" />
                          <span className="text-slate-300">{details[c.id].phone || '-'}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Clock className="w-4 h-4 text-slate-500" />
                          <span className="text-slate-300">Registrato: {new Date(details[c.id].created_at).toLocaleDateString('it-IT')}</span>
                        </div>
                        {details[c.id].last_login && (
                          <div className="flex items-center gap-2 text-sm">
                            <User className="w-4 h-4 text-slate-500" />
                            <span className="text-slate-300">Ultimo accesso: {new Date(details[c.id].last_login).toLocaleDateString('it-IT')}</span>
                          </div>
                        )}
                      </div>

                      <div>
                        <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-2">Abbonamento</h3>
                        {editingSub === c.id ? (
                          <div className="space-y-3 bg-slate-800/50 rounded-xl p-4">
                            <div>
                              <label className="text-xs text-slate-500 block mb-1">Piano</label>
                              <select
                                value={subForm.plan_name}
                                onChange={e => setSubForm(prev => ({ ...prev, plan_name: e.target.value }))}
                                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-blue-500 outline-none"
                                data-testid="sa-edit-plan"
                              >
                                <option value="Trial">Trial</option>
                                <option value="Nautiqo">Nautiqo</option>
                              </select>
                            </div>
                            <div>
                              <label className="text-xs text-slate-500 block mb-1">Stato</label>
                              <select
                                value={subForm.subscription_status}
                                onChange={e => setSubForm(prev => ({ ...prev, subscription_status: e.target.value }))}
                                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-blue-500 outline-none"
                                data-testid="sa-edit-status"
                              >
                                <option value="trialing">Trialing</option>
                                <option value="active">Attivo</option>
                                <option value="past_due">Pagamento in sospeso</option>
                                <option value="cancelled">Cancellato</option>
                                <option value="expired">Scaduto</option>
                              </select>
                            </div>
                            <div>
                              <label className="text-xs text-slate-500 block mb-1">Scadenza abbonamento</label>
                              <input
                                type="date"
                                value={subForm.subscription_end_date}
                                onChange={e => setSubForm(prev => ({ ...prev, subscription_end_date: e.target.value }))}
                                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-blue-500 outline-none"
                                data-testid="sa-edit-end-date"
                              />
                            </div>
                            <div className="flex items-center gap-2 pt-1">
                              <button
                                onClick={() => saveSub(c.id)}
                                disabled={saving}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 transition disabled:opacity-50"
                                data-testid="sa-save-sub"
                              >
                                <Save className="w-3.5 h-3.5" /> {saving ? 'Salvataggio...' : 'Salva'}
                              </button>
                              <button
                                onClick={() => setEditingSub(null)}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-700 text-slate-300 rounded-lg text-xs font-medium hover:bg-slate-600 transition"
                              >
                                <XCircle className="w-3.5 h-3.5" /> Annulla
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            <div className="flex items-center gap-2 text-sm">
                              <CreditCard className="w-4 h-4 text-slate-500" />
                              <span className="text-slate-300">Piano: <span className="font-semibold text-white">{details[c.id].plan_name || 'Trial'}</span></span>
                            </div>
                            <div className="flex items-center gap-2 text-sm">
                              <BarChart3 className="w-4 h-4 text-slate-500" />
                              <span className="text-slate-300">Stato: </span>
                              <StatusBadge status={details[c.id].subscription_status} />
                            </div>
                            <div className="flex items-center gap-2 text-sm">
                              <Clock className="w-4 h-4 text-slate-500" />
                              <span className="text-slate-300">Scadenza: {details[c.id].subscription_end_date ? new Date(details[c.id].subscription_end_date).toLocaleDateString('it-IT') : '-'}</span>
                            </div>

                            {/* Provider & billing info */}
                            <div className="flex items-center gap-2 text-sm">
                              <Sparkles className="w-4 h-4 text-slate-500" />
                              <span className="text-slate-300">Canale: </span>
                              <ProviderBadge provider={details[c.id].billing_provider} size="sm" />
                            </div>
                            <div className="flex items-center gap-2 text-sm">
                              <CalendarDays className="w-4 h-4 text-slate-500" />
                              <span className="text-slate-300">Ciclo: <span className="font-medium text-white">{
                                details[c.id].billing_cycle === 'yearly' || details[c.id].billing_cycle === 'annual' ? 'Annuale' :
                                details[c.id].billing_cycle === 'monthly' ? 'Mensile' : '—'
                              }</span></span>
                            </div>
                            {details[c.id].cancel_at_period_end && (
                              <div className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2 text-xs">
                                <AlertTriangle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
                                <span className="text-amber-200">Disdetta in corso — accesso attivo fino a scadenza</span>
                              </div>
                            )}
                            {details[c.id].is_trial && details[c.id].trial_ends_at && (
                              <div className="flex items-center gap-2 text-xs text-amber-400">
                                <Clock className="w-3.5 h-3.5" />
                                Trial termina il {new Date(details[c.id].trial_ends_at).toLocaleDateString('it-IT')}
                              </div>
                            )}

                            {/* Provider-specific identifiers with copy-to-clipboard */}
                            {details[c.id].billing_provider === 'apple' && details[c.id].apple_original_transaction_id && (
                              <CopyableField
                                label="Apple original_transaction_id"
                                value={details[c.id].apple_original_transaction_id}
                                testId="apple-tx-id"
                              />
                            )}
                            {details[c.id].billing_provider === 'apple' && details[c.id].apple_product_id && (
                              <div className="flex items-center gap-2 text-xs text-slate-400">
                                <span className="text-slate-500">Apple product:</span>
                                <code className="text-slate-300 bg-slate-800/50 px-2 py-0.5 rounded">{details[c.id].apple_product_id}</code>
                              </div>
                            )}
                            {details[c.id].billing_provider === 'google' && details[c.id].google_purchase_token && (
                              <CopyableField
                                label="Google purchase_token"
                                value={details[c.id].google_purchase_token}
                                truncate={16}
                                testId="google-tx-token"
                              />
                            )}
                            {details[c.id].billing_provider === 'google' && details[c.id].google_product_id && (
                              <div className="flex items-center gap-2 text-xs text-slate-400">
                                <span className="text-slate-500">Google product:</span>
                                <code className="text-slate-300 bg-slate-800/50 px-2 py-0.5 rounded">{details[c.id].google_product_id}</code>
                              </div>
                            )}
                            {details[c.id].billing_provider === 'stripe' && details[c.id].stripe_customer_id && (
                              <div className="flex items-center gap-2 flex-wrap">
                                <a
                                  href={`https://dashboard.stripe.com/${String(details[c.id].stripe_customer_id).startsWith('cus_test_') ? 'test/' : ''}customers/${details[c.id].stripe_customer_id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-blue-300 rounded-lg text-xs font-medium transition"
                                  data-testid="sa-open-stripe-customer"
                                >
                                  <CreditCard className="w-3.5 h-3.5" /> Apri su Stripe Dashboard
                                </a>
                                <button
                                  onClick={() => handleOpenAdminPortal(c.id)}
                                  disabled={portalLoading === c.id}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-500/10 border border-blue-500/20 hover:bg-blue-500/20 text-blue-300 rounded-lg text-xs font-medium transition disabled:opacity-50"
                                  data-testid="sa-open-customer-portal"
                                  title="Apre il Customer Portal Stripe: l'admin può cambiare carta/scaricare fatture per conto dell'utente"
                                >
                                  <ExternalLink className="w-3.5 h-3.5" />
                                  {portalLoading === c.id ? "Apertura…" : "Apri Customer Portal"}
                                </button>
                              </div>
                            )}

                            {details[c.id].staff && (
                              <div className="flex items-center gap-2 text-sm">
                                <User className="w-4 h-4 text-slate-500" />
                                <span className="text-slate-300">Staff: {details[c.id].staff.count || 0} / {details[c.id].staff.max_allowed || 0}</span>
                              </div>
                            )}

                            {/* Plan limits */}
                            {details[c.id].plan_limits && (
                              <div className="mt-2 pt-2 border-t border-slate-700/50">
                                <p className="text-xs text-slate-500 font-medium mb-2">Limiti piano</p>
                                <div className="grid grid-cols-2 gap-1.5">
                                  <div className="flex items-center gap-1.5 text-xs">
                                    <span className={`w-2 h-2 rounded-full ${details[c.id].plan_limits.public_booking ? 'bg-emerald-400' : 'bg-slate-600'}`}></span>
                                    <span className="text-slate-400">Prenotazione pubblica</span>
                                  </div>
                                  <div className="flex items-center gap-1.5 text-xs">
                                    <span className={`w-2 h-2 rounded-full ${details[c.id].plan_limits.stripe_payments ? 'bg-emerald-400' : 'bg-slate-600'}`}></span>
                                    <span className="text-slate-400">Pagamenti Stripe</span>
                                  </div>
                                  <div className="flex items-center gap-1.5 text-xs">
                                    <span className={`w-2 h-2 rounded-full ${details[c.id].plan_limits.ical_sync ? 'bg-emerald-400' : 'bg-slate-600'}`}></span>
                                    <span className="text-slate-400">Sincro iCal</span>
                                  </div>
                                  <div className="flex items-center gap-1.5 text-xs">
                                    <span className={`w-2 h-2 rounded-full ${details[c.id].plan_limits.automations ? 'bg-emerald-400' : 'bg-slate-600'}`}></span>
                                    <span className="text-slate-400">Automazioni</span>
                                  </div>
                                  <div className="flex items-center gap-1.5 text-xs">
                                    <span className="text-slate-500">Barche max:</span>
                                    <span className="text-slate-300">{details[c.id].plan_limits.max_boats === null ? 'Illimitate' : details[c.id].plan_limits.max_boats}</span>
                                  </div>
                                  <div className="flex items-center gap-1.5 text-xs">
                                    <span className="text-slate-500">Staff max:</span>
                                    <span className="text-slate-300">{details[c.id].plan_limits.max_staff || 0}</span>
                                  </div>
                                </div>
                              </div>
                            )}

                            <div className="flex items-center gap-2 mt-3 flex-wrap">
                              <button
                                onClick={() => startEditSub(c)}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-500/15 text-blue-400 rounded-lg text-xs font-medium hover:bg-blue-500/25 transition"
                                data-testid={`sa-edit-sub-${c.id}`}
                              >
                                <Edit3 className="w-3.5 h-3.5" /> Sblocca sub manuale
                              </button>
                              {(details[c.id].billing_provider === 'apple' || details[c.id].billing_provider === 'google') && (
                                <button
                                  onClick={() => setUnlinkTarget({ id: c.id, name: c.company_name, provider: details[c.id].billing_provider })}
                                  className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/10 text-red-400 border border-red-500/20 rounded-lg text-xs font-medium hover:bg-red-500/20 transition"
                                  data-testid={`sa-unlink-sub-${c.id}`}
                                >
                                  <XCircle className="w-3.5 h-3.5" /> Scollega abbonamento
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Stats row */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
                      <div className="bg-slate-800/50 rounded-xl p-3 text-center">
                        <p className="text-xl font-bold text-white">{details[c.id].stats?.bookings || 0}</p>
                        <p className="text-xs text-slate-500">Prenotazioni</p>
                      </div>
                      <div className="bg-slate-800/50 rounded-xl p-3 text-center">
                        <p className="text-xl font-bold text-white">{details[c.id].stats?.boats || 0}</p>
                        <p className="text-xs text-slate-500">Barche</p>
                      </div>
                      <div className="bg-slate-800/50 rounded-xl p-3 text-center">
                        <p className="text-xl font-bold text-white">{details[c.id].stats?.customers || 0}</p>
                        <p className="text-xs text-slate-500">Clienti</p>
                      </div>
                      <div className="bg-slate-800/50 rounded-xl p-3 text-center">
                        <p className="text-xl font-bold text-white">{(details[c.id].stats?.total_revenue || 0).toLocaleString()} €</p>
                        <p className="text-xs text-slate-500">Fatturato</p>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-wrap items-center gap-2 pt-4 border-t border-slate-800">
                      <button
                        onClick={() => navigate(`/super-admin/aziende/${c.id}`)}
                        className="flex items-center gap-2 px-3 py-2 bg-blue-500/15 text-blue-400 hover:bg-blue-500/25 rounded-xl text-sm font-medium transition"
                      >
                        <BookOpen className="w-4 h-4" /> Dettaglio
                      </button>
                      <button
                        onClick={() => setNotifyTarget(c)}
                        className="flex items-center gap-2 px-3 py-2 bg-violet-500/15 text-violet-400 hover:bg-violet-500/25 rounded-xl text-sm font-medium transition"
                      >
                        <Bell className="w-4 h-4" /> Notifica
                      </button>
                      <button
                        onClick={() => handleExtendTrial(c, 7)}
                        disabled={extendingTrial === c.id}
                        className="flex items-center gap-2 px-3 py-2 bg-amber-500/15 text-amber-400 hover:bg-amber-500/25 rounded-xl text-sm font-medium transition disabled:opacity-50"
                        title="Estendi/rinnova di 7 giorni"
                      >
                        {extendingTrial === c.id ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Clock className="w-4 h-4" />} +7gg
                      </button>
                      <button
                        onClick={() => toggleActive(c.id, c.is_active)}
                        className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition ${
                          c.is_active ? 'bg-amber-500/15 text-amber-400 hover:bg-amber-500/25' : 'bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25'
                        }`}
                        data-testid={`sa-toggle-${c.id}`}
                      >
                        {c.is_active ? <><ToggleRight className="w-4 h-4" /> Disattiva</> : <><ToggleLeft className="w-4 h-4" /> Attiva</>}
                      </button>
                      <button
                        onClick={() => deleteCompany(c.id)}
                        className="flex items-center gap-2 px-3 py-2 bg-red-500/15 text-red-400 hover:bg-red-500/25 rounded-xl text-sm font-medium transition"
                        data-testid={`sa-delete-${c.id}`}
                      >
                        <Trash2 className="w-4 h-4" /> Elimina
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="w-5 h-5 text-slate-500 animate-spin" />
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="text-center py-16 text-slate-500">
            <Building2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>Nessuna azienda trovata</p>
          </div>
        )}
      </div>

      {/* Unlink confirmation modal */}
      {unlinkTarget && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) { setUnlinkTarget(null); setUnlinkConfirm(false); setUnlinkReason(""); } }}
          data-testid="unlink-modal"
        >
          <div className="bg-slate-900 border border-red-500/30 rounded-2xl p-6 max-w-md w-full">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-red-500/15 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-white">Scollega abbonamento</h3>
            </div>
            <p className="text-sm text-slate-400 mb-4">
              Stai per scollegare l'abbonamento <span className="text-white font-medium">{unlinkTarget.provider === "apple" ? "App Store" : "Google Play"}</span> di <span className="text-white font-medium">{unlinkTarget.name}</span>.
            </p>
            <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-3 mb-4 space-y-1.5">
              <p className="text-xs text-red-200 font-semibold">Cosa succede:</p>
              <ul className="text-xs text-slate-400 space-y-1 list-disc pl-4">
                <li>L'identifier di acquisto viene rimosso dal record utente</li>
                <li>Lo status viene impostato a <code className="bg-slate-800 px-1 rounded">inactive</code></li>
                <li>L'utente perde accesso al servizio</li>
                <li><strong className="text-red-300">NON</strong> viene cancellato l'abbonamento su Apple/Google — deve farlo l'utente dalle impostazioni OS</li>
              </ul>
            </div>
            <label className="block mb-3">
              <span className="text-xs text-slate-400 mb-1 block">Motivo (opzionale)</span>
              <textarea
                value={unlinkReason}
                onChange={(e) => setUnlinkReason(e.target.value)}
                maxLength={300}
                rows={2}
                placeholder="Es. Richiesta cliente, utente duplicato, refund…"
                className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-600 outline-none focus:border-red-500/50 resize-none"
                data-testid="unlink-reason"
              />
            </label>
            <label className="flex items-start gap-2 mb-4 cursor-pointer">
              <input
                type="checkbox"
                checked={unlinkConfirm}
                onChange={(e) => setUnlinkConfirm(e.target.checked)}
                className="mt-0.5 accent-red-500"
                data-testid="unlink-confirm-checkbox"
              />
              <span className="text-xs text-slate-300">Confermo di voler scollegare questo abbonamento. Operazione irreversibile.</span>
            </label>
            <div className="flex gap-3">
              <button
                onClick={() => { setUnlinkTarget(null); setUnlinkConfirm(false); setUnlinkReason(""); }}
                disabled={unlinking}
                className="flex-1 py-2.5 border border-slate-700 text-slate-300 rounded-xl hover:bg-slate-800 transition disabled:opacity-50"
              >
                Annulla
              </button>
              <button
                onClick={handleUnlink}
                disabled={!unlinkConfirm || unlinking}
                className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 disabled:bg-red-600/40 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition"
                data-testid="unlink-submit"
              >
                {unlinking ? "Scollegamento…" : "Scollega"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notify single company modal */}
      {notifyTarget && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setNotifyTarget(null); }}>
          <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 max-w-md w-full">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-violet-500/15 flex items-center justify-center">
                <Bell className="w-5 h-5 text-violet-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Notifica a {notifyTarget.company_name}</h3>
                <p className="text-xs text-slate-500">{notifyTarget.email}</p>
              </div>
            </div>
            <div className="flex gap-2 mb-4">
              {['both','push','email'].map(ch => (
                <button key={ch} onClick={() => setNotifyForm(f => ({...f, channel: ch}))}
                  className={`flex-1 py-2 text-xs font-semibold rounded-lg transition ${notifyForm.channel === ch ? 'bg-violet-600 text-white' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'}`}>
                  {ch === 'both' ? 'Push+Email' : ch === 'push' ? 'Solo Push' : 'Solo Email'}
                </button>
              ))}
            </div>
            <input type="text" value={notifyForm.title} onChange={e => setNotifyForm(f => ({...f, title: e.target.value}))}
              placeholder="Titolo" className="w-full mb-3 px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-xl text-white text-sm placeholder-slate-600 outline-none focus:border-violet-500" />
            <textarea value={notifyForm.message} onChange={e => setNotifyForm(f => ({...f, message: e.target.value}))}
              rows={3} placeholder="Messaggio..." className="w-full mb-4 px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-xl text-white text-sm placeholder-slate-600 outline-none focus:border-violet-500 resize-none" />
            <div className="flex gap-3">
              <button onClick={() => setNotifyTarget(null)} className="flex-1 py-2.5 border border-slate-700 text-slate-400 rounded-xl hover:bg-slate-800 transition text-sm">Annulla</button>
              <button onClick={handleNotifyCompany} disabled={notifySending || !notifyForm.title.trim() || !notifyForm.message.trim()}
                className="flex-1 py-2.5 bg-violet-600 hover:bg-violet-700 disabled:opacity-40 text-white font-semibold rounded-xl transition text-sm">
                {notifySending ? 'Invio...' : 'Invia'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating Bulk Action Bar */}
      {selectedIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 bg-slate-800 border border-slate-600 rounded-2xl shadow-2xl px-5 py-3">
          <span className="text-white font-medium text-sm">{selectedIds.size} selezionati</span>
          <div className="w-px h-5 bg-slate-600" />
          <button onClick={() => { setBulkModal('extend'); setBulkResult(null); }} className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 hover:bg-amber-500 text-white rounded-lg text-xs font-medium transition-colors">
            <CalendarCheck size={13} /> Estendi Trial
          </button>
          <button onClick={() => { setBulkModal('notify'); setBulkResult(null); }} className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-xs font-medium transition-colors">
            <Bell size={13} /> Notifica
          </button>
          <button onClick={bulkExport} className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-600 hover:bg-slate-500 text-white rounded-lg text-xs font-medium transition-colors">
            <Download size={13} /> Esporta
          </button>
          <button onClick={() => setSelectedIds(new Set())} className="text-slate-400 hover:text-white ml-1">
            <X size={16} />
          </button>
        </div>
      )}

      {/* Bulk Modal */}
      {bulkModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 w-full max-w-md shadow-2xl">
            {bulkModal === 'extend' && (
              <>
                <h3 className="text-white font-semibold mb-4 flex items-center gap-2"><CalendarCheck size={18} className="text-amber-400" /> Estendi Trial — {selectedIds.size} aziende</h3>
                <div className="mb-4">
                  <label className="text-slate-400 text-sm block mb-2">Giorni da aggiungere</label>
                  <select value={bulkDays} onChange={e => setBulkDays(Number(e.target.value))} className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm">
                    <option value={7}>7 giorni</option>
                    <option value={14}>14 giorni</option>
                    <option value={30}>30 giorni</option>
                    <option value={60}>60 giorni</option>
                  </select>
                </div>
                {bulkResult && <p className="text-sm text-emerald-400 mb-3">{bulkResult}</p>}
                <div className="flex gap-3">
                  <button onClick={() => setBulkModal(null)} className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm">Annulla</button>
                  <button onClick={bulkExtendTrial} disabled={bulkLoading} className="flex-1 px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white rounded-lg text-sm disabled:opacity-60">
                    {bulkLoading ? <RefreshCw size={14} className="animate-spin mx-auto" /> : 'Conferma'}
                  </button>
                </div>
              </>
            )}
            {bulkModal === 'notify' && (
              <>
                <h3 className="text-white font-semibold mb-4 flex items-center gap-2"><Bell size={18} className="text-blue-400" /> Notifica — {selectedIds.size} aziende</h3>
                <div className="space-y-3 mb-4">
                  <input placeholder="Titolo" value={bulkNotifyForm.title} onChange={e => setBulkNotifyForm(p => ({...p, title: e.target.value}))} className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm" />
                  <textarea rows={3} placeholder="Messaggio..." value={bulkNotifyForm.message} onChange={e => setBulkNotifyForm(p => ({...p, message: e.target.value}))} className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm resize-none" />
                  <select value={bulkNotifyForm.channel} onChange={e => setBulkNotifyForm(p => ({...p, channel: e.target.value}))} className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm">
                    <option value="both">Push + Email</option>
                    <option value="push">Solo Push</option>
                    <option value="email">Solo Email</option>
                  </select>
                </div>
                {bulkResult && <p className="text-sm text-emerald-400 mb-3">{bulkResult}</p>}
                <div className="flex gap-3">
                  <button onClick={() => setBulkModal(null)} className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm">Annulla</button>
                  <button onClick={bulkNotify} disabled={bulkLoading || !bulkNotifyForm.title || !bulkNotifyForm.message} className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm disabled:opacity-60">
                    {bulkLoading ? <RefreshCw size={14} className="animate-spin mx-auto" /> : 'Invia'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Pages removed: Prenotazioni, Barche, Clienti (Super Admin is account-management only)

// ==================== PRICING CONSTANTS ====================
// Monthly price lookup. Only the current canonical "Nautiqo" plan is listed;
// legacy names (Pro, Start, Business) have been retired Apr 2026.
const PLAN_PRICING = {
  Trial: 0,
  Nautiqo: 19.90,
  'Nautiqo Mensile': 19.90,
  'Nautiqo Annuale': 16.58, // €199 / 12, used for MRR computations only
};
const COMMISSION_RATE = 0.03; // 3%

// ==================== REVENUE PAGE ====================
export const SARevenuePage = ({ getAuthHeaders }) => {
  const [companies, setCompanies] = useState([]);
  const [companyDetails, setCompanyDetails] = useState({});
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('year'); // 'month', 'quarter', 'year'

  useEffect(() => {
    const headers = getAuthHeaders();
    Promise.all([
      axios.get(`${API}/admin/companies`, { headers }).catch(() => ({ data: [] })),
      axios.get(`${API}/admin/bookings`, { headers }).catch(() => ({ data: [] })),
    ]).then(async ([companiesRes, bookingsRes]) => {
      const comps = Array.isArray(companiesRes.data) ? companiesRes.data : [];
      setCompanies(comps);
      setBookings(Array.isArray(bookingsRes.data) ? bookingsRes.data : []);

      // Load details for each company to get total_revenue
      const detailPromises = comps.map(c =>
        axios.get(`${API}/admin/companies/${c.id}`, { headers }).then(r => [c.id, r.data]).catch(() => [c.id, null])
      );
      const details = await Promise.all(detailPromises);
      const detailMap = {};
      details.forEach(([id, data]) => { if (data) detailMap[id] = data; });
      setCompanyDetails(detailMap);
      setLoading(false);
    });
  }, [getAuthHeaders]);

  if (loading) return <div className="flex items-center justify-center h-64"><RefreshCw className="w-6 h-6 text-slate-500 animate-spin" /></div>;

  // ===== CALCULATIONS =====

  // Subscription MRR
  const subscriptionMRR = companies.reduce((sum, c) => {
    if (!c.is_active) return sum;
    const price = PLAN_PRICING[c.plan_name] || 0;
    return sum + price;
  }, 0);

  // Total commission from all bookings
  const totalBookingRevenue = bookings.reduce((sum, b) => sum + (b.total_price || 0), 0);
  const totalCommission = totalBookingRevenue * COMMISSION_RATE;

  // Total platform revenue (estimated annualized subscriptions + commissions)
  const totalPlatformRevenue = (subscriptionMRR * 12) + totalCommission;

  // Subscription status breakdown for the pie chart (replaces legacy Start/Pro plans)
  const statusCounts = { Trial: 0, Attivi: 0, Cancellati: 0, Scaduti: 0 };
  companies.forEach(c => {
    const status = (c.subscription_status || 'trial').toLowerCase();
    if (status === 'active') statusCounts.Attivi++;
    else if (status === 'cancelled') statusCounts.Cancellati++;
    else if (status === 'expired') statusCounts.Scaduti++;
    else statusCounts.Trial++;
  });

  // ===== MONTHLY TREND DATA =====
  const getMonthKey = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  };

  const monthLabels = {
    '01': 'Gen', '02': 'Feb', '03': 'Mar', '04': 'Apr',
    '05': 'Mag', '06': 'Giu', '07': 'Lug', '08': 'Ago',
    '09': 'Set', '10': 'Ott', '11': 'Nov', '12': 'Dic',
  };

  // Build monthly data based on actual data range
  const now = new Date();
  const allDates = [
    ...companies.map(c => c.created_at).filter(Boolean),
    ...bookings.map(b => b.booking_date).filter(Boolean),
  ].map(d => new Date(d));

  const earliestDate = allDates.length > 0 ? new Date(Math.min(...allDates.map(d => d.getTime()))) : now;
  const latestDate = allDates.length > 0 ? new Date(Math.max(...allDates.map(d => d.getTime()), now.getTime())) : now;

  const startMonth = new Date(earliestDate.getFullYear(), earliestDate.getMonth(), 1);
  const endMonth = new Date(latestDate.getFullYear(), latestDate.getMonth(), 1);

  const monthlyData = [];
  const cursor = new Date(startMonth);
  while (cursor <= endMonth) {
    const key = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}`;
    const monthNum = String(cursor.getMonth() + 1).padStart(2, '0');

    // Companies active in this month (created before month end)
    const monthEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
    const activeComps = companies.filter(c => {
      if (!c.created_at) return false;
      return new Date(c.created_at) <= monthEnd;
    });

    const subRevenue = activeComps.reduce((sum, c) => {
      const price = PLAN_PRICING[c.plan_name] || 0;
      return sum + price;
    }, 0);

    // Bookings in this month
    const monthBookings = bookings.filter(b => {
      const bk = getMonthKey(b.booking_date);
      return bk === key;
    });
    const commRevenue = monthBookings.reduce((sum, b) => sum + (b.total_price || 0), 0) * COMMISSION_RATE;

    monthlyData.push({
      month: `${monthLabels[monthNum]} ${cursor.getFullYear().toString().slice(2)}`,
      monthKey: key,
      abbonamenti: Math.round(subRevenue),
      commissioni: Math.round(commRevenue * 100) / 100,
      totale: Math.round((subRevenue + commRevenue) * 100) / 100,
      prenotazioni: monthBookings.length,
      nuoveAziende: companies.filter(c => getMonthKey(c.created_at) === key).length,
    });
    cursor.setMonth(cursor.getMonth() + 1);
  }

  // Pie chart data for subscription status distribution
  const pieData = Object.entries(statusCounts)
    .filter(([, count]) => count > 0)
    .map(([name, value]) => ({ name, value }));
  const PIE_COLORS = { Trial: '#f59e0b', Attivi: '#22c55e', Cancellati: '#fbbf24', Scaduti: '#ef4444' };

  // Top companies by revenue
  const topCompanies = companies
    .map(c => ({
      ...c,
      revenue: companyDetails[c.id]?.stats?.total_revenue || 0,
      bookingCount: companyDetails[c.id]?.stats?.bookings || 0,
    }))
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 10);

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 shadow-xl">
        <p className="text-sm font-semibold text-white mb-2">{label}</p>
        {payload.map((p, i) => (
          <p key={i} className="text-xs" style={{ color: p.color }}>
            {p.name === 'abbonamenti' ? 'Abbonamenti' : p.name === 'commissioni' ? 'Commissioni' : 'Totale'}: €{p.value.toLocaleString()}
          </p>
        ))}
      </div>
    );
  };

  return (
    <div data-testid="sa-revenue">
      <h1 className="text-2xl font-bold text-white mb-1">Revenue</h1>
      <p className="text-slate-500 mb-8">Introiti piattaforma da abbonamenti e commissioni</p>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-slate-900 rounded-2xl p-5 border border-slate-800">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-slate-500">Revenue stimata (anno)</span>
            <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-emerald-500/15 text-emerald-400">
              <DollarSign className="w-4 h-4" />
            </div>
          </div>
          <p className="text-3xl font-bold text-white tracking-tight" data-testid="revenue-total">€{Math.round(totalPlatformRevenue).toLocaleString()}</p>
          <p className="text-xs text-slate-500 mt-1">Abbonamenti + Commissioni</p>
        </div>

        <div className="bg-slate-900 rounded-2xl p-5 border border-slate-800">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-slate-500">MRR Abbonamenti</span>
            <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-blue-500/15 text-blue-400">
              <CreditCard className="w-4 h-4" />
            </div>
          </div>
          <p className="text-3xl font-bold text-white tracking-tight" data-testid="revenue-mrr">€{subscriptionMRR.toLocaleString()}</p>
          <p className="text-xs text-slate-500 mt-1">/mese ricorrente</p>
        </div>

        <div className="bg-slate-900 rounded-2xl p-5 border border-slate-800">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-slate-500">Commissioni totali</span>
            <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-violet-500/15 text-violet-400">
              <Percent className="w-4 h-4" />
            </div>
          </div>
          <p className="text-3xl font-bold text-white tracking-tight" data-testid="revenue-commission">€{totalCommission.toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</p>
          <p className="text-xs text-slate-500 mt-1">3% su €{totalBookingRevenue.toLocaleString()} prenotazioni</p>
        </div>

        <div className="bg-slate-900 rounded-2xl p-5 border border-slate-800">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-slate-500">Prenotazioni piattaforma</span>
            <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-amber-500/15 text-amber-400">
              <CalendarDays className="w-4 h-4" />
            </div>
          </div>
          <p className="text-3xl font-bold text-white tracking-tight" data-testid="revenue-bookings">{bookings.length}</p>
          <p className="text-xs text-slate-500 mt-1">da {companies.length} aziende</p>
        </div>
      </div>

      {/* Revenue Trend Chart */}
      <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6 mb-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-white">Trend revenue mensile</h2>
          <div className="flex gap-3 text-xs">
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-blue-500 inline-block"></span><span className="text-slate-400">Abbonamenti</span></span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-violet-500 inline-block"></span><span className="text-slate-400">Commissioni</span></span>
          </div>
        </div>
        <div style={{ width: '100%', height: 320 }} data-testid="revenue-chart">
          <ResponsiveContainer>
            <AreaChart data={monthlyData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <defs>
                <linearGradient id="colorAbb" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorComm" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="month" stroke="#475569" tick={{ fill: '#64748b', fontSize: 12 }} />
              <YAxis stroke="#475569" tick={{ fill: '#64748b', fontSize: 12 }} tickFormatter={v => `€${v}`} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="abbonamenti" name="abbonamenti" stroke="#3b82f6" strokeWidth={2} fill="url(#colorAbb)" />
              <Area type="monotone" dataKey="commissioni" name="commissioni" stroke="#8b5cf6" strokeWidth={2} fill="url(#colorComm)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Two columns: Plan breakdown + Monthly table */}
      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        {/* Subscription status distribution */}
        <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6">
          <h2 className="text-lg font-semibold text-white mb-1">Distribuzione abbonamenti</h2>
          <p className="text-xs text-slate-500 mb-4">Per stato</p>
          <div style={{ width: '100%', height: 200 }} data-testid="status-pie-chart">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3}>
                  {pieData.map((entry, i) => (
                    <Cell key={i} fill={PIE_COLORS[entry.name] || '#64748b'} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '12px' }}
                  labelStyle={{ color: '#fff' }}
                  itemStyle={{ color: '#cbd5e1' }}
                  formatter={(value, name) => [`${value} aziende`, name]}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-2 mt-4">
            {Object.entries(statusCounts).map(([status, count]) => (
              <div key={status} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full" style={{ background: PIE_COLORS[status] || '#64748b' }}></span>
                  <span className="text-slate-300">{status}</span>
                </div>
                <span className="text-slate-500">{count} aziende</span>
              </div>
            ))}
          </div>
        </div>

        {/* Monthly detail table */}
        <div className="lg:col-span-2 bg-slate-900 rounded-2xl border border-slate-800 p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Dettaglio mensile</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="monthly-detail-table">
              <thead>
                <tr className="border-b border-slate-800 text-left">
                  <th className="pb-3 text-xs font-semibold text-slate-500 uppercase">Mese</th>
                  <th className="pb-3 text-xs font-semibold text-slate-500 uppercase text-right">Abbonamenti</th>
                  <th className="pb-3 text-xs font-semibold text-slate-500 uppercase text-right">Commissioni</th>
                  <th className="pb-3 text-xs font-semibold text-slate-500 uppercase text-right">Totale</th>
                  <th className="pb-3 text-xs font-semibold text-slate-500 uppercase text-right">Pren.</th>
                  <th className="pb-3 text-xs font-semibold text-slate-500 uppercase text-right">Nuove az.</th>
                </tr>
              </thead>
              <tbody>
                {monthlyData.map((m, i) => (
                  <tr key={i} className="border-b border-slate-800/50 hover:bg-slate-800/30 transition">
                    <td className="py-3 text-slate-300 font-medium">{m.month}</td>
                    <td className="py-3 text-right text-blue-400">€{m.abbonamenti.toLocaleString()}</td>
                    <td className="py-3 text-right text-violet-400">€{m.commissioni.toLocaleString()}</td>
                    <td className="py-3 text-right font-semibold text-white">€{m.totale.toLocaleString()}</td>
                    <td className="py-3 text-right text-slate-400">{m.prenotazioni}</td>
                    <td className="py-3 text-right text-slate-400">{m.nuoveAziende}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-slate-700">
                  <td className="pt-3 font-semibold text-white">Totale</td>
                  <td className="pt-3 text-right font-semibold text-blue-400">€{monthlyData.reduce((s, m) => s + m.abbonamenti, 0).toLocaleString()}</td>
                  <td className="pt-3 text-right font-semibold text-violet-400">€{Math.round(monthlyData.reduce((s, m) => s + m.commissioni, 0)).toLocaleString()}</td>
                  <td className="pt-3 text-right font-bold text-white">€{Math.round(monthlyData.reduce((s, m) => s + m.totale, 0)).toLocaleString()}</td>
                  <td className="pt-3 text-right font-semibold text-slate-400">{monthlyData.reduce((s, m) => s + m.prenotazioni, 0)}</td>
                  <td className="pt-3 text-right font-semibold text-slate-400">{monthlyData.reduce((s, m) => s + m.nuoveAziende, 0)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>

      {/* Top companies by booking revenue */}
      <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6">
        <h2 className="text-lg font-semibold text-white mb-4">Top aziende per volume prenotazioni</h2>
        {topCompanies.length === 0 ? (
          <p className="text-slate-500 text-sm text-center py-6">Nessun dato disponibile</p>
        ) : (
          <div className="space-y-3">
            {topCompanies.map((c, i) => {
              const maxRev = topCompanies[0]?.revenue || 1;
              const pct = maxRev > 0 ? Math.round((c.revenue / maxRev) * 100) : 0;
              return (
                <div key={c.id} className="flex items-center gap-4" data-testid={`top-company-${i}`}>
                  <span className="w-6 text-right text-sm font-bold text-slate-500">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-white truncate">{c.company_name}</span>
                        <StatusBadge status={c.subscription_status} />
                      </div>
                      <div className="flex items-center gap-3 text-xs text-slate-400">
                        <span>{c.bookingCount} pren.</span>
                        <span className="font-semibold text-white">€{c.revenue.toLocaleString()}</span>
                        <span className="text-violet-400">comm. €{Math.round(c.revenue * COMMISSION_RATE).toLocaleString()}</span>
                      </div>
                    </div>
                    <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-blue-500 to-violet-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};


// ==================== NOTIFICHE PAGE ====================
export const SANotifichePage = ({ getAuthHeaders }) => {
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [channel, setChannel] = useState('both');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/notifications-log`, { headers: getAuthHeaders() })
      .then(res => setHistory(Array.isArray(res.data) ? res.data : []))
      .catch(() => {})
      .finally(() => setHistoryLoading(false));
  }, [getAuthHeaders]);

  const sendNotification = async () => {
    if (!title.trim() || !message.trim()) return;
    setSending(true);
    setResult(null);
    try {
      const { data } = await axios.post(`${API}/admin/broadcast`, { title, message, channel }, { headers: getAuthHeaders() });
      setResult({ success: true, data });
      setHistory(prev => [{
        title, message, channel, sent_at: new Date().toISOString(),
        results: data,
      }, ...prev]);
      setTitle('');
      setMessage('');
    } catch (err) {
      setResult({ success: false, error: err.response?.data?.detail || 'Errore nell\'invio' });
    } finally {
      setSending(false);
    }
  };

  const channelOptions = [
    { value: 'both', label: 'Push + Email', icon: Bell, desc: 'Invia sia notifica push che email' },
    { value: 'push', label: 'Solo Push', icon: MessageSquare, desc: 'Notifica push nell\'app' },
    { value: 'email', label: 'Solo Email', icon: Mail, desc: 'Email a tutte le aziende' },
  ];

  return (
    <div data-testid="sa-notifiche">
      <h1 className="text-2xl font-bold text-white mb-1">Notifiche</h1>
      <p className="text-slate-500 mb-8">Invia notifiche push o email a tutte le aziende registrate</p>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Send form */}
        <div className="lg:col-span-2">
          <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6">
            <h2 className="text-lg font-semibold text-white mb-5">Nuova notifica</h2>

            {/* Channel selector */}
            <div className="mb-5">
              <label className="text-sm font-medium text-slate-400 block mb-3">Canale di invio</label>
              <div className="grid grid-cols-3 gap-3">
                {channelOptions.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setChannel(opt.value)}
                    className={`p-4 rounded-xl border text-left transition ${
                      channel === opt.value
                        ? 'border-blue-500 bg-blue-500/10'
                        : 'border-slate-800 bg-slate-800/30 hover:border-slate-700'
                    }`}
                    data-testid={`channel-${opt.value}`}
                  >
                    <opt.icon className={`w-5 h-5 mb-2 ${channel === opt.value ? 'text-blue-400' : 'text-slate-500'}`} />
                    <p className={`text-sm font-semibold ${channel === opt.value ? 'text-white' : 'text-slate-300'}`}>{opt.label}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{opt.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Title */}
            <div className="mb-4">
              <label className="text-sm font-medium text-slate-400 block mb-2">Titolo</label>
              <input
                type="text"
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="Es: Aggiornamento piattaforma"
                className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-xl text-white placeholder-slate-600 text-sm focus:border-blue-500 outline-none transition"
                data-testid="notification-title"
              />
            </div>

            {/* Message */}
            <div className="mb-6">
              <label className="text-sm font-medium text-slate-400 block mb-2">Messaggio</label>
              <textarea
                value={message}
                onChange={e => setMessage(e.target.value)}
                rows={5}
                placeholder="Scrivi il contenuto della notifica..."
                className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-xl text-white placeholder-slate-600 text-sm focus:border-blue-500 outline-none transition resize-none"
                data-testid="notification-message"
              />
              <p className="text-xs text-slate-600 mt-1">{message.length} caratteri</p>
            </div>

            {/* Result banner */}
            {result && (
              <div className={`mb-5 p-4 rounded-xl flex items-start gap-3 ${
                result.success ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-red-500/10 border border-red-500/20'
              }`} data-testid="notification-result">
                {result.success ? (
                  <>
                    <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-emerald-400">Notifica inviata con successo</p>
                      <div className="text-xs text-emerald-400/70 mt-1 space-y-0.5">
                        {result.data.email_sent > 0 && <p>Email inviate: {result.data.email_sent}</p>}
                        {result.data.email_failed > 0 && <p>Email fallite: {result.data.email_failed}</p>}
                        {result.data.push_sent > 0 && <p>Push inviate: {result.data.push_sent}</p>}
                        {result.data.push_failed > 0 && <p>Push fallite: {result.data.push_failed}</p>}
                        <p>Destinatari: {result.data.total_users} aziende</p>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-red-400">Errore nell'invio</p>
                      <p className="text-xs text-red-400/70 mt-1">{result.error}</p>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Send button */}
            <button
              onClick={sendNotification}
              disabled={sending || !title.trim() || !message.trim()}
              className="w-full flex items-center justify-center gap-2 px-6 py-3.5 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="send-notification-btn"
            >
              {sending ? (
                <><RefreshCw className="w-4 h-4 animate-spin" /> Invio in corso...</>
              ) : (
                <><Send className="w-4 h-4" /> Invia a tutte le aziende</>
              )}
            </button>
          </div>
        </div>

        {/* Sidebar: preview + history */}
        <div className="space-y-6">
          {/* Preview */}
          <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6">
            <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4">Anteprima</h3>
            <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-blue-500/15 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                  {channel === 'email' ? <Mail className="w-5 h-5 text-blue-400" /> : <Bell className="w-5 h-5 text-blue-400" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-white text-sm truncate">{title || 'Titolo notifica'}</p>
                  <p className="text-xs text-slate-400 mt-1 line-clamp-3">{message || 'Il messaggio apparirà qui...'}</p>
                  <div className="flex items-center gap-2 mt-3">
                    {(channel === 'push' || channel === 'both') && (
                      <span className="text-[10px] bg-violet-500/15 text-violet-400 px-2 py-0.5 rounded-full font-semibold">PUSH</span>
                    )}
                    {(channel === 'email' || channel === 'both') && (
                      <span className="text-[10px] bg-blue-500/15 text-blue-400 px-2 py-0.5 rounded-full font-semibold">EMAIL</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* History (persistent from DB) */}
          <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6">
            <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4">Storico invii</h3>
            {historyLoading ? (
              <div className="flex justify-center py-6"><RefreshCw className="w-5 h-5 text-slate-500 animate-spin" /></div>
            ) : history.length === 0 ? (
              <p className="text-xs text-slate-600 text-center py-4">Nessuna notifica inviata</p>
            ) : (
              <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
                {history.map((h, i) => (
                  <div key={i} className="bg-slate-800/50 rounded-xl p-3 border border-slate-700/30">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm font-medium text-white truncate flex-1">{h.title}</p>
                      <div className="flex gap-1 ml-2">
                        {(h.channel === 'push' || h.channel === 'both') && (
                          <span className="text-[9px] bg-violet-500/15 text-violet-400 px-1.5 py-0.5 rounded-full font-bold">PUSH</span>
                        )}
                        {(h.channel === 'email' || h.channel === 'both') && (
                          <span className="text-[9px] bg-blue-500/15 text-blue-400 px-1.5 py-0.5 rounded-full font-bold">EMAIL</span>
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-slate-500 truncate">{h.message}</p>
                    <div className="flex items-center gap-2 mt-2 text-[10px] text-slate-600">
                      <span>{new Date(h.sent_at || h.timestamp).toLocaleString('it-IT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                      <span>·</span>
                      {(h.results || h.result || {}).email_sent > 0 && <span className="text-blue-400">{(h.results || h.result || {}).email_sent} email</span>}
                      {(h.results || h.result || {}).push_sent > 0 && <span className="text-violet-400">{(h.results || h.result || {}).push_sent} push</span>}
                      <span>→ {(h.results || h.result || {}).total_users || 0} dest.</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== MARKETING PAGE ====================
export const SAMarketingPage = ({ getAuthHeaders }) => {
  // --- Template management state ---
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    name: '', subject: '', body: '', hero_title: '', hero_subtitle: '', cta_link: '', cta_label: '',
    attachment_url: '', attachment_label: '', attachment_type: ''
  });
  const [templateSaving, setTemplateSaving] = useState(false);
  const [templateError, setTemplateError] = useState(null);
  const [attachmentUploading, setAttachmentUploading] = useState(false);
  const [attachmentFileName, setAttachmentFileName] = useState('');
  const attachmentInputRef = useRef(null);

  // --- Send campaign state ---
  const [sendTemplateId, setSendTemplateId] = useState('');
  const [emailList, setEmailList] = useState('');
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  // --- CSV import state ---
  const csvInputRef = useRef(null);
  const [csvImportMsg, setCsvImportMsg] = useState(null);

  // --- Saved lists state ---
  const [savedLists, setSavedLists] = useState([]);
  const [saveListName, setSaveListName] = useState('');
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [savingList, setSavingList] = useState(false);
  const [selectedListId, setSelectedListId] = useState('');

  // --- Test email state ---
  const [testEmailTo, setTestEmailTo] = useState('');
  const [testEmailTemplate, setTestEmailTemplate] = useState('welcome');
  const [testEmailSending, setTestEmailSending] = useState(false);
  const [testEmailResult, setTestEmailResult] = useState(null);

  const loadTemplates = useCallback(() => {
    setTemplatesLoading(true);
    axios.get(`${API}/marketing/templates`, { headers: getAuthHeaders() })
      .then(res => setTemplates(Array.isArray(res.data) ? res.data : []))
      .catch(() => setTemplates([]))
      .finally(() => setTemplatesLoading(false));
  }, [getAuthHeaders]);

  const loadSavedLists = useCallback(() => {
    axios.get(`${API}/marketing/lists`, { headers: getAuthHeaders() })
      .then(res => setSavedLists(Array.isArray(res.data) ? res.data : []))
      .catch(() => setSavedLists([]));
  }, [getAuthHeaders]);

  useEffect(() => { loadTemplates(); loadSavedLists(); }, [loadTemplates, loadSavedLists]);

  const resetTemplateForm = () => {
    setTemplateForm({ name: '', subject: '', body: '', hero_title: '', hero_subtitle: '', cta_link: '', cta_label: '', attachment_url: '', attachment_label: '', attachment_type: '' });
    setTemplateError(null);
    setShowCreateForm(false);
    setEditingTemplate(null);
  };

  const startEdit = (tpl) => {
    setEditingTemplate(tpl.id);
    setTemplateForm({
      name: tpl.name || '',
      subject: tpl.subject || '',
      body: tpl.body || '',
      hero_title: tpl.hero_title || '',
      hero_subtitle: tpl.hero_subtitle || '',
      cta_link: tpl.cta_link || '',
      cta_label: tpl.cta_label || '',
      attachment_url: tpl.attachment_url || '',
      attachment_label: tpl.attachment_label || '',
      attachment_type: tpl.attachment_type || ''
    });
    setShowCreateForm(true);
    setTemplateError(null);
  };

  const uploadAttachment = async (file) => {
    if (!file) return;
    setAttachmentUploading(true);
    setAttachmentFileName(file.name);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API}/upload/marketing-attachment`, fd, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      setTemplateForm(p => ({ ...p, attachment_url: res.data.url }));
    } catch (err) {
      setTemplateError(err.response?.data?.detail || 'Errore upload allegato');
      setAttachmentFileName('');
    } finally {
      setAttachmentUploading(false);
    }
  };

  const saveTemplate = async () => {
    if (!templateForm.name.trim() || !templateForm.subject.trim() || !templateForm.body.trim()) {
      setTemplateError('Nome, oggetto e corpo sono obbligatori.');
      return;
    }
    setTemplateSaving(true);
    setTemplateError(null);
    try {
      if (editingTemplate) {
        await axios.put(`${API}/marketing/templates/${editingTemplate}`, templateForm, { headers: getAuthHeaders() });
      } else {
        await axios.post(`${API}/marketing/templates`, templateForm, { headers: getAuthHeaders() });
      }
      resetTemplateForm();
      loadTemplates();
    } catch (err) {
      setTemplateError(err.response?.data?.detail || 'Errore nel salvataggio del template');
    } finally {
      setTemplateSaving(false);
    }
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm('Eliminare questo template?')) return;
    try {
      await axios.delete(`${API}/marketing/templates/${id}`, { headers: getAuthHeaders() });
      loadTemplates();
      if (sendTemplateId === String(id)) setSendTemplateId('');
    } catch {
      alert("Errore durante l'eliminazione");
    }
  };

  const parseEmails = (raw) => {
    return raw
      .split(/[\n,;]+/)
      .map(e => e.trim().toLowerCase())
      .filter(e => e.includes('@'));
  };

  const handleCsvImport = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result;
      const lines = text.split(/\r?\n/).filter(l => l.trim());
      if (lines.length === 0) { setCsvImportMsg('File vuoto'); return; }
      const header = lines[0].split(/[,;]/).map(h => h.trim().replace(/^"|"$/g, ''));
      const emailColIdx = header.findIndex(h => /^email$/i.test(h));
      let extracted = [];
      if (emailColIdx >= 0) {
        extracted = lines.slice(1).map(line => {
          const cols = line.split(/[,;]/).map(c => c.trim().replace(/^"|"$/g, ''));
          return cols[emailColIdx] || '';
        }).filter(e => e.includes('@'));
      } else {
        extracted = lines.flatMap(line =>
          line.split(/[,;]/).map(c => c.trim().replace(/^"|"$/g, ''))
        ).filter(e => e.includes('@'));
      }
      if (extracted.length === 0) { setCsvImportMsg('Nessuna email trovata nel file'); return; }
      setEmailList(prev => {
        const existing = prev.trim();
        return existing ? existing + '\n' + extracted.join('\n') : extracted.join('\n');
      });
      setCsvImportMsg(`${extracted.length} email importate`);
      setTimeout(() => setCsvImportMsg(null), 3000);
    };
    reader.readAsText(file, 'UTF-8');
    e.target.value = '';
  };

  const sendTestEmail = async () => {
    if (!testEmailTo.trim()) { setTestEmailResult({ success: false, error: 'Inserisci un indirizzo email' }); return; }
    setTestEmailSending(true);
    setTestEmailResult(null);
    try {
      await axios.post(`${API}/admin/email/test-send`, { to: testEmailTo.trim(), template: testEmailTemplate }, { headers: getAuthHeaders() });
      setTestEmailResult({ success: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      setTestEmailResult({ success: false, error: typeof detail === 'string' ? detail : "Errore nell'invio" });
    } finally {
      setTestEmailSending(false);
    }
  };

  const saveList = async () => {
    const emails = parseEmails(emailList);
    if (!saveListName.trim()) return;
    if (emails.length === 0) { alert('Nessuna email valida nella lista'); return; }
    setSavingList(true);
    try {
      await axios.post(`${API}/marketing/lists`, { name: saveListName.trim(), emails }, { headers: getAuthHeaders() });
      setSaveListName('');
      setShowSaveForm(false);
      loadSavedLists();
    } catch { alert('Errore nel salvataggio della lista'); }
    finally { setSavingList(false); }
  };

  const loadListIntoTextarea = async (listId) => {
    if (!listId) return;
    try {
      const { data } = await axios.get(`${API}/marketing/lists/${listId}`, { headers: getAuthHeaders() });
      setEmailList((data.emails || []).join('\n'));
      setSelectedListId(listId);
    } catch { alert('Errore nel caricamento della lista'); }
  };

  const deleteList = async (listId, name) => {
    if (!window.confirm(`Eliminare la lista "${name}"?`)) return;
    try {
      await axios.delete(`${API}/marketing/lists/${listId}`, { headers: getAuthHeaders() });
      if (selectedListId === listId) setSelectedListId('');
      loadSavedLists();
    } catch { alert("Errore durante l'eliminazione"); }
  };

  const sendCampaign = async () => {
    const emails = parseEmails(emailList);
    if (!sendTemplateId) { setSendResult({ success: false, error: 'Seleziona un template' }); return; }
    if (emails.length === 0) { setSendResult({ success: false, error: 'Inserisci almeno un indirizzo email' }); return; }
    setSending(true);
    setSendResult(null);
    try {
      const payload = { template_id: sendTemplateId, emails };
      if (selectedListId) payload.list_id = selectedListId;
      const { data } = await axios.post(`${API}/marketing/send`, payload, { headers: getAuthHeaders() });
      setSendResult({ success: true, data });
      setEmailList('');
      setSelectedListId('');
    } catch (err) {
      const detail = err.response?.data?.detail;
      const errMsg = typeof detail === 'string' ? detail : Array.isArray(detail) ? 'Errore di validazione del server' : "Errore nell'invio";
      setSendResult({ success: false, error: errMsg });
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="sa-marketing">
      <h1 className="text-2xl font-bold text-white mb-1">Marketing</h1>
      <p className="text-slate-500 mb-8">Gestisci i template email e invia campagne commerciali</p>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left: templates list + create */}
        <div className="lg:col-span-2 space-y-6">
          {/* Template list */}
          <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <FileText className="w-5 h-5 text-purple-400" />
                Template email
              </h2>
              {!showCreateForm && (
                <button
                  onClick={() => { resetTemplateForm(); setShowCreateForm(true); }}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold rounded-xl transition"
                  data-testid="new-template-btn"
                >
                  <PlusCircle className="w-4 h-4" />
                  Nuovo template
                </button>
              )}
            </div>

            {/* Create / Edit form */}
            {showCreateForm && (
              <div className="mb-6 bg-slate-800/50 rounded-xl border border-slate-700 p-5">
                <h3 className="text-sm font-semibold text-slate-300 mb-4">
                  {editingTemplate ? 'Modifica template' : 'Nuovo template'}
                </h3>

                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="text-xs font-medium text-slate-400 block mb-1.5">Nome template *</label>
                    <input
                      type="text"
                      value={templateForm.name}
                      onChange={e => setTemplateForm(p => ({...p, name: e.target.value}))}
                      placeholder="Es: Newsletter Maggio"
                      className="w-full px-3 py-2.5 bg-slate-700/50 border border-slate-600 rounded-xl text-white placeholder-slate-500 text-sm focus:border-purple-500 outline-none transition"
                      data-testid="tpl-name"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-400 block mb-1.5">Oggetto email *</label>
                    <input
                      type="text"
                      value={templateForm.subject}
                      onChange={e => setTemplateForm(p => ({...p, subject: e.target.value}))}
                      placeholder="Es: Scopri le novità di Nautiqo"
                      className="w-full px-3 py-2.5 bg-slate-700/50 border border-slate-600 rounded-xl text-white placeholder-slate-500 text-sm focus:border-purple-500 outline-none transition"
                      data-testid="tpl-subject"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="text-xs font-medium text-slate-400 block mb-1.5">Corpo email * <span className="text-slate-600 font-normal">(HTML o testo)</span></label>
                  <textarea
                    value={templateForm.body}
                    onChange={e => setTemplateForm(p => ({...p, body: e.target.value}))}
                    rows={6}
                    placeholder="Scrivi il contenuto dell'email..."
                    className="w-full px-3 py-2.5 bg-slate-700/50 border border-slate-600 rounded-xl text-white placeholder-slate-500 text-sm focus:border-purple-500 outline-none transition resize-none font-mono"
                    data-testid="tpl-body"
                  />
                  <p className="text-xs text-slate-600 mt-1">{templateForm.body.length} caratteri</p>
                </div>

                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="text-xs font-medium text-slate-400 block mb-1.5">Hero title <span className="text-slate-600 font-normal">(opzionale)</span></label>
                    <input
                      type="text"
                      value={templateForm.hero_title}
                      onChange={e => setTemplateForm(p => ({...p, hero_title: e.target.value}))}
                      placeholder="Es: Novità in arrivo!"
                      className="w-full px-3 py-2.5 bg-slate-700/50 border border-slate-600 rounded-xl text-white placeholder-slate-500 text-sm focus:border-purple-500 outline-none transition"
                      data-testid="tpl-hero-title"
                    />
                  </div>
                  {templateForm.hero_title && (
                    <div>
                      <label className="text-xs font-medium text-slate-400 block mb-1.5">Hero subtitle</label>
                      <input
                        type="text"
                        value={templateForm.hero_subtitle}
                        onChange={e => setTemplateForm(p => ({...p, hero_subtitle: e.target.value}))}
                        placeholder="Es: Scopri tutte le funzionalità"
                        className="w-full px-3 py-2.5 bg-slate-700/50 border border-slate-600 rounded-xl text-white placeholder-slate-500 text-sm focus:border-purple-500 outline-none transition"
                        data-testid="tpl-hero-subtitle"
                      />
                    </div>
                  )}
                </div>

                <div className="grid sm:grid-cols-2 gap-4 mb-5">
                  <div>
                    <label className="text-xs font-medium text-slate-400 block mb-1.5">CTA link <span className="text-slate-600 font-normal">(opzionale)</span></label>
                    <input
                      type="url"
                      value={templateForm.cta_link}
                      onChange={e => setTemplateForm(p => ({...p, cta_link: e.target.value}))}
                      placeholder="https://nautiqo.cloud/..."
                      className="w-full px-3 py-2.5 bg-slate-700/50 border border-slate-600 rounded-xl text-white placeholder-slate-500 text-sm focus:border-purple-500 outline-none transition"
                      data-testid="tpl-cta-link"
                    />
                  </div>
                  {templateForm.cta_link && (
                    <div>
                      <label className="text-xs font-medium text-slate-400 block mb-1.5">CTA label</label>
                      <input
                        type="text"
                        value={templateForm.cta_label}
                        onChange={e => setTemplateForm(p => ({...p, cta_label: e.target.value}))}
                        placeholder="Es: Scopri di più"
                        className="w-full px-3 py-2.5 bg-slate-700/50 border border-slate-600 rounded-xl text-white placeholder-slate-500 text-sm focus:border-purple-500 outline-none transition"
                        data-testid="tpl-cta-label"
                      />
                    </div>
                  )}
                </div>

                {/* ── Allegato ── */}
                <div className="mb-4 p-4 rounded-xl bg-slate-700/30 border border-slate-600/50">
                  <p className="text-xs font-semibold text-slate-300 mb-3 flex items-center gap-1.5">
                    <Paperclip className="w-3.5 h-3.5" /> Allegato inline <span className="text-slate-600 font-normal">(opzionale)</span>
                  </p>

                  {/* File picker */}
                  <input
                    type="file"
                    ref={attachmentInputRef}
                    className="hidden"
                    accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.svg"
                    onChange={e => uploadAttachment(e.target.files?.[0])}
                  />
                  <div className="flex items-center gap-2 mb-3">
                    <button
                      type="button"
                      onClick={() => attachmentInputRef.current?.click()}
                      disabled={attachmentUploading}
                      className="flex items-center gap-2 px-4 py-2.5 bg-slate-600 hover:bg-slate-500 text-white text-sm font-medium rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                    >
                      {attachmentUploading
                        ? <><RefreshCw className="w-4 h-4 animate-spin" /> Caricamento...</>
                        : <><Upload className="w-4 h-4" /> Sfoglia file</>}
                    </button>
                    {attachmentFileName && !attachmentUploading && (
                      <span className="text-xs text-slate-400 truncate">{attachmentFileName}</span>
                    )}
                  </div>

                  {/* Fallback URL manuale */}
                  <div className="mb-3">
                    <label className="text-xs font-medium text-slate-500 block mb-1">oppure incolla un URL</label>
                    <input
                      type="url"
                      value={templateForm.attachment_url}
                      onChange={e => { setTemplateForm(p => ({...p, attachment_url: e.target.value})); setAttachmentFileName(''); }}
                      placeholder="https://... (PDF o immagine)"
                      className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-xl text-white placeholder-slate-600 text-sm focus:border-purple-500 outline-none transition"
                    />
                  </div>

                  {/* Preview e opzioni — visibili solo se c'è un URL */}
                  {templateForm.attachment_url && (
                    <>
                      <div className="flex items-center gap-2 mb-3 p-2.5 rounded-lg bg-slate-800/50 border border-slate-600/50">
                        <Paperclip className="w-3.5 h-3.5 text-purple-400 flex-shrink-0" />
                        <a href={templateForm.attachment_url} target="_blank" rel="noreferrer"
                           className="text-xs text-purple-300 hover:text-purple-200 truncate underline">
                          {templateForm.attachment_url}
                        </a>
                        <button
                          type="button"
                          onClick={() => { setTemplateForm(p => ({...p, attachment_url: '', attachment_label: '', attachment_type: ''})); setAttachmentFileName(''); }}
                          className="ml-auto text-slate-500 hover:text-red-400 transition flex-shrink-0"
                          title="Rimuovi allegato"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs font-medium text-slate-400 block mb-1.5">Sottotitolo allegato</label>
                          <input
                            type="text"
                            value={templateForm.attachment_label}
                            onChange={e => setTemplateForm(p => ({...p, attachment_label: e.target.value}))}
                            placeholder="Es: Guarda la presentazione dell'app"
                            className="w-full px-3 py-2.5 bg-slate-700/50 border border-slate-600 rounded-xl text-white placeholder-slate-500 text-sm focus:border-purple-500 outline-none transition"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-slate-400 block mb-1.5">Tipo</label>
                          <select
                            value={templateForm.attachment_type}
                            onChange={e => setTemplateForm(p => ({...p, attachment_type: e.target.value}))}
                            className="w-full px-3 py-2.5 bg-slate-700/50 border border-slate-600 rounded-xl text-white text-sm focus:border-purple-500 outline-none transition"
                          >
                            <option value="">Auto-detect dall'URL</option>
                            <option value="pdf">📄 PDF / Documento</option>
                            <option value="image">🖼️ Immagine</option>
                          </select>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {templateError && (
                  <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" />
                    <p className="text-sm text-red-400">{templateError}</p>
                  </div>
                )}

                <div className="flex items-center gap-3">
                  <button
                    onClick={saveTemplate}
                    disabled={templateSaving}
                    className="flex items-center gap-2 px-5 py-2.5 bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold rounded-xl transition disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="save-template-btn"
                  >
                    {templateSaving ? <><RefreshCw className="w-4 h-4 animate-spin" /> Salvataggio...</> : <><Save className="w-4 h-4" /> Salva template</>}
                  </button>
                  <button
                    onClick={resetTemplateForm}
                    className="flex items-center gap-2 px-5 py-2.5 bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm font-semibold rounded-xl transition"
                    data-testid="cancel-template-btn"
                  >
                    <XCircle className="w-4 h-4" /> Annulla
                  </button>
                </div>
              </div>
            )}

            {/* Templates list */}
            {templatesLoading ? (
              <div className="flex items-center justify-center py-10">
                <RefreshCw className="w-6 h-6 text-slate-600 animate-spin" />
              </div>
            ) : templates.length === 0 ? (
              <div className="text-center py-10">
                <FileText className="w-10 h-10 text-slate-700 mx-auto mb-3" />
                <p className="text-slate-500 text-sm">Nessun template ancora</p>
                <p className="text-slate-600 text-xs mt-1">Crea il primo template per iniziare</p>
              </div>
            ) : (
              <div className="space-y-3">
                {templates.map(tpl => (
                  <div
                    key={tpl.id}
                    className="flex items-start gap-4 p-4 bg-slate-800/40 rounded-xl border border-slate-700/50 hover:border-slate-600/50 transition"
                    data-testid={`template-${tpl.id}`}
                  >
                    <div className="w-9 h-9 bg-purple-500/15 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                      <FileText className="w-4 h-4 text-purple-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-white truncate">{tpl.name}</p>
                      <p className="text-xs text-slate-400 truncate mt-0.5">{tpl.subject}</p>
                      <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                        {tpl.hero_title && (
                          <span className="text-[10px] bg-slate-700/60 text-slate-400 px-2 py-0.5 rounded-full">Hero</span>
                        )}
                        {tpl.cta_link && (
                          <span className="text-[10px] bg-slate-700/60 text-slate-400 px-2 py-0.5 rounded-full">CTA</span>
                        )}
                        {tpl.attachment_url && (
                          <span className="text-[10px] bg-purple-700/40 text-purple-300 px-2 py-0.5 rounded-full flex items-center gap-1">
                            <Paperclip className="w-2.5 h-2.5" />{tpl.attachment_type === 'image' ? 'Immagine' : 'PDF'}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => startEdit(tpl)}
                        className="p-2 rounded-lg bg-slate-700/50 hover:bg-slate-600/50 text-slate-400 hover:text-white transition"
                        title="Modifica"
                        data-testid={`edit-template-${tpl.id}`}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => deleteTemplate(tpl.id)}
                        className="p-2 rounded-lg bg-slate-700/50 hover:bg-red-500/20 text-slate-400 hover:text-red-400 transition"
                        title="Elimina"
                        data-testid={`delete-template-${tpl.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right: send campaign */}
        <div className="space-y-6">
          <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6">
            <h2 className="text-base font-semibold text-white flex items-center gap-2 mb-5">
              <Send className="w-4 h-4 text-blue-400" />
              Invia campagna
            </h2>

            {/* Template selector */}
            <div className="mb-4">
              <label className="text-xs font-medium text-slate-400 block mb-1.5">Template</label>
              <select
                value={sendTemplateId}
                onChange={e => setSendTemplateId(e.target.value)}
                className="w-full px-3 py-2.5 bg-slate-800/50 border border-slate-700 rounded-xl text-white text-sm focus:border-blue-500 outline-none transition"
                data-testid="send-template-select"
              >
                <option value="">Seleziona template...</option>
                {templates.map(tpl => (
                  <option key={tpl.id} value={tpl.id}>{tpl.name}</option>
                ))}
              </select>
            </div>

            {/* Saved lists */}
            {savedLists.length > 0 && (
              <div className="mb-4">
                <label className="text-xs font-medium text-slate-400 block mb-1.5">Liste salvate</label>
                <div className="space-y-1.5">
                  {savedLists.map(lst => (
                    <div key={lst.id} className="flex items-center gap-2 p-2.5 bg-slate-800/50 border border-slate-700 rounded-xl">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white font-medium truncate">{lst.name}</p>
                        <p className="text-xs text-slate-500">{lst.email_count} email</p>
                      </div>
                      <button
                        onClick={() => loadListIntoTextarea(lst.id)}
                        className="px-2.5 py-1 bg-blue-600/20 hover:bg-blue-600/40 text-blue-400 text-xs font-medium rounded-lg transition"
                      >
                        Carica
                      </button>
                      <button
                        onClick={() => deleteList(lst.id, lst.name)}
                        className="px-2.5 py-1 bg-red-600/10 hover:bg-red-600/30 text-red-400 text-xs font-medium rounded-lg transition"
                      >
                        Elimina
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Email list */}
            <div className="mb-5">
              <label className="text-xs font-medium text-slate-400 block mb-1.5">
                Lista email <span className="text-slate-600 font-normal">(una per riga o separate da virgola)</span>
              </label>
              <textarea
                value={emailList}
                onChange={e => { setEmailList(e.target.value); setSelectedListId(''); }}
                rows={7}
                placeholder={`mario@example.com\nluca@example.com\ngiulia@example.com`}
                className="w-full px-3 py-2.5 bg-slate-800/50 border border-slate-700 rounded-xl text-white placeholder-slate-600 text-sm focus:border-blue-500 outline-none transition resize-none font-mono"
                data-testid="email-list-input"
              />
              <div className="flex items-center justify-between mt-1.5 flex-wrap gap-2">
                <p className="text-xs text-slate-600">
                  {parseEmails(emailList).length > 0 ? `${parseEmails(emailList).length} email valide` : 'Nessuna email inserita'}
                </p>
                <div className="flex items-center gap-2 flex-wrap">
                  {csvImportMsg && <span className="text-xs text-emerald-400">{csvImportMsg}</span>}
                  <button
                    onClick={() => csvInputRef.current?.click()}
                    className="flex items-center gap-1.5 px-2.5 py-1 bg-slate-700/60 hover:bg-slate-600/60 text-slate-300 hover:text-white text-xs font-medium rounded-lg transition"
                    data-testid="import-csv-btn"
                    title="Importa email da file CSV"
                  >
                    <Upload className="w-3 h-3" />
                    Importa CSV
                  </button>
                  <input
                    ref={csvInputRef}
                    type="file"
                    accept=".csv,text/csv"
                    onChange={handleCsvImport}
                    className="hidden"
                    data-testid="csv-file-input"
                  />
                  {parseEmails(emailList).length > 0 && (
                    <button
                      onClick={() => setShowSaveForm(v => !v)}
                      className="flex items-center gap-1.5 px-2.5 py-1 bg-emerald-600/20 hover:bg-emerald-600/40 text-emerald-400 text-xs font-medium rounded-lg transition"
                    >
                      <Bookmark className="w-3 h-3" />
                      Salva lista
                    </button>
                  )}
                </div>
              </div>
              {showSaveForm && (
                <div className="mt-2 flex items-center gap-2">
                  <input
                    type="text"
                    value={saveListName}
                    onChange={e => setSaveListName(e.target.value)}
                    placeholder="Nome lista (es. Clienti 2025)"
                    className="flex-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-xl text-white text-sm focus:border-emerald-500 outline-none"
                    onKeyDown={e => e.key === 'Enter' && saveList()}
                  />
                  <button
                    onClick={saveList}
                    disabled={savingList || !saveListName.trim()}
                    className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-xl transition disabled:opacity-40"
                  >
                    {savingList ? '...' : 'Salva'}
                  </button>
                  <button
                    onClick={() => { setShowSaveForm(false); setSaveListName(''); }}
                    className="px-3 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm rounded-xl transition"
                  >
                    ✕
                  </button>
                </div>
              )}
            </div>

            {/* Result banner */}
            {sendResult && (
              <div className={`mb-5 p-3.5 rounded-xl border text-sm ${
                sendResult.success
                  ? 'bg-emerald-500/10 border-emerald-500/20'
                  : 'bg-red-500/10 border-red-500/20'
              }`} data-testid="send-result">
                {sendResult.success ? (
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <div className="text-emerald-400">
                      <p className="font-semibold">Campagna inviata!</p>
                      <div className="text-xs mt-1 space-y-0.5 text-emerald-400/80">
                        <p>Inviate: {sendResult.data.sent}</p>
                        {sendResult.data.failed > 0 && <p className="text-red-400">Fallite: {sendResult.data.failed}</p>}
                        {sendResult.data.skipped > 0 && <p className="text-amber-400">Saltate: {sendResult.data.skipped}</p>}
                        <p>Totale: {sendResult.data.total}</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                    <div className="text-red-400">
                      <p className="font-semibold">Errore</p>
                      <p className="text-xs mt-0.5 text-red-400/80">{sendResult.error}</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Send button */}
            <button
              onClick={sendCampaign}
              disabled={sending || !sendTemplateId || parseEmails(emailList).length === 0}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold text-sm transition disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="send-campaign-btn"
            >
              {sending ? (
                <><RefreshCw className="w-4 h-4 animate-spin" /> Invio in corso...</>
              ) : (
                <><Send className="w-4 h-4" /> Invia campagna</>
              )}
            </button>
          </div>

          {/* Info card */}
          <div className="bg-slate-900 rounded-2xl border border-slate-800 p-5">
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Note</h3>
            <ul className="space-y-2 text-xs text-slate-500">
              <li className="flex items-start gap-2">
                <Check className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" />
                Le email duplicate vengono automaticamente saltate
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" />
                Gli indirizzi non validi vengono scartati
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" />
                I template supportano Hero e CTA opzionali
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" />
                CSV: colonna "email" auto-rilevata, o tutte le celle analizzate
              </li>
            </ul>
          </div>

          {/* Test email card */}
          <div className="bg-slate-900 rounded-2xl border border-slate-800 p-5">
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Email di test</h3>
            <p className="text-xs text-slate-600 mb-3">Invia un template di sistema a un indirizzo di prova</p>
            <select
              value={testEmailTemplate}
              onChange={e => setTestEmailTemplate(e.target.value)}
              className="w-full px-3 py-2 bg-slate-800/50 border border-slate-700 rounded-xl text-white text-xs mb-2 outline-none focus:border-purple-500 transition"
              data-testid="test-email-template-select"
            >
              <option value="welcome">Welcome (onboarding)</option>
              <option value="trial_15">Trial scade in 15 giorni</option>
              <option value="trial_7">Trial scade in 7 giorni</option>
              <option value="trial_1">Trial scade domani</option>
            </select>
            <input
              type="email"
              value={testEmailTo}
              onChange={e => { setTestEmailTo(e.target.value); setTestEmailResult(null); }}
              placeholder="email@test.com"
              className="w-full px-3 py-2 bg-slate-800/50 border border-slate-700 rounded-xl text-white placeholder-slate-600 text-xs mb-3 outline-none focus:border-purple-500 transition"
              data-testid="test-email-to-input"
            />
            {testEmailResult && (
              <div className={`mb-3 px-3 py-2 rounded-lg text-xs ${testEmailResult.success ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`}>
                {testEmailResult.success ? 'Email di test inviata!' : testEmailResult.error}
              </div>
            )}
            <button
              onClick={sendTestEmail}
              disabled={testEmailSending || !testEmailTo.trim()}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 text-xs font-semibold rounded-xl transition disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="send-test-email-btn"
            >
              {testEmailSending
                ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Invio...</>
                : <><Mail className="w-3.5 h-3.5" /> Invia email di test</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};


// ==================== WHATSAPP MARKETING PAGE ====================
export const SAWhatsAppPage = ({ getAuthHeaders }) => {
  const waFileInputRef = useRef(null);
  const [activeTab, setActiveTab] = useState('send'); // 'send' | 'inbox'

  // CSV recipients state
  const [recipients, setRecipients] = useState([]); // [{nome_azienda, phone}]
  const [csvError, setCsvError] = useState(null);
  const [csvMsg, setCsvMsg] = useState(null);
  const [rawPaste, setRawPaste] = useState('');
  const [showPasteArea, setShowPasteArea] = useState(false);

  // Template config
  const [templateName, setTemplateName] = useState('');
  const [templateLang, setTemplateLang] = useState('it');
  const [waTemplates, setWaTemplates] = useState([]);
  const [loadingWaTemplates, setLoadingWaTemplates] = useState(false);
  const [waTemplatesError, setWaTemplatesError] = useState(null);

  // Send state
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  // History
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [expandedHistory, setExpandedHistory] = useState(null);

  // Inbox
  const [inbox, setInbox] = useState([]);
  const [loadingInbox, setLoadingInbox] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Conversation chat view
  const [selectedChat, setSelectedChat] = useState(null); // { phone, name }
  const [chatMessages, setChatMessages] = useState([]);
  const [loadingChat, setLoadingChat] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);

  // Recipient source
  const [recipientSource, setRecipientSource] = useState('companies'); // 'companies' | 'manual' | 'csv'
  const [allCompanies, setAllCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [selectedCompanyIds, setSelectedCompanyIds] = useState(new Set());
  const [companySearch, setCompanySearch] = useState('');
  const [manualName, setManualName] = useState('');
  const [manualPhone, setManualPhone] = useState('');

  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const { data } = await axios.get(`${API}/admin/whatsapp/history`, { headers: getAuthHeaders() });
      setHistory(Array.isArray(data) ? data : []);
    } catch { setHistory([]); } finally { setLoadingHistory(false); }
  }, [getAuthHeaders]);

  const loadInbox = useCallback(async () => {
    setLoadingInbox(true);
    try {
      const { data } = await axios.get(`${API}/admin/whatsapp/inbox`, { headers: getAuthHeaders() });
      const msgs = Array.isArray(data) ? data : [];
      setInbox(msgs);
      setUnreadCount(msgs.filter(m => !m.read).length);
    } catch { setInbox([]); } finally { setLoadingInbox(false); }
  }, [getAuthHeaders]);

  const loadCompanies = useCallback(async () => {
    setLoadingCompanies(true);
    try {
      const { data } = await axios.get(`${API}/admin/companies`, { headers: getAuthHeaders() });
      setAllCompanies((Array.isArray(data) ? data : []).filter(c => c.phone?.trim()));
    } catch { setAllCompanies([]); } finally { setLoadingCompanies(false); }
  }, [getAuthHeaders]);

  const loadWaTemplates = useCallback(async () => {
    setLoadingWaTemplates(true);
    setWaTemplatesError(null);
    try {
      const { data } = await axios.get(`${API}/admin/whatsapp/templates`, { headers: getAuthHeaders() });
      setWaTemplates(Array.isArray(data) ? data : []);
    } catch (err) {
      setWaTemplatesError(err.response?.data?.detail || 'Errore nel caricamento dei template');
      setWaTemplates([]);
    } finally { setLoadingWaTemplates(false); }
  }, [getAuthHeaders]);

  const markRead = async (msgId) => {
    try {
      await axios.patch(`${API}/admin/whatsapp/inbox/${msgId}/read`, {}, { headers: getAuthHeaders() });
      setInbox(prev => prev.map(m => m.id === msgId ? { ...m, read: true } : m));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const openChat = async (phone, name) => {
    setSelectedChat({ phone, name });
    setLoadingChat(true);
    setChatMessages([]);
    setReplyText('');
    try {
      const { data } = await axios.get(`${API}/admin/whatsapp/conversation/${encodeURIComponent(phone)}`, { headers: getAuthHeaders() });
      setChatMessages(Array.isArray(data) ? data : []);
      // Update unread count after marking read
      const msgs = Array.isArray(inbox) ? inbox : [];
      setUnreadCount(msgs.filter(m => !m.read && m.from_phone !== phone).length);
      setInbox(prev => prev.map(m => m.from_phone === phone ? { ...m, read: true } : m));
    } catch { setChatMessages([]); } finally { setLoadingChat(false); }
  };

  const sendReply = async () => {
    if (!replyText.trim() || !selectedChat || sendingReply) return;
    setSendingReply(true);
    const text = replyText.trim();
    setReplyText('');
    try {
      await axios.post(`${API}/admin/whatsapp/reply`, { to_phone: selectedChat.phone, text }, { headers: getAuthHeaders() });
      // Optimistically add message
      setChatMessages(prev => [...prev, {
        id: `local_${Date.now()}`,
        from_phone: 'me',
        to_phone: selectedChat.phone,
        direction: 'outgoing',
        type: 'text',
        text,
        received_at: new Date().toISOString(),
        read: true,
      }]);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Errore nell\'invio';
      alert(detail);
      setReplyText(text);
    } finally { setSendingReply(false); }
  };


  useEffect(() => { loadHistory(); loadInbox(); loadCompanies(); loadWaTemplates(); }, [loadHistory, loadInbox, loadCompanies, loadWaTemplates]);

  // Parse CSV text into [{nome_azienda, phone}]
  const parseCsvText = (text) => {
    const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
    if (lines.length === 0) return { rows: [], error: 'File vuoto' };

    const header = lines[0].split(/[,;|\t]/).map(h => h.trim().replace(/^"|"$/g, '').toLowerCase());
    const phoneIdx = header.findIndex(h => /tel|phone|numero|cellulare|mobile/.test(h));
    const nameIdx = header.findIndex(h => /nome|company|azienda|name/.test(h));

    if (phoneIdx === -1) return { rows: [], error: 'Colonna telefono non trovata. Usa "telefono", "phone" o "numero" come intestazione.' };

    const rows = lines.slice(1).map(line => {
      const cols = line.split(/[,;|\t]/).map(c => c.trim().replace(/^"|"$/g, ''));
      return {
        nome_azienda: nameIdx >= 0 ? (cols[nameIdx] || '') : '',
        phone: cols[phoneIdx] || '',
      };
    }).filter(r => r.phone);

    if (rows.length === 0) return { rows: [], error: 'Nessun numero di telefono trovato' };
    return { rows, error: null };
  };

  const handleCsvFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const { rows, error } = parseCsvText(ev.target.result);
      if (error) { setCsvError(error); return; }
      setCsvError(null);
      setRecipients(rows);
      setCsvMsg(`${rows.length} destinatari importati`);
      setTimeout(() => setCsvMsg(null), 3000);
    };
    reader.readAsText(file, 'UTF-8');
    e.target.value = '';
  };

  const handlePasteImport = () => {
    const { rows, error } = parseCsvText(rawPaste);
    if (error) { setCsvError(error); return; }
    setCsvError(null);
    setRecipients(rows);
    setRawPaste('');
    setShowPasteArea(false);
    setCsvMsg(`${rows.length} destinatari importati`);
    setTimeout(() => setCsvMsg(null), 3000);
  };

  const removeRecipient = (idx) => setRecipients(prev => prev.filter((_, i) => i !== idx));
  const clearAll = () => { setRecipients([]); setSendResult(null); };

  const [exportingCompanies, setExportingCompanies] = useState(false);

  const exportCompaniesCSV = async () => {
    setExportingCompanies(true);
    try {
      const { data } = await axios.get(`${API}/admin/companies`, { headers: getAuthHeaders() });
      const companies = Array.isArray(data) ? data : [];
      const withPhone = companies.filter(c => c.phone && c.phone.trim());
      if (withPhone.length === 0) { alert('Nessuna azienda con numero di telefono registrato.'); return; }
      const rows = [['nome_azienda', 'telefono'], ...withPhone.map(c => [
        `"${(c.company_name || '').replace(/"/g, '""')}"`,
        `"${(c.phone || '').replace(/"/g, '""')}"`,
      ])];
      const csv = rows.map(r => r.join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `nautiqo_aziende_whatsapp_${new Date().toISOString().slice(0,10)}.csv`;
      a.click(); URL.revokeObjectURL(url);
    } catch { alert('Errore nel caricamento aziende'); } finally { setExportingCompanies(false); }
  };

  const send = async () => {
    if (!templateName.trim()) { setSendResult({ ok: false, error: 'Inserisci il nome del template' }); return; }
    if (recipients.length === 0) { setSendResult({ ok: false, error: 'Importa almeno un destinatario' }); return; }
    setSending(true);
    setSendResult(null);
    try {
      const { data } = await axios.post(`${API}/admin/whatsapp/send`, {
        template_name: templateName.trim(),
        template_lang: templateLang || 'it',
        recipients: recipients.map(r => ({ phone: r.phone, nome_azienda: r.nome_azienda || '' })),
      }, { headers: getAuthHeaders() });
      setSendResult({ ok: true, data });
      setRecipients([]);
      loadHistory();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setSendResult({ ok: false, error: typeof detail === 'string' ? detail : 'Errore nell\'invio' });
    } finally { setSending(false); }
  };

  return (
    <div data-testid="sa-whatsapp">
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-2xl font-bold text-white">WhatsApp</h1>
      </div>
      <p className="text-slate-500 mb-6">Invio campagne e messaggi in arrivo dai clienti</p>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-slate-900 border border-slate-800 rounded-xl p-1 w-fit">
        <button
          onClick={() => setActiveTab('send')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${activeTab === 'send' ? 'bg-green-600 text-white' : 'text-slate-400 hover:text-white'}`}
        >
          <Send className="w-4 h-4" /> Invio campagne
        </button>
        <button
          onClick={() => { setActiveTab('inbox'); loadInbox(); }}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition relative ${activeTab === 'inbox' ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-white'}`}
        >
          <MessageCircle className="w-4 h-4" /> Messaggi ricevuti
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      </div>

      {/* ── INBOX TAB ── */}
      {activeTab === 'inbox' && (() => {
        // Group inbox by contact phone (incoming: from_phone, outgoing: to_phone)
        const convMap = {};
        [...inbox].sort((a, b) => a.received_at < b.received_at ? -1 : 1).forEach(m => {
          const phone = m.direction === 'outgoing' ? m.to_phone : m.from_phone;
          if (!phone || phone === 'me') return;
          if (!convMap[phone]) convMap[phone] = { phone, name: m.sender_name || '', messages: [], unread: 0 };
          if (m.sender_name && !convMap[phone].name) convMap[phone].name = m.sender_name;
          convMap[phone].messages.push(m);
          if (!m.read && m.direction !== 'outgoing') convMap[phone].unread++;
        });
        const conversations = Object.values(convMap).sort((a, b) => {
          const la = a.messages[a.messages.length - 1]?.received_at || '';
          const lb = b.messages[b.messages.length - 1]?.received_at || '';
          return la < lb ? 1 : -1;
        });

        return (
          <div className="flex gap-4 h-[calc(100vh-220px)] min-h-[500px]">
            {/* ── Conversation list (left) ── */}
            <div className="w-80 flex-shrink-0 bg-slate-900 rounded-2xl border border-slate-800 flex flex-col overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
                <h2 className="text-sm font-semibold text-white flex items-center gap-2">
                  <MessageCircle className="w-4 h-4 text-green-400" />
                  Conversazioni
                  {unreadCount > 0 && (
                    <span className="text-xs font-semibold px-2 py-0.5 bg-green-500/15 text-green-400 rounded-full">{unreadCount}</span>
                  )}
                </h2>
                <button onClick={() => { loadInbox(); }} className="p-1.5 hover:bg-slate-800 rounded-lg text-slate-400 transition">
                  <RefreshCw className={`w-3.5 h-3.5 ${loadingInbox ? 'animate-spin' : ''}`} />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto">
                {loadingInbox ? (
                  <div className="text-center py-10 text-slate-500 text-sm">Caricamento...</div>
                ) : conversations.length === 0 ? (
                  <div className="text-center py-10 px-4">
                    <MessageCircle className="w-8 h-8 text-slate-700 mx-auto mb-2" />
                    <p className="text-slate-500 text-xs">Nessun messaggio ricevuto</p>
                  </div>
                ) : conversations.map(conv => {
                  const last = conv.messages[conv.messages.length - 1];
                  const isSelected = selectedChat?.phone === conv.phone;
                  return (
                    <button key={conv.phone} onClick={() => openChat(conv.phone, conv.name)}
                      className={`w-full flex items-start gap-3 px-4 py-3 border-b border-slate-800/60 text-left transition hover:bg-slate-800/60 ${isSelected ? 'bg-slate-800' : ''}`}
                    >
                      <div className="w-9 h-9 rounded-full bg-slate-700 flex items-center justify-center flex-shrink-0 text-sm font-bold text-slate-300">
                        {(conv.name || conv.phone || '?')[0].toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-1 mb-0.5">
                          <span className="text-sm font-semibold text-white truncate">{conv.name || conv.phone}</span>
                          <span className="text-[10px] text-slate-500 flex-shrink-0">{last ? fmtDate(last.received_at) : ''}</span>
                        </div>
                        <div className="flex items-center justify-between gap-1">
                          <p className="text-xs text-slate-400 truncate">
                            {last?.direction === 'outgoing' ? <span className="text-slate-500">Tu: </span> : ''}{last?.text || ''}
                          </p>
                          {conv.unread > 0 && (
                            <span className="flex-shrink-0 w-4 h-4 bg-green-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">{conv.unread}</span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* ── Chat view (right) ── */}
            <div className="flex-1 bg-slate-900 rounded-2xl border border-slate-800 flex flex-col overflow-hidden">
              {!selectedChat ? (
                <div className="flex-1 flex flex-col items-center justify-center text-center px-8">
                  <MessageCircle className="w-12 h-12 text-slate-700 mb-3" />
                  <p className="text-slate-500 text-sm">Seleziona una conversazione</p>
                  <p className="text-slate-600 text-xs mt-1">Clicca su un contatto per vedere la chat</p>
                </div>
              ) : (
                <>
                  {/* Chat header */}
                  <div className="flex items-center gap-3 px-5 py-3 border-b border-slate-800">
                    <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-sm font-bold text-slate-300">
                      {(selectedChat.name || selectedChat.phone)[0].toUpperCase()}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white">{selectedChat.name || 'Sconosciuto'}</p>
                      <p className="text-xs text-green-400 font-mono">+{selectedChat.phone}</p>
                    </div>
                    <div className="ml-auto">
                      <span className="text-[10px] text-slate-500 bg-slate-800 px-2 py-1 rounded-lg">Finestra 24h Meta</span>
                    </div>
                  </div>

                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-2" ref={el => { if (el) el.scrollTop = el.scrollHeight; }}>
                    {loadingChat ? (
                      <div className="text-center py-10 text-slate-500 text-sm">Caricamento...</div>
                    ) : chatMessages.length === 0 ? (
                      <div className="text-center py-10 text-slate-600 text-sm">Nessun messaggio</div>
                    ) : chatMessages.map(msg => {
                      const isOut = msg.direction === 'outgoing';
                      return (
                        <div key={msg.id} className={`flex ${isOut ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[70%] rounded-2xl px-3.5 py-2 ${isOut ? 'bg-green-600 text-white rounded-br-sm' : 'bg-slate-800 text-slate-100 rounded-bl-sm'}`}>
                            <p className="text-sm leading-relaxed">{msg.text}</p>
                            <p className={`text-[10px] mt-1 ${isOut ? 'text-green-200' : 'text-slate-500'}`}>{fmtDate(msg.received_at)}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Reply input */}
                  <div className="px-4 py-3 border-t border-slate-800 flex gap-2 items-end">
                    <textarea
                      value={replyText}
                      onChange={e => setReplyText(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                      placeholder="Scrivi un messaggio... (Invio per inviare)"
                      rows={1}
                      className="flex-1 bg-slate-800 border border-slate-700 rounded-xl px-3 py-2 text-sm text-white placeholder-slate-500 resize-none outline-none focus:border-green-500 transition"
                      style={{ minHeight: '40px', maxHeight: '120px' }}
                    />
                    <button
                      onClick={sendReply}
                      disabled={!replyText.trim() || sendingReply}
                      className="flex-shrink-0 w-10 h-10 bg-green-600 hover:bg-green-500 disabled:bg-slate-700 disabled:text-slate-500 text-white rounded-xl flex items-center justify-center transition"
                    >
                      <Send className="w-4 h-4" />
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        );
      })()}

      {/* ── SEND TAB ── */}
      {activeTab === 'send' && (
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left: recipients */}
        <div className="lg:col-span-2 space-y-5">

          {/* Source selector */}
          <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6">
            <div className="flex gap-1 mb-5 bg-slate-800 rounded-xl p-1">
              {[['companies', Building2, 'Aziende Nautiqo'], ['manual', UserPlus, 'Aggiungi manuale'], ['csv', Upload, 'CSV']].map(([key, Icon, label]) => (
                <button key={key} onClick={() => setRecipientSource(key)}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium transition ${recipientSource === key ? 'bg-green-600 text-white' : 'text-slate-400 hover:text-white'}`}>
                  <Icon className="w-3.5 h-3.5" />{label}
                </button>
              ))}
            </div>

            {/* ── Aziende Nautiqo ── */}
            {recipientSource === 'companies' && (
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
                    <input value={companySearch} onChange={e => setCompanySearch(e.target.value)}
                      placeholder="Cerca azienda..." className="w-full pl-9 pr-3 py-2 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-500 text-sm outline-none focus:border-green-500 transition" />
                  </div>
                  <button onClick={() => {
                    const visible = allCompanies.filter(c => !companySearch || c.company_name?.toLowerCase().includes(companySearch.toLowerCase()));
                    const allSelected = visible.every(c => selectedCompanyIds.has(c.id));
                    setSelectedCompanyIds(prev => {
                      const next = new Set(prev);
                      visible.forEach(c => allSelected ? next.delete(c.id) : next.add(c.id));
                      return next;
                    });
                  }} className="text-xs text-slate-400 hover:text-white whitespace-nowrap transition">
                    {allCompanies.filter(c => !companySearch || c.company_name?.toLowerCase().includes(companySearch.toLowerCase())).every(c => selectedCompanyIds.has(c.id)) ? 'Deseleziona tutti' : 'Seleziona tutti'}
                  </button>
                </div>

                {loadingCompanies ? (
                  <div className="text-center py-8 text-slate-500 text-sm">Caricamento aziende...</div>
                ) : (
                  <div className="space-y-1 max-h-64 overflow-y-auto pr-1">
                    {allCompanies.filter(c => !companySearch || c.company_name?.toLowerCase().includes(companySearch.toLowerCase())).map(c => (
                      <label key={c.id} className="flex items-center gap-3 py-2 px-3 bg-slate-800/40 hover:bg-slate-800 rounded-xl cursor-pointer group transition">
                        <input type="checkbox" checked={selectedCompanyIds.has(c.id)}
                          onChange={() => setSelectedCompanyIds(prev => { const n = new Set(prev); n.has(c.id) ? n.delete(c.id) : n.add(c.id); return n; })}
                          className="rounded border-slate-600 bg-slate-700 text-green-500 focus:ring-green-500 focus:ring-offset-0" />
                        <span className="flex-1 text-sm text-white truncate">{c.company_name || c.email}</span>
                        <span className="text-xs text-green-400 flex-shrink-0">{c.phone}</span>
                      </label>
                    ))}
                    {allCompanies.filter(c => !companySearch || c.company_name?.toLowerCase().includes(companySearch.toLowerCase())).length === 0 && (
                      <p className="text-center py-6 text-slate-500 text-sm">Nessuna azienda con telefono trovata</p>
                    )}
                  </div>
                )}

                <button
                  disabled={selectedCompanyIds.size === 0}
                  onClick={() => {
                    const toAdd = allCompanies.filter(c => selectedCompanyIds.has(c.id)).map(c => ({ nome_azienda: c.company_name || '', phone: c.phone }));
                    setRecipients(prev => {
                      const existing = new Set(prev.map(r => r.phone));
                      return [...prev, ...toAdd.filter(r => !existing.has(r.phone))];
                    });
                    setSelectedCompanyIds(new Set());
                    setCsvMsg(`${toAdd.length} aziend${toAdd.length === 1 ? 'a aggiunta' : 'e aggiunte'}`);
                    setTimeout(() => setCsvMsg(null), 3000);
                  }}
                  className="mt-3 w-full py-2.5 bg-green-600 hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-xl transition"
                >
                  Aggiungi {selectedCompanyIds.size > 0 ? `${selectedCompanyIds.size} selezionat${selectedCompanyIds.size === 1 ? 'a' : 'e'}` : 'selezionate'}
                </button>
              </div>
            )}

            {/* ── Aggiungi manuale ── */}
            {recipientSource === 'manual' && (
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-slate-400 block mb-1.5">Nome azienda</label>
                  <input value={manualName} onChange={e => setManualName(e.target.value)}
                    placeholder="es. Seawave Charter"
                    className="w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-500 text-sm outline-none focus:border-green-500 transition" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-400 block mb-1.5">Numero di telefono *</label>
                  <input value={manualPhone} onChange={e => setManualPhone(e.target.value)}
                    placeholder="es. 3391234567 oppure +39339..."
                    className="w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-500 text-sm outline-none focus:border-green-500 transition" />
                </div>
                <button
                  disabled={!manualPhone.trim()}
                  onClick={() => {
                    const phone = manualPhone.trim();
                    if (!phone) return;
                    if (recipients.some(r => r.phone === phone)) { setCsvError('Numero già presente'); return; }
                    setRecipients(prev => [...prev, { nome_azienda: manualName.trim(), phone }]);
                    setManualName(''); setManualPhone(''); setCsvError(null);
                    setCsvMsg('Destinatario aggiunto'); setTimeout(() => setCsvMsg(null), 2000);
                  }}
                  className="w-full py-2.5 bg-green-600 hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-xl transition"
                >
                  Aggiungi
                </button>
                {csvError && <p className="text-xs text-red-400">✗ {csvError}</p>}
                {csvMsg && <p className="text-xs text-green-400">✓ {csvMsg}</p>}
              </div>
            )}

            {/* ── CSV ── */}
            {recipientSource === 'csv' && (
              <div>
                <div className="p-4 border-2 border-dashed border-slate-700 rounded-xl mb-4 text-center hover:border-green-500/50 transition cursor-pointer"
                  onClick={() => waFileInputRef.current?.click()}>
                  <Upload className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                  <p className="text-sm text-slate-400 font-medium">Clicca per caricare un file CSV</p>
                  <p className="text-xs text-slate-600 mt-1">Colonne: <code className="text-green-400">nome_azienda</code> (opz.) e <code className="text-green-400">telefono</code></p>
                  <input ref={waFileInputRef} type="file" accept=".csv,.txt" onChange={handleCsvFile} className="hidden" />
                </div>
                <button onClick={() => setShowPasteArea(v => !v)}
                  className="w-full flex items-center justify-center gap-2 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm font-medium rounded-xl transition">
                  <Copy className="w-4 h-4" />{showPasteArea ? 'Nascondi' : 'Incolla testo CSV'}
                </button>
                {showPasteArea && (
                  <div className="mt-3 space-y-2">
                    <textarea value={rawPaste} onChange={e => setRawPaste(e.target.value)}
                      placeholder={"nome_azienda,telefono\nSeawave Charter,3391234567"} rows={5}
                      className="w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-600 text-sm font-mono outline-none focus:border-green-500 transition resize-none" />
                    <button onClick={handlePasteImport} disabled={!rawPaste.trim()}
                      className="w-full py-2 bg-green-600 hover:bg-green-700 disabled:opacity-40 text-white text-sm font-semibold rounded-xl transition">Importa</button>
                  </div>
                )}
                {csvError && <p className="mt-2 text-xs text-red-400">✗ {csvError}</p>}
                {csvMsg && <p className="mt-2 text-xs text-green-400">✓ {csvMsg}</p>}
              </div>
            )}
          </div>

          {/* Recipient list */}
          {recipients.length > 0 && (
            <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                  <UsersIcon className="w-5 h-5 text-slate-400" />
                  Destinatari
                  <span className="text-sm font-normal text-slate-500">({recipients.length})</span>
                </h2>
                <button onClick={clearAll} className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1 transition">
                  <Trash2 className="w-3.5 h-3.5" /> Svuota
                </button>
              </div>
              <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
                {recipients.map((r, i) => (
                  <div key={i} className="flex items-center justify-between py-2 px-3 bg-slate-800/50 rounded-xl group">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="text-sm text-white truncate">{r.nome_azienda || <span className="text-slate-500 italic">—</span>}</span>
                      <span className="text-xs text-green-400 flex items-center gap-1 flex-shrink-0">
                        <Phone className="w-3 h-3" />{r.phone}
                      </span>
                    </div>
                    <button onClick={() => removeRecipient(i)} className="p-1 text-slate-700 group-hover:text-red-400 transition flex-shrink-0 ml-2">
                      <XCircle className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* History */}
          <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <Clock className="w-5 h-5 text-slate-400" />
                Storico invii
              </h2>
              <button onClick={loadHistory} className="p-2 hover:bg-slate-800 rounded-lg text-slate-400 transition">
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
            {loadingHistory ? (
              <div className="text-center py-6 text-slate-500 text-sm">Caricamento...</div>
            ) : history.length === 0 ? (
              <div className="text-center py-6 text-slate-500 text-sm">Nessun invio ancora</div>
            ) : (
              <div className="space-y-3">
                {history.map((h, i) => (
                  <div key={h.id || i} className="border border-slate-700/50 rounded-xl overflow-hidden">
                    <div
                      className="flex items-center justify-between p-3 cursor-pointer hover:bg-slate-800/30 transition"
                      onClick={() => setExpandedHistory(expandedHistory === h.id ? null : h.id)}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${h.fail_count === 0 ? 'bg-green-400' : h.ok_count === 0 ? 'bg-red-400' : 'bg-amber-400'}`} />
                        <div className="min-w-0">
                          <span className="text-sm font-medium text-white">{h.template_name}</span>
                          <span className="text-xs text-slate-500 ml-2">{fmtDate(h.sent_at)}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <span className="text-xs text-emerald-400">{h.ok_count} ok</span>
                        {h.fail_count > 0 && <span className="text-xs text-red-400">{h.fail_count} err</span>}
                        <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${expandedHistory === h.id ? 'rotate-180' : ''}`} />
                      </div>
                    </div>
                    {expandedHistory === h.id && (
                      <div className="border-t border-slate-700/50 p-3 space-y-1 max-h-48 overflow-y-auto">
                        {(h.results || []).map((r, j) => (
                          <div key={j} className="flex items-center justify-between text-xs py-1 border-b border-slate-800 last:border-0">
                            <span className={`${r.ok ? 'text-slate-300' : 'text-red-300'} truncate mr-2`}>{r.nome_azienda || r.phone}</span>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span className="text-slate-600 text-[10px]">{r.phone}</span>
                              <span className={r.ok ? 'text-emerald-400' : 'text-red-400'}>{r.ok ? '✓' : `✗ ${r.error || 'Errore'}`}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right: template + send */}
        <div className="space-y-5">
          <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-green-400" />
                Template WhatsApp
              </h2>
              <button onClick={loadWaTemplates} className="p-1.5 hover:bg-slate-800 rounded-lg text-slate-400 transition" title="Ricarica">
                <RefreshCw className={`w-3.5 h-3.5 ${loadingWaTemplates ? 'animate-spin' : ''}`} />
              </button>
            </div>

            {waTemplatesError ? (
              <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-xl mb-4">
                <p className="text-xs text-red-400">{waTemplatesError}</p>
              </div>
            ) : loadingWaTemplates ? (
              <div className="text-center py-6 text-slate-500 text-sm">Caricamento template...</div>
            ) : waTemplates.length === 0 ? (
              <div className="text-center py-6 text-slate-600 text-sm">Nessun template approvato trovato</div>
            ) : (
              <div className="space-y-2 mb-4">
                {waTemplates.map(t => {
                  const isSelected = templateName === t.name;
                  return (
                    <button key={`${t.name}_${t.language}`}
                      onClick={() => { setTemplateName(t.name); setTemplateLang(t.language); }}
                      className={`w-full text-left p-3 rounded-xl border transition ${isSelected ? 'border-green-500 bg-green-500/10' : 'border-slate-700 hover:border-slate-600 bg-slate-800/40'}`}
                    >
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="text-sm font-semibold text-white font-mono">{t.name}</span>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <span className="text-[10px] text-slate-400 bg-slate-700 px-1.5 py-0.5 rounded">{t.language}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${t.category === 'MARKETING' ? 'bg-purple-500/20 text-purple-300' : t.category === 'UTILITY' ? 'bg-blue-500/20 text-blue-300' : 'bg-slate-700 text-slate-400'}`}>{t.category}</span>
                        </div>
                      </div>
                      {t.body && (
                        <p className="text-xs text-slate-400 leading-relaxed line-clamp-2">{t.body}</p>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {templateName && (
              <div className="p-3 bg-green-500/5 border border-green-500/20 rounded-xl mb-3">
                <p className="text-[11px] text-green-400 font-medium mb-0.5">Template selezionato</p>
                <p className="text-xs text-slate-300 font-mono">{templateName} <span className="text-slate-500">({templateLang})</span></p>
              </div>
            )}

            <div className="p-3 bg-slate-800/60 rounded-xl">
              <p className="text-[11px] text-slate-400 font-medium mb-1">Variabile automatica</p>
              <p className="text-[11px] text-slate-500 leading-relaxed">
                Il valore di <code className="text-green-400 font-mono">{'{{nome_azienda}}'}</code> nel template viene
                sostituito con il nome dell'azienda per ogni destinatario.
              </p>
            </div>
          </div>

          <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6">
            <div className="mb-4 text-sm text-slate-400">
              {recipients.length === 0
                ? 'Importa destinatari dal CSV'
                : <span className="text-white font-medium">{recipients.length} destinatar{recipients.length === 1 ? 'io' : 'i'} pronti</span>}
            </div>

            {sendResult && (
              <div className={`mb-4 p-3 rounded-xl text-sm ${sendResult.ok ? 'bg-green-500/10 border border-green-500/20 text-green-400' : 'bg-red-500/10 border border-red-500/20 text-red-400'}`}>
                {sendResult.ok
                  ? `✓ Inviati ${sendResult.data?.ok}/${sendResult.data?.sent} messaggi`
                  : `✗ ${sendResult.error}`}
                {sendResult.ok && sendResult.data?.failed > 0 && (
                  <span className="text-amber-400 ml-1">({sendResult.data.failed} falliti)</span>
                )}
              </div>
            )}

            <button
              onClick={send}
              disabled={sending || recipients.length === 0 || !templateName.trim()}
              className="w-full flex items-center justify-center gap-2 py-3 bg-green-600 hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition"
            >
              {sending
                ? <><RefreshCw className="w-4 h-4 animate-spin" /> Invio in corso...</>
                : <><Send className="w-4 h-4" /> Invia a {recipients.length || '…'} destinatari</>}
            </button>

            <div className="mt-4 p-3 bg-amber-500/5 border border-amber-500/15 rounded-xl">
              <p className="text-[11px] text-amber-400/80 leading-relaxed">
                ⚠️ Assicurati che il template sia approvato su Meta e che <code className="font-mono">WHATSAPP_ENABLED=true</code> sia impostato su Railway.
              </p>
            </div>
          </div>
        </div>
      </div>
      )} {/* end activeTab === 'send' */}
    </div>
  );
};


// ==================== INSIGHTS PAGE (analytics dashboard) ====================
const REASON_PALETTE = {
  price: "#f87171",
  features: "#a78bfa",
  low_usage: "#fbbf24",
  alternative: "#60a5fa",
  other: "#94a3b8",
};

const KPICard = ({ icon: Icon, label, value, sub, trend, color = "blue", testId }) => {
  const colorMap = {
    blue: { ring: "from-blue-500/20", iconBg: "bg-blue-500/15", iconText: "text-blue-300" },
    emerald: { ring: "from-emerald-500/20", iconBg: "bg-emerald-500/15", iconText: "text-emerald-300" },
    violet: { ring: "from-violet-500/20", iconBg: "bg-violet-500/15", iconText: "text-violet-300" },
    amber: { ring: "from-amber-500/20", iconBg: "bg-amber-500/15", iconText: "text-amber-300" },
    red: { ring: "from-red-500/20", iconBg: "bg-red-500/15", iconText: "text-red-300" },
  }[color];
  const trendVal = typeof trend === "number" ? trend : null;
  const trendPositive = trendVal !== null && trendVal > 0;
  const TrendIcon = trendVal === null ? null : trendPositive ? TrendingUp : trendVal < 0 ? TrendingDown : null;
  return (
    <div className={`relative overflow-hidden rounded-2xl bg-slate-900/60 border border-slate-800 p-5 hover:border-slate-700 transition`} data-testid={testId}>
      <div className={`absolute inset-x-0 top-0 h-px bg-gradient-to-r ${colorMap.ring} via-transparent to-transparent`} />
      <div className="flex items-start justify-between mb-3">
        <div className={`w-9 h-9 rounded-xl ${colorMap.iconBg} flex items-center justify-center`}>
          <Icon className={`w-4 h-4 ${colorMap.iconText}`} />
        </div>
        {TrendIcon && (
          <div className={`flex items-center gap-1 text-[11px] font-semibold ${trendPositive ? "text-emerald-400" : "text-red-400"}`}>
            <TrendIcon className="w-3 h-3" />
            {trendPositive ? "+" : ""}{trendVal}{typeof trend === "number" ? "%" : ""}
          </div>
        )}
      </div>
      <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">{label}</p>
      <p className="text-2xl font-bold text-white tabular-nums">{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
    </div>
  );
};

export const SAInsightsPage = ({ getAuthHeaders }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/admin/insights`, { headers: getAuthHeaders() })
      .then((res) => { setData(res.data); setError(null); })
      .catch((e) => setError(e.response?.data?.detail || "Errore caricamento"))
      .finally(() => setLoading(false));
  }, [getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="p-6 max-w-7xl mx-auto" data-testid="insights-loading">
        <div className="h-7 w-44 bg-slate-800 rounded mb-2 animate-pulse" />
        <div className="h-4 w-72 bg-slate-800 rounded mb-8 animate-pulse" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[0, 1, 2, 3, 4, 5, 6, 7].map(i => (
            <div key={i} className="h-28 bg-slate-900/60 border border-slate-800 rounded-2xl animate-pulse" />
          ))}
        </div>
        <div className="grid lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 h-80 bg-slate-900/60 border border-slate-800 rounded-2xl animate-pulse" />
          <div className="h-80 bg-slate-900/60 border border-slate-800 rounded-2xl animate-pulse" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 max-w-3xl mx-auto" data-testid="insights-error">
        <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-6 text-red-300">
          <p className="font-semibold">Impossibile caricare gli insights</p>
          <p className="text-sm mt-1">{error}</p>
          <button onClick={load} className="mt-3 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg text-sm font-medium">Riprova</button>
        </div>
      </div>
    );
  }

  const k = data.kpi || {};
  const statusData = Object.entries(data.subscriptions_by_status || {}).filter(([, v]) => v > 0).map(([key, value]) => ({
    name: key === "active" ? "Attivi" : key === "cancelled" ? "Cancel. pending" : key === "past_due" ? "Past due" : key === "expired" ? "Scaduti" : "Altri",
    value,
    color: { active: "#22c55e", cancelled: "#fbbf24", past_due: "#f97316", expired: "#ef4444", other: "#64748b" }[key],
  }));
  const billingData = [
    { name: "Mensile", value: (data.billing_distribution || {}).monthly || 0, color: "#60a5fa" },
    { name: "Annuale", value: (data.billing_distribution || {}).annual || 0, color: "#a78bfa" },
  ].filter(d => d.value > 0);

  const reasonsData = (data.cancel_reasons || []).map(r => ({ ...r, fill: REASON_PALETTE[r.reason] || "#64748b" }));
  const totalReasons = reasonsData.reduce((s, r) => s + r.count, 0);

  return (
    <div className="p-4 lg:p-6 max-w-7xl mx-auto" data-testid="insights-page">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h1 className="text-2xl font-bold text-white">Insights</h1>
          <p className="text-slate-500 text-sm">Salute commerciale, churn, e motivi di cancellazione</p>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-2 px-3 py-2 bg-slate-900/60 hover:bg-slate-900 border border-slate-800 rounded-lg text-xs text-slate-300"
          data-testid="insights-refresh"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Aggiorna
        </button>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 mb-4">
        <KPICard icon={DollarSign} label="MRR Stripe" value={fmtEur(k.mrr)} sub="Preciso (da transazioni)" color="emerald" testId="kpi-mrr" />
        <KPICard icon={TrendingUp} label="ARR" value={fmtEur(k.arr, false)} sub="Proiezione annuale Stripe" color="emerald" testId="kpi-arr" />
        <KPICard icon={UsersIcon} label="Paganti Stripe" value={k.paying_total} sub={`${k.cancel_pending} con disdetta in corso`} color="blue" testId="kpi-paying" />
        <KPICard icon={Sparkles} label="Trial attivi" value={k.trials_active} sub="In periodo di prova" color="amber" testId="kpi-trials" />
        <KPICard icon={UserPlus} label="Nuovi (30g)" value={k.new_subs_30d} sub="Trial → paganti" color="violet" testId="kpi-new-subs" />
        <KPICard icon={UserMinus} label="Annullamenti (30g)" value={k.cancellations_30d} sub="Rinnovo annullato" color="amber" testId="kpi-cancels" />
        <KPICard icon={Percent} label="Churn rate (30g)" value={`${k.churn_pct_30d}%`} sub="Cancel / paganti" color={k.churn_pct_30d > 10 ? "red" : "emerald"} testId="kpi-churn" />
        <KPICard icon={Activity} label="Stato salute" value={k.churn_pct_30d <= 5 ? "Ottima" : k.churn_pct_30d <= 10 ? "Buona" : "Da migliorare"} sub="Basato su churn" color={k.churn_pct_30d <= 5 ? "emerald" : k.churn_pct_30d <= 10 ? "blue" : "amber"} testId="kpi-health" />
      </div>

      {/* Inline error banner if backend returned a fallback payload (degraded mode) */}
      {data.error && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-4 mb-6 flex items-start gap-3" data-testid="insights-degraded-banner">
          <Activity className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-amber-200">Insights in modalità ridotta</p>
            <p className="text-xs text-amber-300/80 mt-1">Il server ha restituito dati parziali. Causa tecnica: <code className="text-amber-200">{data.error}</code></p>
            <p className="text-xs text-slate-400 mt-2">Suggerimento: rideploya l'app dopo aver aggiornato le variabili Stripe in produzione.</p>
          </div>
        </div>
      )}

      {/* MRR breakdown by provider */}
      <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-5 mb-6" data-testid="mrr-breakdown">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-sm font-semibold text-white flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-emerald-400" /> MRR totale (cross-provider)
            </h2>
            <p className="text-[11px] text-slate-500">Stripe è preciso, Apple e Google sono stimati sul listino EUR</p>
          </div>
          <div className="text-right">
            <p className="text-3xl font-bold text-white tabular-nums">{fmtEur(k.mrr_total_estimated)}<span className="text-base text-slate-500 font-normal">*</span></p>
            <p className="text-[10px] text-slate-500 uppercase tracking-wider">MRR Totale</p>
          </div>
        </div>
        <div className="grid sm:grid-cols-4 gap-3 mb-3">
          {[
            { key: "stripe", label: "Stripe (web)", icon: CreditCard, color: "text-blue-300", dot: "bg-blue-400", sub: "preciso" },
            { key: "apple", label: "Apple App Store", icon: null, color: "text-slate-200", dot: "bg-slate-200", sub: "stimato" },
            { key: "google", label: "Google Play", icon: null, color: "text-emerald-300", dot: "bg-emerald-400", sub: "stimato" },
            { key: "manual", label: "Manuale / Legacy", icon: null, color: "text-slate-400", dot: "bg-slate-500", sub: "non conteggiato" },
          ].map(p => {
            const pb = (data.provider_breakdown || {})[p.key] || { count: 0, mrr: 0, monthly: 0, annual: 0 };
            return (
              <div key={p.key} className="bg-slate-950/40 border border-slate-800 rounded-xl p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`w-2 h-2 rounded-full ${p.dot}`} />
                  <span className="text-[11px] font-semibold text-slate-300 flex-1">{p.label}</span>
                </div>
                <p className="text-lg font-bold text-white tabular-nums">{fmtEur(pb.mrr)}{p.key === "manual" && "—"}</p>
                <p className="text-[10px] text-slate-500 mt-0.5">
                  {pb.count} {pb.count === 1 ? "utente" : "utenti"}
                  {pb.monthly > 0 && ` · ${pb.monthly}M`}
                  {pb.annual > 0 && ` · ${pb.annual}A`}
                  <span className="italic"> · {p.sub}</span>
                </p>
              </div>
            );
          })}
        </div>
        <p className="text-[10px] text-slate-500 italic leading-relaxed">
          * Apple e Google sono stimati sul prezzo di listino Nautiqo (€19,90/mese · €199/anno). Il revenue netto reale, al netto delle commissioni 15-30% degli store e degli eventuali refund, è visibile solo su App Store Connect e Google Play Console.
        </p>
      </div>

      {/* Timeline + Distributions */}
      <div className="grid lg:grid-cols-3 gap-4 mb-6">
        {/* Timeline area chart */}
        <div className="lg:col-span-2 bg-slate-900/60 border border-slate-800 rounded-2xl p-5" data-testid="insights-timeline">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-sm font-semibold text-white">Andamento ultimi 30 giorni</h2>
              <p className="text-[11px] text-slate-500">Pagamenti e nuove subscription</p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={data.timeline_30d || []}>
              <defs>
                <linearGradient id="gradPaid" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#22c55e" stopOpacity={0.45} />
                  <stop offset="100%" stopColor="#22c55e" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gradAmount" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#60a5fa" stopOpacity={0.45} />
                  <stop offset="100%" stopColor="#60a5fa" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="date" stroke="#475569" tick={{ fontSize: 10 }} tickFormatter={fmtDateShort} interval={4} />
              <YAxis yAxisId="left" stroke="#475569" tick={{ fontSize: 10 }} />
              <YAxis yAxisId="right" orientation="right" stroke="#475569" tick={{ fontSize: 10 }} tickFormatter={(v) => `€${v}`} />
              <Tooltip
                contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 8, fontSize: 12 }}
                labelFormatter={fmtDate}
                formatter={(value, name) => name === "Incasso (€)" ? [fmtEur(value), name] : [value, name]}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Area yAxisId="left" type="monotone" dataKey="paid" name="Pagamenti" stroke="#22c55e" fill="url(#gradPaid)" strokeWidth={2} />
              <Area yAxisId="right" type="monotone" dataKey="amount" name="Incasso (€)" stroke="#60a5fa" fill="url(#gradAmount)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Status pie */}
        <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-5" data-testid="insights-status-distribution">
          <h2 className="text-sm font-semibold text-white mb-1">Stato abbonamenti</h2>
          <p className="text-[11px] text-slate-500 mb-4">Snapshot attuale</p>
          {statusData.length === 0 ? (
            <div className="flex items-center justify-center h-44 text-xs text-slate-600">Nessun dato</div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={75} paddingAngle={2}>
                  {statusData.map((entry, i) => <Cell key={i} fill={entry.color} stroke="#0f172a" strokeWidth={2} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 8, fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
          <div className="space-y-1.5 mt-3">
            {statusData.map((d) => (
              <div key={d.name} className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-2 text-slate-300">
                  <span className="w-2 h-2 rounded-full" style={{ background: d.color }} />
                  {d.name}
                </span>
                <span className="text-white font-medium tabular-nums">{d.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Cancel reasons + Billing */}
      <div className="grid lg:grid-cols-3 gap-4 mb-6">
        <div className="lg:col-span-2 bg-slate-900/60 border border-slate-800 rounded-2xl p-5" data-testid="insights-reasons">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-sm font-semibold text-white flex items-center gap-2">
                <UserMinus className="w-4 h-4 text-amber-400" /> Motivi di cancellazione
              </h2>
              <p className="text-[11px] text-slate-500">{totalReasons === 0 ? "Nessuna risposta ancora raccolta" : `${totalReasons} risposte totali`}</p>
            </div>
          </div>
          {totalReasons === 0 ? (
            <div className="text-center py-12 text-sm text-slate-500">
              Quando un utente annulla il rinnovo automatico, il motivo verrà raccolto qui per l'analisi del prodotto.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={reasonsData} layout="vertical" margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" horizontal={false} />
                <XAxis type="number" stroke="#475569" tick={{ fontSize: 11 }} />
                <YAxis dataKey="label" type="category" stroke="#475569" tick={{ fontSize: 11 }} width={140} />
                <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 8, fontSize: 12 }} formatter={(v) => [v, "Risposte"]} />
                <Bar dataKey="count" radius={[0, 6, 6, 0]}>
                  {reasonsData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-5" data-testid="insights-billing">
          <h2 className="text-sm font-semibold text-white mb-1">Tipologia piano</h2>
          <p className="text-[11px] text-slate-500 mb-4">Mensile vs Annuale</p>
          {billingData.length === 0 ? (
            <div className="flex items-center justify-center h-44 text-xs text-slate-600">Nessun piano attivo</div>
          ) : (
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={billingData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={75} label={(e) => e.name}>
                  {billingData.map((entry, i) => <Cell key={i} fill={entry.color} stroke="#0f172a" strokeWidth={2} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 8, fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
          <div className="space-y-1.5 mt-3">
            {billingData.map((d) => (
              <div key={d.name} className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-2 text-slate-300">
                  <span className="w-2 h-2 rounded-full" style={{ background: d.color }} />{d.name}
                </span>
                <span className="text-white font-medium tabular-nums">{d.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent feedback notes + Recent paid */}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-5" data-testid="insights-feedback-notes">
          <h2 className="text-sm font-semibold text-white flex items-center gap-2 mb-4">
            <MessageCircle className="w-4 h-4 text-violet-400" /> Note dagli utenti che annullano
          </h2>
          {(data.recent_feedback_notes || []).length === 0 ? (
            <div className="text-center py-8 text-xs text-slate-500">Nessuna nota raccolta finora.</div>
          ) : (
            <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
              {(data.recent_feedback_notes || []).map((f, i) => (
                <div key={i} className="border-l-2 pl-3 py-1" style={{ borderColor: REASON_PALETTE[f.reason] || "#64748b" }}>
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-[10px] uppercase tracking-wider font-bold" style={{ color: REASON_PALETTE[f.reason] || "#94a3b8" }}>{f.label}</span>
                    <span className="text-[10px] text-slate-600">{fmtDate(f.created_at)}</span>
                  </div>
                  <p className="text-xs text-slate-300 italic leading-relaxed">"{f.note}"</p>
                  <p className="text-[10px] text-slate-600 mt-0.5">— {f.user_email || "anonimo"}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-5" data-testid="insights-recent-paid">
          <h2 className="text-sm font-semibold text-white flex items-center gap-2 mb-4">
            <CreditCard className="w-4 h-4 text-emerald-400" /> Ultimi pagamenti
          </h2>
          {(data.recent_paid_transactions || []).length === 0 ? (
            <div className="text-center py-8 text-xs text-slate-500">Nessun pagamento finora.</div>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
              {(data.recent_paid_transactions || []).map((t, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-slate-800 last:border-0">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-white truncate">{t.user_email || "—"}</p>
                    <p className="text-[10px] text-slate-500">
                      {t.billing_interval === "annual" ? "Annuale" : "Mensile"} · {fmtDate(t.created_at)}
                    </p>
                  </div>
                  <span className="text-sm font-semibold text-emerald-300 tabular-nums whitespace-nowrap">+{fmtEur(t.amount)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};



// ==================== SUPPORTO PAGE ====================
export const SASupportoPage = ({ getAuthHeaders }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/help-requests`, { headers: getAuthHeaders() });
      setRequests(Array.isArray(res.data) ? res.data : []);
    } catch {} finally { setLoading(false); }
  }, [getAuthHeaders]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const resolve = async (id, newStatus) => {
    try {
      await axios.patch(`${API}/admin/help-requests/${id}`, { status: newStatus }, { headers: getAuthHeaders() });
      setRequests(prev => prev.map(r => r.id === id ? { ...r, status: newStatus } : r));
    } catch (e) { alert("Errore: " + e.message); }
  };

  const filtered = requests.filter(r => statusFilter === "all" || r.status === statusFilter);
  const openCount = requests.filter(r => r.status === "open").length;

  return (
    <div data-testid="sa-supporto">
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-2xl font-bold text-white">Supporto</h1>
        {openCount > 0 && (
          <span className="px-3 py-1 bg-red-500/15 border border-red-500/30 text-red-400 text-sm font-semibold rounded-full">
            {openCount} aperte
          </span>
        )}
      </div>
      <p className="text-slate-500 mb-6">Richieste di aiuto inviate dagli utenti dall'app</p>

      {/* Filters */}
      <div className="flex gap-2 mb-6">
        {[["all","Tutte"], ["open","Aperte"], ["resolved","Risolte"]].map(([v, l]) => (
          <button key={v} onClick={() => setStatusFilter(v)}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition ${statusFilter === v ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'}`}>
            {l}
          </button>
        ))}
        <button onClick={fetchRequests} className="ml-auto p-2 bg-slate-800 rounded-xl text-slate-400 hover:bg-slate-700 transition">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><RefreshCw className="w-6 h-6 text-slate-500 animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-slate-500">
          <HeadphonesIcon className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>Nessuna richiesta {statusFilter !== "all" ? statusFilter : ""}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map((r) => (
            <div key={r.id} className={`bg-slate-900 rounded-2xl border p-5 ${r.status === "open" ? "border-amber-500/30" : "border-slate-800"}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${r.status === "open" ? "bg-amber-500/15 text-amber-400" : "bg-emerald-500/15 text-emerald-400"}`}>
                      {r.status === "open" ? "APERTA" : "RISOLTA"}
                    </span>
                    <span className="text-xs text-slate-500">{fmtDate(r.created_at)}</span>
                  </div>
                  <p className="text-sm font-semibold text-white mb-0.5">{r.company_name}</p>
                  <p className="text-xs text-slate-400 mb-3">{r.user_email} {r.phone && `· ${r.phone}`}</p>
                  {r.boat_count && <p className="text-xs text-slate-500 mb-1">🚤 Barche: {r.boat_count}</p>}
                  <p className="text-sm text-slate-300 leading-relaxed bg-slate-800/50 rounded-xl p-3">{r.message}</p>
                </div>
                <div className="flex flex-col gap-2 flex-shrink-0">
                  <a href={`mailto:${r.user_email}?subject=Re: Richiesta supporto Nautiqo`}
                    className="flex items-center gap-1.5 px-3 py-2 bg-blue-500/15 text-blue-400 hover:bg-blue-500/25 rounded-xl text-xs font-medium transition">
                    <Mail className="w-3.5 h-3.5" /> Rispondi
                  </a>
                  {r.status === "open" ? (
                    <button onClick={() => resolve(r.id, "resolved")}
                      className="flex items-center gap-1.5 px-3 py-2 bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25 rounded-xl text-xs font-medium transition">
                      <CheckCircle className="w-3.5 h-3.5" /> Risolvi
                    </button>
                  ) : (
                    <button onClick={() => resolve(r.id, "open")}
                      className="flex items-center gap-1.5 px-3 py-2 bg-slate-700/50 text-slate-400 hover:bg-slate-700 rounded-xl text-xs font-medium transition">
                      <RefreshCw className="w-3.5 h-3.5" /> Riapri
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


// ==================== RIMBORSI PAGE ====================
export const SARimborsiPage = ({ getAuthHeaders }) => {
  const [refunds, setRefunds] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/refunds`, { headers: getAuthHeaders() })
      .then(res => setRefunds(Array.isArray(res.data) ? res.data : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [getAuthHeaders]);

  const total = refunds.reduce((s, r) => s + (r.refunded_amount || 0), 0);

  return (
    <div data-testid="sa-rimborsi">
      <h1 className="text-2xl font-bold text-white mb-1">Rimborsi</h1>
      <p className="text-slate-500 mb-6">Tutti i rimborsi Stripe emessi tramite l'app</p>

      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        <div className="bg-slate-900 rounded-2xl border border-slate-800 p-5">
          <p className="text-xs text-slate-500 mb-2">Rimborsi totali</p>
          <p className="text-3xl font-bold text-white">{refunds.length}</p>
        </div>
        <div className="bg-slate-900 rounded-2xl border border-slate-800 p-5">
          <p className="text-xs text-slate-500 mb-2">Importo totale rimborsato</p>
          <p className="text-3xl font-bold text-red-400">{fmtEur(total)}</p>
        </div>
        <div className="bg-slate-900 rounded-2xl border border-slate-800 p-5">
          <p className="text-xs text-slate-500 mb-2">Media per rimborso</p>
          <p className="text-3xl font-bold text-white">{refunds.length > 0 ? fmtEur(total / refunds.length) : "—"}</p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><RefreshCw className="w-6 h-6 text-slate-500 animate-spin" /></div>
      ) : refunds.length === 0 ? (
        <div className="text-center py-16 text-slate-500">
          <RotateCcw className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>Nessun rimborso emesso</p>
        </div>
      ) : (
        <div className="bg-slate-900 rounded-2xl border border-slate-800 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-800">
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Azienda</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Cliente</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Data prenotazione</th>
                  <th className="text-right px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Pagato</th>
                  <th className="text-right px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Rimborsato</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Data rimborso</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {refunds.map((r, i) => (
                  <tr key={i} className="hover:bg-slate-800/30 transition">
                    <td className="px-5 py-3">
                      <p className="font-medium text-white">{r.company_name || "—"}</p>
                      <p className="text-xs text-slate-500">{r.company_email}</p>
                    </td>
                    <td className="px-5 py-3 text-slate-300">{r.customer_name || "—"}</td>
                    <td className="px-5 py-3 text-slate-400">{fmtDate(r.booking_date)}</td>
                    <td className="px-5 py-3 text-right text-slate-300 tabular-nums">{fmtEur(r.amount_paid_online || 0)}</td>
                    <td className="px-5 py-3 text-right font-semibold text-red-400 tabular-nums">{fmtEur(r.refunded_amount || 0)}</td>
                    <td className="px-5 py-3 text-slate-400">{fmtDate(r.refunded_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};


// ==================== COMPANY DETAIL PAGE ====================
export const SACompanyDetailPage = ({ getAuthHeaders, id }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [extendingTrial, setExtendingTrial] = useState(false);

  useEffect(() => {
    if (!id) return;
    axios.get(`${API}/admin/companies/${id}/full`, { headers: getAuthHeaders() })
      .then(res => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id, getAuthHeaders]);

  const handleExtend = async (days) => {
    setExtendingTrial(true);
    try {
      const res = await axios.post(`${API}/admin/companies/${id}/extend-trial`, { days }, { headers: getAuthHeaders() });
      setData(prev => ({ ...prev, user: { ...prev.user, subscription_end_date: res.data.new_end_date } }));
      alert(`✅ Scadenza aggiornata al ${res.data.new_end_date}`);
    } catch (e) { alert("Errore: " + e.message); }
    finally { setExtendingTrial(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><RefreshCw className="w-6 h-6 text-slate-500 animate-spin" /></div>;
  if (!data) return <div className="text-center py-16 text-slate-500">Azienda non trovata</div>;

  const { user, boats, recent_bookings, stats } = data;
  const subColor = user.subscription_status === "active" ? "emerald" : user.subscription_status === "trial" ? "amber" : "red";

  return (
    <div data-testid="sa-company-detail">
      {/* Header */}
      <button onClick={() => navigate("/super-admin/aziende")} className="flex items-center gap-2 text-slate-400 hover:text-white mb-6 transition text-sm">
        <ArrowLeft className="w-4 h-4" /> Torna ad Aziende
      </button>

      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white mb-1">{user.company_name}</h1>
          <p className="text-slate-400 text-sm">{user.email} {user.phone && `· ${user.phone}`}</p>
          <p className="text-xs text-slate-600 mt-1">Registrato il {fmtDate(user.created_at)} · Ultimo accesso: {fmtDate(user.last_login)}</p>
        </div>
        <span className={`px-3 py-1.5 rounded-full text-xs font-bold bg-${subColor}-500/15 text-${subColor}-400 border border-${subColor}-500/20 uppercase`}>
          {user.subscription_status}
        </span>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[
          { label: "Barche", value: stats.boats_count, icon: Anchor },
          { label: "Prenotazioni", value: stats.total_bookings, icon: CalendarCheck },
          { label: "Clienti", value: stats.total_customers, icon: UsersIcon },
          { label: "Fatturato stimato", value: fmtEur(stats.total_revenue), icon: DollarSign },
        ].map((k, i) => (
          <div key={i} className="bg-slate-900 rounded-2xl border border-slate-800 p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-slate-500">{k.label}</span>
              <k.icon className="w-4 h-4 text-slate-600" />
            </div>
            <p className="text-2xl font-bold text-white">{k.value}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Subscription details */}
        <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6">
          <h2 className="text-base font-semibold text-white mb-4">Abbonamento</h2>
          <div className="space-y-3 text-sm">
            {[
              ["Piano", user.plan_name || "—"],
              ["Stato", user.subscription_status],
              ["Scadenza", fmtDate(user.subscription_end_date)],
              ["Canale", user.billing_provider || "—"],
              ["Ciclo", user.billing_cycle || "—"],
              ["Stripe Account", user.stripe_account_id || "—"],
            ].map(([l, v]) => (
              <div key={l} className="flex justify-between items-center border-b border-slate-800 pb-2 last:border-0">
                <span className="text-slate-500">{l}</span>
                <span className="text-slate-200 font-medium">{v}</span>
              </div>
            ))}
          </div>
          <div className="flex gap-2 mt-4">
            {[7, 14, 30].map(d => (
              <button key={d} onClick={() => handleExtend(d)} disabled={extendingTrial}
                className="flex-1 py-2 bg-amber-500/15 text-amber-400 hover:bg-amber-500/25 rounded-xl text-xs font-semibold transition disabled:opacity-50">
                {extendingTrial ? '...' : `+${d}gg`}
              </button>
            ))}
          </div>
        </div>

        {/* Boats */}
        <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6">
          <h2 className="text-base font-semibold text-white mb-4">Barche ({boats.length})</h2>
          {boats.length === 0 ? (
            <p className="text-slate-500 text-sm text-center py-6">Nessuna barca registrata</p>
          ) : (
            <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
              {boats.map((b, i) => (
                <div key={i} className="flex items-center gap-3">
                  {b.image_url ? (
                    <img src={b.image_url} alt={b.name} className="w-10 h-10 rounded-xl object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-10 h-10 bg-slate-800 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Anchor className="w-4 h-4 text-slate-600" />
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-medium text-white">{b.name}</p>
                    <p className="text-xs text-slate-500">{b.type || "—"}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent bookings */}
        <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6 lg:col-span-2">
          <h2 className="text-base font-semibold text-white mb-4">Ultime prenotazioni</h2>
          {recent_bookings.length === 0 ? (
            <p className="text-slate-500 text-sm text-center py-6">Nessuna prenotazione</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-800">
                    <th className="text-left pb-3 text-xs font-semibold text-slate-500 uppercase">Cliente</th>
                    <th className="text-left pb-3 text-xs font-semibold text-slate-500 uppercase">Data</th>
                    <th className="text-left pb-3 text-xs font-semibold text-slate-500 uppercase">Stato</th>
                    <th className="text-right pb-3 text-xs font-semibold text-slate-500 uppercase">Totale</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {recent_bookings.map((b, i) => (
                    <tr key={i}>
                      <td className="py-2.5 text-slate-300">{b.customer_name || "—"}</td>
                      <td className="py-2.5 text-slate-400">{fmtDate(b.booking_date)}</td>
                      <td className="py-2.5"><StatusBadge status={b.status} /></td>
                      <td className="py-2.5 text-right font-medium text-white">{fmtEur(b.total_price || 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== SA UTENTI PAGE ====================
export const SAUtentiPage = ({ getAuthHeaders }) => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [providerFilter, setProviderFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/companies`, { headers: getAuthHeaders() });
        setCompanies(r.data || []);
      } catch {
        setCompanies([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [getAuthHeaders]);

  const srcLabel = (src) => {
    if (src === 'ios') return { label: '📱 iOS', color: 'text-blue-400' };
    if (src === 'android') return { label: '🤖 Android', color: 'text-green-400' };
    return { label: '🌐 Web', color: 'text-slate-400' };
  };

  const subLabel = (status) => {
    if (!status || status === 'trial') return { label: 'Trial', color: 'bg-amber-900/40 text-amber-300' };
    if (status === 'active') return { label: 'Attivo', color: 'bg-emerald-900/40 text-emerald-300' };
    if (status === 'past_due') return { label: 'Sospeso', color: 'bg-red-900/40 text-red-300' };
    if (status === 'cancelled') return { label: 'Cancellato', color: 'bg-slate-700 text-slate-400' };
    if (status === 'expired' || status === 'trial_expired') return { label: 'Scaduto', color: 'bg-red-900/40 text-red-400' };
    return { label: status, color: 'bg-slate-700 text-slate-400' };
  };

  const planLabel = (p) => {
    if (!p || p === 'trial') return 'Trial';
    if (p === 'nautiqo' || p === 'pro') return 'Nautiqo';
    if (p === 'start') return 'Start';
    return p || '—';
  };

  const providerLabel = (p) => {
    if (p === 'stripe') return { label: 'Stripe', color: 'text-violet-400' };
    if (p === 'apple') return { label: 'Apple', color: 'text-slate-300' };
    if (p === 'google') return { label: 'Google', color: 'text-green-400' };
    return { label: 'Manual', color: 'text-slate-500' };
  };

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
    setPage(1);
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <span className="text-slate-600 ml-1">↕</span>;
    return <span className="text-blue-400 ml-1">{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  const filtered = companies
    .filter(c => {
      if (search) {
        const q = search.toLowerCase();
        return (c.email || '').toLowerCase().includes(q) || (c.company_name || '').toLowerCase().includes(q);
      }
      return true;
    })
    .filter(c => statusFilter === 'all' || (c.subscription_status || 'trial') === statusFilter)
    .filter(c => providerFilter === 'all' || (c.billing_provider || 'manual') === providerFilter)
    .filter(c => {
      if (planFilter === 'all') return true;
      if (planFilter === 'trial') return !c.plan_name || c.plan_name === 'trial';
      return (c.plan_name || '').toLowerCase().includes(planFilter);
    })
    .sort((a, b) => {
      let va = a[sortField] || '';
      let vb = b[sortField] || '';
      if (sortField === 'created_at' || sortField === 'last_login') {
        va = va ? new Date(va).getTime() : 0;
        vb = vb ? new Date(vb).getTime() : 0;
      } else {
        va = (va + '').toLowerCase();
        vb = (vb + '').toLowerCase();
      }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const exportUsers = () => {
    const rows = filtered.map(c => ({
      id: c.id,
      azienda: c.company_name || '',
      email: c.email || '',
      telefono: c.phone || '',
      piano: planLabel(c.plan_name),
      stato: c.subscription_status || 'trial',
      provider: c.billing_provider || 'manual',
      registrazione: c.created_at ? c.created_at.slice(0, 10) : '',
      last_login: c.last_login ? c.last_login.slice(0, 10) : '',
      prenotazioni: c.booking_count || 0,
    }));
    const csv = [Object.keys(rows[0]).join(','), ...rows.map(r => Object.values(r).map(v => `"${v}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `nautiqo-utenti-${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  if (loading) return (
    <div className="p-6 flex items-center justify-center h-64">
      <RefreshCw className="animate-spin text-blue-400" size={28} />
    </div>
  );

  return (
    <div className="p-4 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-white flex items-center gap-2">
            <UsersIcon className="text-blue-400" size={22} />
            Utenti
          </h1>
          <p className="text-slate-400 text-sm mt-1">{filtered.length} utenti trovati su {companies.length} totali</p>
        </div>
        <button
          onClick={exportUsers}
          className="flex items-center gap-2 px-3 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg text-sm transition-colors"
        >
          <Download size={15} />
          Esporta CSV
        </button>
      </div>

      {/* Filtri */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-5">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            type="text"
            placeholder="Cerca azienda o email..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="w-full pl-9 pr-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
        <select
          value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
          className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
        >
          <option value="all">Tutti gli stati</option>
          <option value="trial">Trial</option>
          <option value="active">Attivi</option>
          <option value="past_due">Sospesi</option>
          <option value="cancelled">Cancellati</option>
          <option value="expired">Scaduti</option>
        </select>
        <select
          value={providerFilter}
          onChange={e => { setProviderFilter(e.target.value); setPage(1); }}
          className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
        >
          <option value="all">Tutti i provider</option>
          <option value="stripe">Stripe</option>
          <option value="apple">Apple</option>
          <option value="google">Google</option>
          <option value="manual">Manual</option>
        </select>
        <select
          value={planFilter}
          onChange={e => { setPlanFilter(e.target.value); setPage(1); }}
          className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
        >
          <option value="all">Tutti i piani</option>
          <option value="trial">Trial</option>
          <option value="nautiqo">Nautiqo</option>
          <option value="start">Start</option>
        </select>
      </div>

      {/* Tabella */}
      <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-800 bg-slate-950">
                <th className="text-left px-4 py-3 text-slate-400 font-medium cursor-pointer hover:text-white" onClick={() => handleSort('company_name')}>
                  Azienda <SortIcon field="company_name" />
                </th>
                <th className="text-left px-4 py-3 text-slate-400 font-medium">Email</th>
                <th className="text-left px-4 py-3 text-slate-400 font-medium cursor-pointer hover:text-white" onClick={() => handleSort('plan_name')}>
                  Piano <SortIcon field="plan_name" />
                </th>
                <th className="text-left px-4 py-3 text-slate-400 font-medium cursor-pointer hover:text-white" onClick={() => handleSort('subscription_status')}>
                  Stato <SortIcon field="subscription_status" />
                </th>
                <th className="text-left px-4 py-3 text-slate-400 font-medium">Provider</th>
                <th className="text-left px-4 py-3 text-slate-400 font-medium cursor-pointer hover:text-white" onClick={() => handleSort('created_at')}>
                  Iscritto il <SortIcon field="created_at" />
                </th>
                <th className="text-left px-4 py-3 text-slate-400 font-medium cursor-pointer hover:text-white" onClick={() => handleSort('last_login')}>
                  Last login <SortIcon field="last_login" />
                </th>
                <th className="text-right px-4 py-3 text-slate-400 font-medium">Book.</th>
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center py-12 text-slate-500">
                    Nessun utente trovato con i filtri selezionati
                  </td>
                </tr>
              )}
              {paginated.map((c, i) => {
                const sl = subLabel(c.subscription_status);
                const pl = providerLabel(c.billing_provider);
                const src = srcLabel(c.source);
                return (
                  <tr key={c.id} className={`border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors ${i % 2 === 0 ? '' : 'bg-slate-900/30'}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-white truncate max-w-[180px]">{c.company_name || '—'}</div>
                      <div className={`text-xs ${src.color}`}>{src.label}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-300 text-xs">{c.email || '—'}</td>
                    <td className="px-4 py-3 text-slate-300">{planLabel(c.plan_name)}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${sl.color}`}>{sl.label}</span>
                    </td>
                    <td className={`px-4 py-3 text-xs font-medium ${pl.color}`}>{pl.label}</td>
                    <td className="px-4 py-3 text-slate-400 text-xs">{fmtDate(c.created_at)}</td>
                    <td className="px-4 py-3 text-slate-400 text-xs">{c.last_login ? fmtDate(c.last_login) : '—'}</td>
                    <td className="px-4 py-3 text-right text-slate-300">{c.booking_count || 0}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Paginazione */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-800">
            <span className="text-slate-500 text-xs">
              Pagina {page} di {totalPages} · {filtered.length} utenti
            </span>
            <div className="flex gap-1">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 rounded bg-slate-800 text-slate-300 text-xs disabled:opacity-40 hover:bg-slate-700 transition-colors"
              >← Prec.</button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const p = Math.max(1, Math.min(page - 2, totalPages - 4)) + i;
                return p <= totalPages ? (
                  <button key={p} onClick={() => setPage(p)}
                    className={`px-3 py-1 rounded text-xs transition-colors ${page === p ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'}`}
                  >{p}</button>
                ) : null;
              })}
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1 rounded bg-slate-800 text-slate-300 text-xs disabled:opacity-40 hover:bg-slate-700 transition-colors"
              >Succ. →</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


// ==================== COUPON PAGE ====================

const COUPON_TYPE_LABELS = {
  trial_extension: { label: "Trial extension", color: "bg-blue-500/20 text-blue-300" },
  percent_off: { label: "Sconto %", color: "bg-emerald-500/20 text-emerald-300" },
  amount_off: { label: "Sconto €", color: "bg-violet-500/20 text-violet-300" },
};

const formatCouponValue = (type, value) => {
  if (type === "trial_extension") return `+${value} giorni`;
  if (type === "percent_off") return `${value}%`;
  if (type === "amount_off") return `${(value / 100).toFixed(2).replace(".", ",")}€`;
  return value;
};

export const SACouponsPage = ({ getAuthHeaders }) => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [detailCoupon, setDetailCoupon] = useState(null);
  const [detailUses, setDetailUses] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [copying, setCopying] = useState(null);

  const fetchCoupons = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/coupons`, { headers: getAuthHeaders() });
      setCoupons(Array.isArray(data) ? data : []);
    } catch (e) {
      setError("Errore nel caricamento dei coupon");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCoupons(); }, []);

  const openDetail = async (coupon) => {
    setDetailCoupon(coupon);
    setDetailUses(null);
    setDetailLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/coupons/${coupon.id}/uses`, { headers: getAuthHeaders() });
      setDetailUses(data);
    } catch (e) {
      setDetailUses({ uses: [], total_uses: 0 });
    } finally {
      setDetailLoading(false);
    }
  };

  const toggleActive = async (coupon) => {
    try {
      await axios.put(`${API}/admin/coupons/${coupon.id}`, { is_active: !coupon.is_active }, { headers: getAuthHeaders() });
      fetchCoupons();
      if (detailCoupon?.id === coupon.id) {
        setDetailCoupon(prev => ({ ...prev, is_active: !coupon.is_active }));
      }
    } catch (e) {
      alert("Errore nell'aggiornamento del coupon");
    }
  };

  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopying(code);
      setTimeout(() => setCopying(null), 1500);
    } catch {}
  };

  const activeCoupons = coupons.filter(c => c.is_active);
  const totalUses = coupons.reduce((s, c) => s + (c.uses_count || 0), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Coupon & Promo</h1>
          <p className="text-slate-400 text-sm mt-0.5">Gestisci codici promozionali per nuovi utenti</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition text-sm"
        >
          <PlusCircle className="w-4 h-4" />
          Crea coupon
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-slate-900 rounded-2xl p-5 border border-slate-800">
          <p className="text-sm text-slate-500 mb-1">Coupon totali</p>
          <p className="text-3xl font-bold text-white">{coupons.length}</p>
        </div>
        <div className="bg-slate-900 rounded-2xl p-5 border border-slate-800">
          <p className="text-sm text-slate-500 mb-1">Attivi</p>
          <p className="text-3xl font-bold text-emerald-400">{activeCoupons.length}</p>
        </div>
        <div className="bg-slate-900 rounded-2xl p-5 border border-slate-800">
          <p className="text-sm text-slate-500 mb-1">Utilizzi totali</p>
          <p className="text-3xl font-bold text-white">{totalUses}</p>
        </div>
        <div className="bg-slate-900 rounded-2xl p-5 border border-slate-800">
          <p className="text-sm text-slate-500 mb-1">Disattivati</p>
          <p className="text-3xl font-bold text-slate-400">{coupons.length - activeCoupons.length}</p>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center py-16 text-slate-500">Caricamento...</div>
      ) : error ? (
        <div className="text-center py-16 text-red-400">{error}</div>
      ) : coupons.length === 0 ? (
        <div className="bg-slate-900 rounded-2xl border border-slate-800 p-12 text-center">
          <Tag className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <p className="text-slate-400 font-medium">Nessun coupon ancora</p>
          <p className="text-slate-600 text-sm mt-1">Crea il primo codice promozionale</p>
        </div>
      ) : (
        <div className="bg-slate-900 rounded-2xl border border-slate-800 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-800">
                  <th className="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Codice</th>
                  <th className="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Tipo</th>
                  <th className="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Valore</th>
                  <th className="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Utilizzi</th>
                  <th className="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Scadenza</th>
                  <th className="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Stato</th>
                  <th className="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Creato</th>
                  <th className="px-5 py-3.5"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/60">
                {coupons.map(c => {
                  const tl = COUPON_TYPE_LABELS[c.type] || { label: c.type, color: "bg-slate-500/20 text-slate-300" };
                  const usageStr = c.max_uses ? `${c.uses_count}/${c.max_uses}` : `${c.uses_count}`;
                  const usagePct = c.max_uses ? (c.uses_count / c.max_uses) * 100 : 0;
                  const isExpired = c.expires_at && c.expires_at < new Date().toISOString().slice(0,10);
                  return (
                    <tr key={c.id} className="hover:bg-slate-800/40 transition-colors">
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-bold text-white tracking-wider">{c.code}</span>
                          <button
                            onClick={() => copyCode(c.code)}
                            className="text-slate-500 hover:text-slate-300 transition"
                            title="Copia codice"
                          >
                            {copying === c.code ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                        {c.description && <p className="text-xs text-slate-500 mt-0.5 truncate max-w-[180px]">{c.description}</p>}
                      </td>
                      <td className="px-5 py-3.5">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${tl.color}`}>{tl.label}</span>
                      </td>
                      <td className="px-5 py-3.5 font-semibold text-white tabular-nums">
                        {formatCouponValue(c.type, c.value)}
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex flex-col gap-1">
                          <span className="text-slate-300 tabular-nums text-xs">{usageStr}</span>
                          {c.max_uses && (
                            <div className="w-16 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                              <div className={`h-full rounded-full ${usagePct >= 100 ? 'bg-red-500' : usagePct >= 80 ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${Math.min(100, usagePct)}%` }} />
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-3.5 text-xs text-slate-400">
                        {c.expires_at ? (
                          <span className={isExpired ? 'text-red-400' : 'text-slate-400'}>{c.expires_at}</span>
                        ) : (
                          <span className="text-slate-600">∞</span>
                        )}
                      </td>
                      <td className="px-5 py-3.5">
                        {c.is_active && !isExpired ? (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-500/20 text-emerald-300">Attivo</span>
                        ) : (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-700/60 text-slate-500">{isExpired ? 'Scaduto' : 'Disattivo'}</span>
                        )}
                      </td>
                      <td className="px-5 py-3.5 text-xs text-slate-500">{fmtDate(c.created_at)}</td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-1 justify-end">
                          <button
                            onClick={() => toggleActive(c)}
                            className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-white transition"
                            title={c.is_active ? 'Disattiva' : 'Attiva'}
                          >
                            {c.is_active ? <ToggleRight className="w-4 h-4 text-emerald-400" /> : <ToggleLeft className="w-4 h-4" />}
                          </button>
                          <button
                            onClick={() => openDetail(c)}
                            className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-slate-200 transition"
                            title="Vedi dettagli"
                          >
                            <ChevronRight className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreate && (
        <CouponCreateModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); fetchCoupons(); }}
          getAuthHeaders={getAuthHeaders}
        />
      )}

      {/* Detail Drawer */}
      {detailCoupon && (
        <CouponDetailDrawer
          coupon={detailCoupon}
          uses={detailUses}
          loading={detailLoading}
          onClose={() => setDetailCoupon(null)}
          onToggle={() => toggleActive(detailCoupon)}
          getAuthHeaders={getAuthHeaders}
          onUpdated={fetchCoupons}
        />
      )}
    </div>
  );
};

// ---- Create Coupon Modal ----
const CouponCreateModal = ({ onClose, onCreated, getAuthHeaders }) => {
  const [form, setForm] = useState({
    code: '',
    description: '',
    type: 'trial_extension',
    value: '',
    max_uses: '',
    expires_at: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const generateCode = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    const code = Array.from({ length: 8 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
    update('code', code);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.code.trim()) { setError('Inserisci un codice'); return; }
    if (!form.value || isNaN(Number(form.value)) || Number(form.value) <= 0) {
      setError('Inserisci un valore valido'); return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/admin/coupons`, {
        code: form.code.trim().toUpperCase(),
        description: form.description.trim(),
        type: form.type,
        value: Math.round(Number(form.value)),
        max_uses: form.max_uses ? Number(form.max_uses) : null,
        expires_at: form.expires_at || null,
      }, { headers: getAuthHeaders() });
      onCreated();
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore nella creazione del coupon');
    } finally {
      setLoading(false);
    }
  };

  const valueLabel = form.type === 'trial_extension' ? 'Giorni extra di trial' :
    form.type === 'percent_off' ? 'Percentuale di sconto (1-100)' : 'Importo di sconto (centesimi, es: 1000 = 10€)';
  const valuePlaceholder = form.type === 'trial_extension' ? 'es: 30' :
    form.type === 'percent_off' ? 'es: 20' : 'es: 1000';

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-900 rounded-2xl border border-slate-700 w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-blue-500/15 flex items-center justify-center">
              <Tag className="w-4 h-4 text-blue-400" />
            </div>
            <h2 className="text-lg font-semibold text-white">Crea coupon</h2>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-300 transition">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-500/10 border border-red-500/20 text-red-400 text-sm rounded-xl px-4 py-3">
              {error}
            </div>
          )}

          <div>
            <label className="text-sm font-medium text-slate-400 block mb-1.5">Codice coupon *</label>
            <div className="flex gap-2">
              <input
                value={form.code}
                onChange={e => update('code', e.target.value.toUpperCase())}
                placeholder="es: ESTATE30"
                className="flex-1 px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-600 focus:border-blue-500 outline-none font-mono uppercase tracking-wider"
                required
              />
              <button type="button" onClick={generateCode} className="px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-xl text-slate-400 hover:text-white hover:border-slate-500 transition text-xs">
                Genera
              </button>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-slate-400 block mb-1.5">Tipo *</label>
            <select
              value={form.type}
              onChange={e => update('type', e.target.value)}
              className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-xl text-white focus:border-blue-500 outline-none"
            >
              <option value="trial_extension">Trial extension (giorni extra)</option>
              <option value="percent_off">Sconto percentuale sul piano</option>
              <option value="amount_off">Sconto fisso in € sul piano</option>
            </select>
          </div>

          <div>
            <label className="text-sm font-medium text-slate-400 block mb-1.5">{valueLabel} *</label>
            <input
              type="number"
              min="1"
              max={form.type === 'percent_off' ? 100 : undefined}
              value={form.value}
              onChange={e => update('value', e.target.value)}
              placeholder={valuePlaceholder}
              className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-600 focus:border-blue-500 outline-none"
              required
            />
            {form.type === 'trial_extension' && form.value && (
              <p className="text-xs text-blue-400 mt-1">Trial di {30 + Number(form.value)} giorni totali (30 + {form.value})</p>
            )}
            {form.type === 'percent_off' && form.value && (
              <p className="text-xs text-emerald-400 mt-1">{form.value}% di sconto sul primo abbonamento</p>
            )}
            {form.type === 'amount_off' && form.value && (
              <p className="text-xs text-emerald-400 mt-1">{(Number(form.value) / 100).toFixed(2)}€ di sconto</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-400 block mb-1.5">Utilizzi massimi</label>
              <input
                type="number"
                min="1"
                value={form.max_uses}
                onChange={e => update('max_uses', e.target.value)}
                placeholder="Illimitati"
                className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-600 focus:border-blue-500 outline-none"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-400 block mb-1.5">Scadenza</label>
              <input
                type="date"
                value={form.expires_at}
                onChange={e => update('expires_at', e.target.value)}
                className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-xl text-white focus:border-blue-500 outline-none"
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-slate-400 block mb-1.5">Descrizione interna</label>
            <input
              value={form.description}
              onChange={e => update('description', e.target.value)}
              placeholder="Nota interna sul coupon"
              className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-600 focus:border-blue-500 outline-none"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 bg-slate-800 text-slate-300 rounded-xl font-medium hover:bg-slate-700 transition">
              Annulla
            </button>
            <button type="submit" disabled={loading} className="flex-1 py-2.5 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition disabled:opacity-50">
              {loading ? 'Creazione...' : 'Crea coupon'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ---- Coupon Detail Drawer ----
const CouponDetailDrawer = ({ coupon, uses, loading, onClose, onToggle, getAuthHeaders, onUpdated }) => {
  const tl = COUPON_TYPE_LABELS[coupon.type] || { label: coupon.type, color: "bg-slate-500/20 text-slate-300" };
  const usesData = uses?.uses || [];
  const isExpired = coupon.expires_at && coupon.expires_at < new Date().toISOString().slice(0,10);
  const exhausted = coupon.max_uses && coupon.uses_count >= coupon.max_uses;

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-end lg:items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-900 rounded-2xl border border-slate-700 w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-slate-800 sticky top-0 bg-slate-900 z-10">
          <div>
            <div className="flex items-center gap-3">
              <span className="font-mono text-xl font-bold text-white tracking-widest">{coupon.code}</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${tl.color}`}>{tl.label}</span>
            </div>
            {coupon.description && <p className="text-sm text-slate-500 mt-0.5">{coupon.description}</p>}
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-300 transition ml-4">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Stats grid */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-slate-800/60 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-white">{formatCouponValue(coupon.type, coupon.value)}</p>
              <p className="text-xs text-slate-500 mt-0.5">Valore</p>
            </div>
            <div className="bg-slate-800/60 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-white">{coupon.uses_count}</p>
              <p className="text-xs text-slate-500 mt-0.5">Utilizzi</p>
            </div>
            <div className="bg-slate-800/60 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-white">{coupon.max_uses ?? '∞'}</p>
              <p className="text-xs text-slate-500 mt-0.5">Limite</p>
            </div>
          </div>

          {/* Status & details */}
          <div className="space-y-3">
            <div className="flex items-center justify-between py-2.5 border-b border-slate-800">
              <span className="text-sm text-slate-500">Stato</span>
              <div className="flex items-center gap-3">
                {coupon.is_active && !isExpired && !exhausted ? (
                  <span className="px-2 py-0.5 rounded-full text-xs bg-emerald-500/20 text-emerald-300">Attivo</span>
                ) : (
                  <span className="px-2 py-0.5 rounded-full text-xs bg-slate-700/60 text-slate-500">
                    {isExpired ? 'Scaduto' : exhausted ? 'Esaurito' : 'Disattivo'}
                  </span>
                )}
                {!isExpired && !exhausted && (
                  <button onClick={onToggle} className="text-xs text-blue-400 hover:text-blue-300 transition">
                    {coupon.is_active ? 'Disattiva' : 'Attiva'}
                  </button>
                )}
              </div>
            </div>
            <div className="flex items-center justify-between py-2.5 border-b border-slate-800">
              <span className="text-sm text-slate-500">Scadenza</span>
              <span className={`text-sm ${isExpired ? 'text-red-400' : 'text-slate-300'}`}>
                {coupon.expires_at || '—'}
              </span>
            </div>
            {coupon.stripe_coupon_id && (
              <div className="flex items-center justify-between py-2.5 border-b border-slate-800">
                <span className="text-sm text-slate-500">Stripe Coupon ID</span>
                <span className="text-sm text-slate-400 font-mono">{coupon.stripe_coupon_id}</span>
              </div>
            )}
            <div className="flex items-center justify-between py-2.5 border-b border-slate-800">
              <span className="text-sm text-slate-500">Creato</span>
              <span className="text-sm text-slate-300">{fmtDate(coupon.created_at)}</span>
            </div>
          </div>

          {/* Usage list */}
          <div>
            <h3 className="text-sm font-semibold text-white mb-3">
              Utenti che hanno usato questo coupon ({usesData.length})
            </h3>
            {loading ? (
              <p className="text-slate-500 text-sm text-center py-4">Caricamento...</p>
            ) : usesData.length === 0 ? (
              <p className="text-slate-600 text-sm text-center py-4">Nessun utilizzo ancora</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                {usesData.map(u => (
                  <div key={u.id} className="flex items-center gap-3 p-3 bg-slate-800/40 rounded-xl">
                    <div className="w-8 h-8 rounded-full bg-blue-500/15 flex items-center justify-center flex-shrink-0">
                      <UsersIcon className="w-4 h-4 text-blue-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white truncate">{u.company_name}</p>
                      <p className="text-xs text-slate-500 truncate">{u.email}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-xs text-emerald-400 font-medium">{u.effect_applied}</p>
                      <p className="text-xs text-slate-600">{fmtDate(u.used_at)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};


// ==================== LEAD PAGE ====================

const LEAD_STATUSES = [
  { value: 'nuovo', label: 'Nuovo', color: 'bg-blue-500/20 text-blue-300 border-blue-500/30' },
  { value: 'contattato', label: 'Contattato', color: 'bg-amber-500/20 text-amber-300 border-amber-500/30' },
  { value: 'demo fissata', label: 'Demo fissata', color: 'bg-purple-500/20 text-purple-300 border-purple-500/30' },
  { value: 'trial attivato', label: 'Trial attivato', color: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30' },
  { value: 'cliente', label: 'Cliente', color: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' },
  { value: 'perso', label: 'Perso', color: 'bg-slate-500/20 text-slate-400 border-slate-600' },
];

const TIPI_ATTIVITA = ['Noleggio gommoni', 'Charter barche', 'Escursioni / tour in barca', 'Noleggio + tour', 'Altro'];

const LeadStatusBadge = ({ status }) => {
  const s = LEAD_STATUSES.find(x => x.value === status) || LEAD_STATUSES[0];
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${s.color}`}>
      {s.label}
    </span>
  );
};

export const SALeadsPage = ({ getAuthHeaders }) => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterTipo, setFilterTipo] = useState('all');
  const [selectedLead, setSelectedLead] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(null);

  const loadLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus !== 'all') params.set('status', filterStatus);
      if (filterTipo !== 'all') params.set('tipo_attivita', filterTipo);
      if (search.trim()) params.set('search', search.trim());
      const { data } = await axios.get(`${API}/admin/leads?${params}`, { headers: getAuthHeaders() });
      setLeads(Array.isArray(data) ? data : []);
    } catch { setLeads([]); } finally { setLoading(false); }
  }, [getAuthHeaders, filterStatus, filterTipo, search]);

  useEffect(() => { loadLeads(); }, [loadLeads]);

  const updateStatus = async (leadId, newStatus) => {
    setUpdatingStatus(leadId);
    try {
      await axios.patch(`${API}/admin/leads/${leadId}/status`, { status: newStatus }, { headers: getAuthHeaders() });
      setLeads(prev => prev.map(l => l.id === leadId ? { ...l, status: newStatus } : l));
      if (selectedLead?.id === leadId) setSelectedLead(prev => ({ ...prev, status: newStatus }));
    } catch { alert('Errore aggiornamento status'); } finally { setUpdatingStatus(null); }
  };

  const openWhatsApp = (lead) => {
    const phone = lead.telefono.replace(/\D/g, '');
    const num = phone.startsWith('39') ? phone : `39${phone}`;
    window.open(`https://wa.me/${num}`, '_blank');
  };

  const copyLead = (lead) => {
    const gestionale = lead.usa_gestionale === 'no' ? 'No'
      : lead.usa_gestionale === 'si' ? `Sì (${lead.gestionale_attuale || '—'})`
      : `Sì, non soddisfatto (${lead.gestionale_attuale || '—'})`;
    const text = `Azienda: ${lead.nome_attivita}\nReferente: ${lead.nome_referente}\nTelefono: ${lead.telefono}\nEmail: ${lead.email}\nTipo: ${lead.tipo_attivita}\nBarche: ${lead.numero_barche}\nGestionale: ${gestionale}\nNote: ${lead.messaggio || '—'}`;
    navigator.clipboard.writeText(text).catch(() => {});
  };

  const nuoviCount = leads.filter(l => l.status === 'nuovo').length;

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <Target className="w-6 h-6 text-blue-400" /> Lead
          {nuoviCount > 0 && (
            <span className="text-sm font-semibold px-2 py-0.5 bg-blue-500/15 text-blue-400 rounded-full">{nuoviCount} nuovi</span>
          )}
        </h1>
        <button onClick={loadLeads} className="p-2 hover:bg-slate-800 rounded-lg text-slate-400 transition">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>
      <p className="text-slate-500 mb-6">Lead dalle campagne Meta Ads — <a href="/prova-gratis" target="_blank" className="text-blue-400 hover:underline">nautiqo.cloud/prova-gratis</a></p>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Cerca azienda, referente, email, telefono..."
            className="w-full pl-9 pr-3 py-2 bg-slate-900 border border-slate-800 rounded-xl text-white placeholder-slate-500 text-sm outline-none focus:border-blue-500 transition" />
        </div>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          className="px-3 py-2 bg-slate-900 border border-slate-800 rounded-xl text-slate-300 text-sm outline-none focus:border-blue-500 transition">
          <option value="all">Tutti gli stati</option>
          {LEAD_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <select value={filterTipo} onChange={e => setFilterTipo(e.target.value)}
          className="px-3 py-2 bg-slate-900 border border-slate-800 rounded-xl text-slate-300 text-sm outline-none focus:border-blue-500 transition">
          <option value="all">Tutti i tipi</option>
          {TIPI_ATTIVITA.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      {/* Lead list */}
      <div className="bg-slate-900 rounded-2xl border border-slate-800 overflow-hidden">
        {loading ? (
          <div className="text-center py-16 text-slate-500">Caricamento...</div>
        ) : leads.length === 0 ? (
          <div className="text-center py-16">
            <Target className="w-10 h-10 text-slate-700 mx-auto mb-3" />
            <p className="text-slate-500">Nessun lead trovato</p>
            <p className="text-slate-600 text-xs mt-1">I lead arriveranno dalla pagina <span className="text-blue-400">/prova-gratis</span></p>
          </div>
        ) : (
          <div className="divide-y divide-slate-800">
            {leads.map(lead => (
              <div key={lead.id} className={`p-4 hover:bg-slate-800/40 transition ${lead.status === 'nuovo' ? 'border-l-2 border-blue-500' : ''}`}>
                <div className="flex items-start gap-3">
                  {/* Avatar */}
                  <div className="w-9 h-9 rounded-full bg-slate-700 flex items-center justify-center flex-shrink-0 text-sm font-bold text-slate-300">
                    {(lead.nome_attivita || '?')[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className="text-sm font-semibold text-white">{lead.nome_attivita}</span>
                      <LeadStatusBadge status={lead.status} />
                      {lead.utm_campaign && (
                        <span className="text-[10px] text-slate-500 bg-slate-800 px-1.5 py-0.5 rounded">📢 {lead.utm_campaign}</span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-slate-400 mb-2">
                      <span>{lead.nome_referente}</span>
                      <span className="text-green-400">{lead.telefono}</span>
                      <span>{lead.email}</span>
                      <span className="text-slate-500">{lead.tipo_attivita} · {lead.numero_barche} barche</span>
                    </div>
                    {/* Status selector */}
                    <div className="flex flex-wrap items-center gap-2">
                      <select
                        value={lead.status}
                        disabled={updatingStatus === lead.id}
                        onChange={e => updateStatus(lead.id, e.target.value)}
                        className="px-2 py-1 bg-slate-800 border border-slate-700 rounded-lg text-xs text-slate-300 outline-none focus:border-blue-500 transition"
                      >
                        {LEAD_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                      <button onClick={() => openWhatsApp(lead)}
                        className="flex items-center gap-1 px-2 py-1 bg-green-600/20 hover:bg-green-600/30 text-green-400 text-xs rounded-lg transition">
                        <MessageCircle className="w-3 h-3" /> WhatsApp
                      </button>
                      <button onClick={() => copyLead(lead)}
                        className="flex items-center gap-1 px-2 py-1 bg-slate-800 hover:bg-slate-700 text-slate-400 text-xs rounded-lg transition">
                        <Copy className="w-3 h-3" /> Copia
                      </button>
                      <button onClick={() => setSelectedLead(selectedLead?.id === lead.id ? null : lead)}
                        className="flex items-center gap-1 px-2 py-1 bg-slate-800 hover:bg-slate-700 text-slate-400 text-xs rounded-lg transition">
                        <Eye className="w-3 h-3" /> {selectedLead?.id === lead.id ? 'Chiudi' : 'Dettagli'}
                      </button>
                      <span className="text-[11px] text-slate-600 ml-auto">{fmtDate(lead.created_at)}</span>
                    </div>
                    {/* Expanded detail */}
                    {selectedLead?.id === lead.id && (
                      <div className="mt-4 p-4 bg-slate-800/60 rounded-xl grid sm:grid-cols-2 gap-3 text-xs">
                        {[
                          ['Azienda', lead.nome_attivita],
                          ['Referente', lead.nome_referente],
                          ['Telefono', lead.telefono],
                          ['Email', lead.email],
                          ['Tipo attività', lead.tipo_attivita],
                          ['Barche/gommoni', lead.numero_barche],
                          ['Usa gestionale', lead.usa_gestionale === 'no' ? 'No' : lead.usa_gestionale === 'si' ? 'Sì' : 'Sì, insoddisfatto'],
                          ['Gestionale attuale', lead.gestionale_attuale || '—'],
                          ['Note', lead.messaggio || '—'],
                          ['Fonte', lead.source || 'web'],
                          ['UTM source', lead.utm_source || '—'],
                          ['UTM campaign', lead.utm_campaign || '—'],
                          ['IP', lead.ip_address || '—'],
                          ['Creato', fmtDate(lead.created_at)],
                        ].map(([k, v]) => (
                          <div key={k}>
                            <span className="text-slate-500">{k}: </span>
                            <span className="text-slate-200">{v}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
